<template>
  <div class="mobile-order-create" @touchstart="onTouchStart" @touchend="onTouchEnd">
    <!-- Header fijo -->
    <div class="mobile-header sticky-top text-white py-2 px-3">
      <b-row align-v="center" class="no-gutters">
        <b-col cols="3">
          <b-button variant="link" @click="goBack()" class="mobile-header-icon-btn text-white p-0" title="Volver">
            <AppIcon name="back" font-scale="1.5" />
          </b-button>
        </b-col>
        <b-col cols="6" class="text-center">
          <h5 class="mb-0 mobile-header-title">{{ headerTitle }}</h5>
          <small>Paso {{ currentStep }} de {{ totalSteps }}</small>
        </b-col>
        <b-col cols="3" class="d-flex align-items-center justify-content-end flex-nowrap">
          <b-button
            variant="link"
            @click="toggleFocusMode()"
            class="mobile-header-icon-btn text-white"
            :title="focusMode ? 'Salir de pantalla completa' : 'Usar pantalla completa'">
            <AppIcon name="arrows-fullscreen" font-scale="1.2" />
          </b-button>
        </b-col>
      </b-row>
    </div>

    <!-- Barra de progreso -->
    <b-progress :max="totalSteps" height="4px" class="rounded-0">
      <b-progress-bar :value="currentStep" variant="success"></b-progress-bar>
    </b-progress>

    <!-- Banner instalación SW -->
    <!-- Banner offline/online para pasos 2 y 3 -->
    <div v-if="currentStep > 1"
         class="offline-status-bar d-flex align-items-center flex-wrap py-1 px-3 small"
         :class="!isOnline ? 'offline-status-bar--offline' : 'offline-status-bar--online'">
      <div class="offline-status-bar__connection d-flex align-items-center">
        <AppIcon :name="!isOnline ? 'wifi-off' : 'wifi'" class="mr-2" font-scale="1.15" />
        <strong>{{ !isOnline ? 'Sin conexión con el servidor' : 'Con conexión' }}</strong>
      </div>
      <span v-if="offline.presyncing" class="offline-status-bar__syncing ml-2">
        <b-spinner small class="mr-1" />
        Actualizando {{ offline.presyncProgress }}%
      </span>
      <b-dropdown v-if="isOnline"
                  right
                  no-caret
                  variant="link"
                  toggle-class="offline-mode-selector-toggle"
                  menu-class="offline-mode-selector-menu"
                  class="offline-mode-selector ml-auto"
                  :class="offline.localFirst ? 'offline-mode-selector--local' : 'offline-mode-selector--connected'">
        <template #button-content>
          <AppIcon :name="offline.localFirst ? 'phone' : 'cloud'" class="mr-1" />
          {{ offline.localFirst ? 'Datos descargados' : 'Datos del servidor' }}
          <AppIcon name="chevron-down" class="ml-1" />
        </template>
        <b-dropdown-item :active="!offline.localFirst" @click="setReadingMode(false)">
          <AppIcon name="cloud" class="mr-2" />
          <strong>Datos del servidor</strong>
          <small class="d-block text-muted ml-4">Consulta clientes, catálogo y precios vigentes.</small>
        </b-dropdown-item>
        <b-dropdown-item :active="offline.localFirst" @click="setReadingMode(true)">
          <AppIcon name="phone" class="mr-2" />
          <strong>Datos descargados</strong>
          <small class="d-block text-muted ml-4">Consulta la copia guardada en el teléfono.</small>
        </b-dropdown-item>
      </b-dropdown>
      <span v-else class="offline-status-bar__mode ml-auto offline-status-bar__mode--local">
        <AppIcon :name="offline.localFirst ? 'phone' : 'cloud'" class="mr-1" />
        {{ offline.localFirst ? 'Datos descargados' : 'Datos del servidor' }}
      </span>
      <span v-if="!isOnline">
        <span v-if="offline.pendingCount > 0"> — <strong>{{ offline.pendingCount }}</strong> pedido(s) pendiente(s)</span>
      </span>
      <span v-else>
        <span v-if="offline.pendingCount > 0" class="ml-2 text-warning font-weight-bold">
          — <b-link class="text-warning" @click="processSyncQueue(true)">Sincronizar {{ offline.pendingCount }} pedido(s)</b-link>
        </span>
      </span>
    </div>

    <b-alert v-if="currentStep > 1 && localDataFreshness" show :variant="localDataFreshness.variant" class="local-data-freshness-alert mb-0 rounded-0">
      <AppIcon name="warning" class="mr-2" />
      <span><strong>{{ localDataFreshness.title }}.</strong> {{ localDataFreshness.message }}</span>
    </b-alert>

    <b-alert
      v-if="isEditingOrder"
      show
      variant="warning"
      class="mb-0 rounded-0 py-2 edit-order-indicator">
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <div class="font-weight-bold mr-2">
          <AppIcon name="pencil-square" class="mr-1" />
          {{ editOrder.editingQueueLocalId ? 'Editando pedido pendiente de sincronizar' : ('Editando pedido #' + crud.form.id) }}
        </div>
        <b-button
          size="sm"
          variant="outline-warning"
          class="edit-order-cancel-btn"
          @click="cancelEditingOrder()">
          Cancelar edición
        </b-button>
      </div>
    </b-alert>

    <!-- ============================================ -->
    <!-- PASO 1: Selección de Cliente -->
    <!-- ============================================ -->
    <div v-if="currentStep === 1" class="mobile-step p-3 pb-5">

      <!-- Card estado conexión / sync -->
      <b-card class="mb-2" :border-variant="!isOnline ? 'danger' : 'success'">
        <div class="d-flex align-items-start justify-content-between flex-nowrap">

          <!-- Estado conexión -->
          <div class="d-flex align-items-center flex-grow-1">
            <AppIcon :name="!isOnline ? 'wifi-off' : 'wifi'"
                    :class="!isOnline ? 'text-danger' : 'text-success'"
                    font-scale="1.6"
                    class="mr-3 flex-shrink-0" />
            <div>
              <div class="font-weight-bold" :class="!isOnline ? 'text-danger' : 'text-success'">
                {{ !isOnline ? 'Sin conexión' : 'Con conexión' }}
              </div>
              <div v-if="offline.syncCardCollapsed" class="offline-sync-summary">
                <div class="d-flex align-items-center">
                  <span class="offline-mode-indicator"
                        :class="offline.localFirst ? 'offline-mode-indicator--local' : 'offline-mode-indicator--connected'">
                    <AppIcon :name="offline.localFirst ? 'phone' : 'cloud'" class="mr-1" />
                    {{ offline.localFirst ? 'Datos descargados' : 'Datos del servidor' }}
                  </span>
                  <b-button
                    variant="link"
                    class="offline-mode-help-btn p-0"
                    v-b-tooltip.click.blur.bottom
                    :title="offline.localFirst
                      ? 'Datos descargados: lee catálogo, clientes y precios base guardados en el teléfono. Solo cambia la lectura de datos; el envío de pedidos sigue el mismo proceso.'
                      : 'Datos del servidor: consulta clientes, productos y precios vigentes. Solo cambia la lectura de datos; el envío de pedidos sigue el mismo proceso.'"
                    :aria-label="offline.localFirst ? 'Ayuda sobre datos descargados' : 'Ayuda sobre datos del servidor'">
                    <AppIcon name="info-circle" />
                  </b-button>
                </div>
                <small v-if="offline.localFirst" class="d-block text-muted mt-1">
                  <strong :title="offlineLastSyncExact || ''">{{ offlineLastSync || 'Nunca sincronizado' }}</strong>
                </small>
              </div>
              <small v-else-if="offline.localFirst" class="text-muted">
                Última sincronización:
                <strong :title="offlineLastSyncExact || ''">{{ offlineLastSync || 'Nunca' }}</strong>
                <span v-if="offlineLastSyncExact"> · {{ offlineLastSyncExact }}</span>
              </small>
            </div>
          </div>

          <div class="ml-2 flex-shrink-0">
            <b-button
              size="sm"
              variant="outline-secondary"
              class="sync-card-toggle-btn"
              :title="offline.syncCardCollapsed ? 'Mostrar detalle de sincronización' : 'Minimizar detalle de sincronización'"
              @click="offline.syncCardCollapsed = !offline.syncCardCollapsed">
              <AppIcon :name="offline.syncCardCollapsed ? 'chevron-down' : 'chevron-up'" />
            </b-button>
          </div>
        </div>

        <b-alert v-if="localDataFreshness" show :variant="localDataFreshness.variant" class="local-data-freshness-alert mt-3 mb-0">
          <AppIcon name="warning" class="mr-2" />
          <span><strong>{{ localDataFreshness.title }}.</strong> {{ localDataFreshness.message }}</span>
        </b-alert>

        <div v-show="!offline.syncCardCollapsed">
          <!-- Botón actualizar datos (online) -->
          <b-button v-if="isOnline"
                    block
                    class="mt-3"
                    :variant="offline.presyncing ? 'secondary' : 'outline-primary'"
                    :disabled="offline.presyncing"
                    @click="preSync()">
            <b-spinner small v-if="offline.presyncing" class="mr-1"></b-spinner>
            <AppIcon v-else name="cloud-download" class="mr-1" />
            <span v-if="offline.presyncing">{{ offline.presyncStep || 'Actualizando...' }}{{ offline.presyncProgress > 0 ? ' ' + offline.presyncProgress + '%' : '' }}</span>
            <span v-else>Actualizar datos</span>
          </b-button>

          <b-form-checkbox
            v-model="offline.localFirst"
            class="mt-3 pt-2 border-top"
            switch
            :disabled="!isOnline"
            @change="onLocalFirstModeChange">
            Usar datos descargados
            <small class="d-block text-muted ml-1">
              <template v-if="isOnline">Clientes, catálogo y precios se leen del teléfono; se actualizan automáticamente cada 15 minutos. El envío de pedidos no cambia.</template>
              <template v-else>Se usa automáticamente la copia del teléfono hasta recuperar conexión con el servidor.</template>
            </small>
          </b-form-checkbox>

          <!-- Barra progreso sync -->
          <b-progress v-if="offline.presyncing"
                      :value="offline.presyncProgress"
                      max="100"
                      height="4px"
                      class="mt-2"
                      variant="info">
          </b-progress>

        <!-- Pedidos pendientes -->
        <div v-if="offline.pendingCount > 0" class="mt-2 pt-2 border-top d-flex align-items-center justify-content-between flex-wrap" style="gap:8px">
          <div>
            <AppIcon name="exclamation-triangle-fill" class="mr-1 text-warning" />
            <strong>{{ offline.pendingCount }}</strong> pedido(s) pendiente(s) de enviar
          </div>
          <div class="mobile-equal-height-actions">
            <b-button v-if="isOnline"
                      size="sm"
                      variant="warning"
                      :disabled="offline.syncing"
                      @click="processSyncQueue(true)">
              <b-spinner small v-if="offline.syncing" class="mr-1"></b-spinner>
              <AppIcon v-else name="cloud-upload" class="mr-1" />
              {{ offline.syncing ? 'Enviando...' : 'Enviar ahora' }}
            </b-button>
            <b-button size="sm" variant="outline-secondary" class="ml-1" @click="openEditOrderModal()">
              <AppIcon name="list-ul" class="mr-1" />Ver
            </b-button>
          </div>
        </div>
        </div>
      </b-card>

      <b-card v-if="showEditOrderCard" class="mb-2 step1-edit-order-card">
        <div class="d-flex align-items-center justify-content-between">
          <div>
            <strong>Pedidos guardados</strong>
            <small class="text-muted d-block">Vendedor: {{ getSeller ? getSeller.name : '-' }}</small>
          </div>
          <b-button
            size="sm"
            variant="outline-primary"
            @click="openEditOrderModal()"
            :disabled="editOrder.loading || editOrder.selecting">
            <b-spinner small v-if="editOrder.loading || editOrder.selecting" class="mr-1"></b-spinner>
            <AppIcon v-else name="search" class="mr-1" />
            Buscar pedido
          </b-button>
        </div>
      </b-card>

      <div v-if="!crud.form.customers || customerChange.active">
        <div v-if="customerChange.active" class="d-flex justify-content-between align-items-center mb-2">
          <small class="text-muted">Elegí el nuevo cliente.</small>
          <b-button variant="link" size="sm" class="mobile-text-action-btn p-0" @click="cancelCustomerChange()">
            Cancelar
          </b-button>
        </div>
        <small v-if="!customerChange.active" class="text-muted d-block mb-1">
          <AppIcon name="person-add" class="mr-1" fill />
          Para un pedido nuevo, elegí un cliente.
        </small>
        <!-- Búsqueda -->
        <b-input-group class="mb-2">
          <b-form-input
            v-model="search.customer"
            type="search"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            placeholder="Buscar cliente por nombre, código, CUIT, ciudad o zona..."
            size="lg"
            class="mobile-input">
          </b-form-input>
          <b-input-group-append>
            <b-button variant="outline-secondary">
              <AppIcon name="search" />
            </b-button>
          </b-input-group-append>
        </b-input-group>

        <!-- Selector zona / ciudad -->
        <div class="d-flex mb-2">
          <b-button-group size="sm">
            <b-button :variant="customerGroupBy === 'city' ? 'primary' : 'outline-secondary'" @click="customerGroupBy = 'city'">
              <AppIcon name="geo-alt" class="mr-1" />Ciudad
            </b-button>
            <b-button :variant="customerGroupBy === 'zone' ? 'primary' : 'outline-secondary'" @click="customerGroupBy = 'zone'">
              <AppIcon name="map" class="mr-1" />Zona
            </b-button>
          </b-button-group>
        </div>

        <!-- Loading -->
        <div v-if="loading.customers" class="text-center py-5">
          <b-spinner variant="primary"></b-spinner>
          <p class="mt-2 text-muted">Cargando clientes...</p>
        </div>

        <!-- Lista de clientes agrupados (Acordeón) -->
        <div v-else class="zones-accordion" role="tablist">
          <b-card no-body class="mb-2" v-for="group in activeGroups" :key="group.key">
            <b-card-header header-tag="header" class="p-0" role="tab">
              <b-button
                block
                @click="toggleGroup(group.key)"
                variant="light"
                class="d-flex align-items-center justify-content-between text-left py-3 px-3 zone-header">
                <div class="d-flex align-items-center">
                  <AppIcon name="geo-alt-fill" class="mr-2 text-primary" />
                  <span class="font-weight-bold">{{ group.name }}</span>
                </div>
                <div class="d-flex align-items-center">
                  <b-badge variant="secondary" pill class="mr-2">{{ (filteredCustomersByGroup[group.key] || []).length }}</b-badge>
                  <AppIcon name="chevron-down" class="zone-chevron" />
                </div>
              </b-button>
            </b-card-header>
            <div v-if="expandedGroups[group.key]">
              <b-list-group flush>
                <b-list-group-item
                  v-for="customer in (filteredCustomersByGroup[group.key] || []).slice(0, groupPageSize[group.key] || 50)"
                  :key="customer.id"
                  @click="selectCustomer(customer, group.zone || null)"
                  class="mobile-list-item d-flex align-items-center py-3"
                  button>
                  <div class="mr-3 customer-avatar-wrap">
                    <b-avatar
                      :src="customer.image || null"
                      :text="avatarFromName(customer.name).text"
                      :style="avatarFromName(customer.name).style"
                      variant="dark"
                      size="52px"
                      class="customer-avatar">
                    </b-avatar>
                  </div>
                  <div class="flex-grow-1">
                    <div class="font-weight-bold">
                      <span v-if="customer.code" class="mr-1">({{ customer.code }})</span>{{ customer.name }}
                    </div>
                    <small class="text-muted d-block" v-if="getCustomerFullAddress(customer)">
                      <AppIcon name="geo-alt" class="mr-1" />{{ getCustomerFullAddress(customer) }}
                    </small>
                  </div>
                  <AppIcon name="chevron-right" class="text-muted" />
                </b-list-group-item>
              </b-list-group>
              <div v-if="(filteredCustomersByGroup[group.key] || []).length > (groupPageSize[group.key] || 50)"
                   :data-group-key="group.key"
                   class="group-scroll-sentinel py-1">
              </div>
            </div>
          </b-card>
        </div>

        <EmptyState v-if="!activeGroups.length && !loading.customers && !search.customer"
                    icon="person"
                    title="No hay clientes asignados"
                    class="mt-3" />

        <EmptyState v-if="!hasFilteredCustomers && !loading.customers && search.customer"
                    icon="search"
                    title="No se encontraron clientes"
                    :description="'Sin resultados para: ' + search.customer"
                    class="mt-3" />
      </div>

      <!-- Cliente seleccionado y punto de venta -->
      <b-card v-if="crud.form.customers && !customerChange.active" class="mt-3 customer-configuration-card">
        <div class="d-flex justify-content-end mb-1">
          <b-button variant="link" size="sm" class="mobile-text-action-btn p-0 text-muted" @click="startCustomerChange()">
            Cambiar cliente
          </b-button>
        </div>
        <div class="d-flex align-items-center mb-2">
          <b-avatar
            :src="crud.form.customers.image || null"
            :text="avatarFromName(crud.form.customers.name).text"
            :style="avatarFromName(crud.form.customers.name).style"
            variant="dark"
            size="52px"
            class="mr-3 customer-avatar">
          </b-avatar>
          <div>
            <div class="font-weight-bold">
              <span v-if="crud.form.customers.code" class="mr-1">({{ crud.form.customers.code }})</span>{{ crud.form.customers.name }}
            </div>
          </div>
        </div>
        <b-form-group label="Punto de venta" label-size="sm">
          <b-form-select
            v-model="crud.form.erp_points_sales_id"
            :options="pointsSalesOptions"
            size="lg">
          </b-form-select>
        </b-form-group>
        <b-form-group label="Medio de pago" label-size="sm">
          <b-form-select
            v-model="crud.form.methods_payment_id"
            :options="methodsPaymentOptions"
            size="lg"
            @change="onMethodsPaymentChange">
          </b-form-select>
        </b-form-group>
        <b-form-group label="Lista de precios" label-size="sm">
          <b-form-select
            v-model="crud.form.price_list_id"
            :options="priceListOptions"
            size="lg"
            :disabled="crud.form.detail.length > 0"
            @change="onPriceListChange" />
        </b-form-group>
      </b-card>

      <!-- Botonera de acciones del cliente -->
      <div v-if="crud.form.customers && !customerChange.active && !isEditingOrder && isOnline" class="mt-3 mb-5">
        <b-button
          block
          class="d-flex align-items-center justify-content-center action-duplicate-btn"
          @click="onDuplicarPedido()">
          <AppIcon name="files" class="mr-2" />
          Duplicar desde un pedido anterior
        </b-button>

        <hr v-if="haveReportsPayment || haveReportsNews" class="my-3">

        <div v-if="haveReportsPayment || haveReportsNews" class="d-flex mobile-equal-height-actions" style="gap:10px">
          <b-button
            v-if="haveReportsPayment"
            class="d-flex align-items-center justify-content-center action-square-btn action-square-btn--cobranza"
            style="flex:1"
            @click="onCobranza()">
            <AppIcon name="cash-stack" class="mr-2 action-square-btn__icon action-square-btn__icon--cobranza" />
            Registrar cobranza
          </b-button>
          <b-button
            v-if="haveReportsNews"
            class="d-flex align-items-center justify-content-center action-square-btn action-square-btn--observaciones"
            style="flex:1"
            @click="onObservaciones()">
            <AppIcon name="chat" class="mr-2 action-square-btn__icon action-square-btn__icon--observaciones" />
            Agregar observación
          </b-button>
        </div>

        <!-- Resumen diario -->
        <div v-if="haveReportsPayment || haveReportsNews" class="mt-3">
          <div v-if="haveReportsPayment" class="stats-hoy-card stats-hoy-card--cobranza mb-2">
            <AppIcon name="cash-stack" class="mr-1 stats-hoy-card__icon" />
            <span v-if="statsHoy.cobranzas.count > 0">
              Cobranzas realizadas: <strong>{{ statsHoy.cobranzas.count }}</strong> — Total: <strong>$ {{ formatPrice(statsHoy.cobranzas.total) }}</strong>
            </span>
            <span v-else class="text-muted">Sin cobranzas registradas hoy</span>
          </div>
          <div v-if="haveReportsNews" class="stats-hoy-card stats-hoy-card--observaciones">
            <AppIcon name="chat" class="mr-1 stats-hoy-card__icon" />
            <span v-if="statsHoy.observaciones.count > 0">
              <strong>{{ statsHoy.observaciones.count }}</strong> observación{{ statsHoy.observaciones.count !== 1 ? 'es' : '' }} enviada{{ statsHoy.observaciones.count !== 1 ? 's' : '' }} hoy
            </span>
            <span v-else class="text-muted">Sin observaciones enviadas hoy</span>
          </div>
        </div>
      </div>

      <div class="fixed-bottom mobile-footer mobile-footer--action" v-if="crud.form.customers && !customerChange.active && canContinueStep1">
        <b-button
          variant="success"
          block
          size="lg"
          @click="nextStepFromCustomer()">
          Continuar
          <AppIcon name="arrow-right" class="ml-1" />
        </b-button>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- PASO 2: Selección de Productos -->
    <!-- ============================================ -->
    <div v-if="currentStep === 2" class="mobile-step">
      <!-- Contexto del pedido: acompaña el inicio de la búsqueda sin quedar
           parcialmente oculto al hacer scroll. -->
      <div class="p-3 pb-1 bg-light product-customer-context-wrap">
        <b-card no-body class="product-customer-context-card" v-if="crud.form.customers">
          <b-card-body class="p-2">
        <div class="customer-summary mb-0">
          <div class="customer-summary__heading d-flex align-items-center">
            <b-avatar
              :src="crud.form.customers.image || null"
              :text="avatarFromName(crud.form.customers.name).text"
              :style="avatarFromName(crud.form.customers.name).style"
              variant="dark"
              size="36px"
              class="customer-avatar customer-summary__avatar">
            </b-avatar>
            <div class="customer-summary__name flex-grow-1">
              <span v-if="crud.form.customers.code" class="mr-1">({{ crud.form.customers.code }})</span>{{ crud.form.customers.name }}
            </div>
            <b-button
              size="sm"
              variant="outline-secondary"
              class="customer-summary__toggle"
              :title="customerDetailsExpanded ? 'Minimizar datos del cliente' : 'Ver datos del cliente'"
              :aria-label="customerDetailsExpanded ? 'Minimizar datos del cliente' : 'Ver datos del cliente'"
              :aria-expanded="customerDetailsExpanded ? 'true' : 'false'"
              aria-controls="customer-summary-details"
              @click="customerDetailsExpanded = !customerDetailsExpanded">
              <AppIcon :name="customerDetailsExpanded ? 'chevron-up' : 'chevron-down'" />
            </b-button>
          </div>
          <div id="customer-summary-details" v-show="customerDetailsExpanded" class="customer-summary__details">
            <div v-if="selectedPointSaleLabel" class="customer-summary__detail" title="Punto de venta">
              <AppIcon name="shop" />
              <span>{{ selectedPointSaleLabel }}</span>
            </div>
            <div v-if="selectedMethodsPaymentLabel" class="customer-summary__detail" title="Medio de pago">
              <AppIcon name="cash-stack" />
              <span>{{ selectedMethodsPaymentLabel }}</span>
            </div>
            <div v-if="selectedPriceListLabel" class="customer-summary__detail" title="Lista de precios">
              <AppIcon name="list-ul" />
              <span>{{ selectedPriceListLabel }}</span>
            </div>
            <div v-if="customerDocumentLabel" class="customer-summary__detail" title="Documento">
              <AppIcon name="person-badge" />
              <span>{{ customerDocumentLabel }}</span>
            </div>
            <div v-if="customerAddressLabel" class="customer-summary__detail" title="Dirección">
              <AppIcon name="geo-alt" />
              <span>{{ customerAddressLabel }}</span>
            </div>
            <div v-if="selectedCustomerZone" class="customer-summary__detail" title="Zona">
              <AppIcon name="geo-alt-fill" />
              <span>{{ selectedCustomerZone.name }}</span>
            </div>
            <div v-if="customerPhoneLabel" class="customer-summary__detail" title="Teléfono">
              <AppIcon name="call" />
              <span>{{ customerPhoneLabel }}</span>
            </div>
            <div v-if="crud.form.customers.email" class="customer-summary__detail" title="Correo electrónico">
              <AppIcon name="envelope" />
              <span>{{ crud.form.customers.email }}</span>
            </div>
          </div>
        </div>
          </b-card-body>
        </b-card>
      </div>

      <!-- Búsqueda de productos -->
      <div class="p-3 pt-1 bg-light sticky-top product-search-wrap" style="top: 60px; z-index: 100;">
        <b-input-group>
          <b-input-group-prepend>
            <b-button
              :variant="numericKeyboard ? 'secondary' : 'outline-secondary'"
              @click="numericKeyboard = !numericKeyboard; saveNumericKeyboard()"
              :title="numericKeyboard ? 'Teclado numérico activo' : 'Activar teclado numérico'">
              <AppIcon name="keyboard" />
            </b-button>
          </b-input-group-prepend>
          <b-form-input
            v-model="search.product"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            placeholder="Buscar producto por código o nombre..."
            size="lg"
            class="mobile-input"
            :inputmode="numericKeyboard ? 'numeric' : 'text'"
            :disabled="loading.products"
            @focus="onProductSearchFocus"
            @keyup.enter="forceSearchProducts()">
          </b-form-input>
          <b-input-group-append>
            <b-button variant="outline-secondary" @click="forceSearchProducts()" :disabled="loading.products">
              <b-spinner small v-if="loading.products"></b-spinner>
              <AppIcon name="search" v-else />
            </b-button>
          </b-input-group-append>
        </b-input-group>
        <div v-if="filteredProducts.length" class="text-muted small mt-1 product-results-counter">
          {{ filteredProducts.length }} producto(s) encontrado(s)
        </div>
        <div class="product-controls-row mt-2">
          <b-button-group size="sm" class="product-view-toggle">
            <b-button
              :variant="isAccordionView ? 'secondary' : 'outline-secondary'"
              title="Vista acordeón"
              aria-label="Vista acordeón"
              @click="setProductViewMode('accordion')">
              <AppIcon name="list-nested" />
            </b-button>
            <b-button
              :variant="isListView ? 'secondary' : 'outline-secondary'"
              title="Vista listado"
              aria-label="Vista listado"
              @click="setProductViewMode('list')">
              <AppIcon name="grid3x3-gap" />
            </b-button>
          </b-button-group>
          <div class="product-query-filters-inline">
            <b-form-checkbox
              switch
              class="product-switch mb-0"
              v-model="productQueryFilters.onlyWithPrice"
              @change="onProductQueryFiltersChange">
              Con precio
            </b-form-checkbox>
            <b-form-checkbox
              switch
              class="product-switch mb-0"
              v-model="productQueryFilters.onlyWithStock"
              @change="onProductQueryFiltersChange">
              Con stock
            </b-form-checkbox>
          </div>
        </div>
      </div>

      <!-- Loading productos -->
      <div v-if="loading.products" class="text-center py-5">
        <b-spinner variant="primary"></b-spinner>
        <p class="mt-2 text-muted">Cargando productos...</p>
      </div>

      <!-- Lista de productos -->
      <div v-else class="product-list p-3">
        <div v-if="isAccordionView && groupedProducts.length" class="products-accordion" role="tablist">
          <b-card no-body class="mb-2" v-for="(category, catIndex) in groupedProducts" :key="category.key">
            <b-card-header header-tag="header" class="p-0" role="tab">
              <b-button
                block
                v-b-toggle="'category-collapse-' + category.key"
                variant="light"
                class="d-flex align-items-center justify-content-between text-left py-3 px-3 category-header">
                <div class="d-flex align-items-center">
                  <AppIcon name="folder-fill" class="mr-2 text-primary" />
                  <span class="font-weight-bold">{{ category.name }}</span>
                </div>
                <div class="d-flex align-items-center">
                  <b-badge v-if="getCategoryAddedCount(category)" variant="dark" pill class="mr-2">
                    <AppIcon name="check2-circle" class="mr-1" />{{ getCategoryAddedCount(category) }}
                  </b-badge>
                  <b-badge variant="secondary" pill class="mr-2">{{ getCategoryProductCount(category) }}</b-badge>
                  <AppIcon name="chevron-down" class="zone-chevron" />
                </div>
              </b-button>
            </b-card-header>
            <b-collapse :id="'category-collapse-' + category.key"
                        :visible="isCategoryExpanded(category, catIndex)"
                        role="tabpanel">
              <div class="product-category-body p-2">
                <b-card no-body class="mb-2 subcategory-card"
                        v-for="(subcategory, subIndex) in category.subcategories"
                        :key="subcategory.key">
                  <b-card-header header-tag="header" class="p-0" role="tab">
                    <b-button
                      block
                      v-b-toggle="'subcategory-collapse-' + category.key + '-' + subcategory.key"
                      variant="light"
                      class="d-flex align-items-center justify-content-between text-left py-2 px-3 subcategory-header">
                      <div class="d-flex align-items-center">
                        <AppIcon name="folder2-open" class="mr-2 text-muted" />
                        <span class="font-weight-bold">{{ subcategory.name }}</span>
                      </div>
                      <div class="d-flex align-items-center">
                        <b-badge v-if="getSubcategoryAddedCount(subcategory)" variant="secondary" pill class="mr-2">
                          <AppIcon name="check2-circle" class="mr-1" />{{ getSubcategoryAddedCount(subcategory) }}
                        </b-badge>
                        <b-badge variant="light" pill class="mr-2">{{ subcategory.products.length }}</b-badge>
                        <AppIcon name="chevron-down" class="zone-chevron" />
                      </div>
                    </b-button>
                  </b-card-header>
                  <b-collapse :id="'subcategory-collapse-' + category.key + '-' + subcategory.key"
                              :visible="isSubcategoryExpanded(category, subcategory, catIndex, subIndex)"
                              role="tabpanel"
                              @show="onSubcategoryShow(category.key + '-' + subcategory.key)">
                    <div class="product-subcategory-body p-2" v-if="mountedSubcategories[category.key + '-' + subcategory.key]">
                      <div v-for="product in visibleProductsFor(category.key + '-' + subcategory.key, subcategory.products)" :key="product.id"
                           class="product-card product-card-price mb-3 p-3 border rounded"
                           :data-product-id="product.id"
                           :class="{ 'product-card-added': isProductInCart(product) }">
                        <b-row>
                          <b-col cols="12">
                            <div class="font-weight-bold">{{ product.name }}</div>
                            <div class="d-flex align-items-center justify-content-between">
                              <div class="text-muted small" v-if="product.code">{{ product.code }}</div>
                              <div
                                v-if="product.stock !== undefined && product.stock !== null && product.stock !== ''"
                                class="product-stock-pill product-stock-pill-inline d-inline-flex align-items-center"
                                :class="{ 'product-stock-pill-empty': getProductStock(product) <= 0 }">
                                <AppIcon name="box-seam" class="mr-1" />
                                <span class="product-stock-value">{{ formatStock(product.stock) }}</span>
                              </div>
                            </div>
                            <div class="mt-1">
                              <span v-if="product.priceLoading" class="text-primary font-weight-bold">Cargando precio...</span>
                              <template v-else-if="product.price !== null">
                                <!-- Precio base -->
                                <div class="d-flex align-items-center flex-wrap font-weight-bold">
                                  <template v-if="getProductPriceWithIva(product, product.price_initial || product.price)">
                                    <span class="text-primary">$ {{ formatPrice(getProductPriceWithIva(product, product.price_initial || product.price)) }} <small class="font-weight-normal">c/IVA</small></span>
                                    <span class="mx-1 text-muted">·</span>
                                    <span class="text-muted" style="font-size:0.85em">$ {{ formatPrice(product.price_initial || product.price) }} <small class="font-weight-normal">s/IVA</small></span>
                                  </template>
                                  <template v-else>
                                    <span class="text-primary">$ {{ formatPrice(product.price_initial || product.price) }} <small class="text-muted font-weight-normal">s/IVA</small></span>
                                  </template>
                                  <span v-if="getProductPurchaseUnitLabel(product)" class="text-muted font-weight-normal small ml-2">x {{ getProductPurchaseUnitLabel(product) }}</span>
                                  <AppIcon v-if="product.unit_measurement_purchase_id && product.unit_measurement_sale_id && product.unit_measurement_purchase_id !== product.unit_measurement_sale_id" name="info-circle" class="text-info ml-1" style="cursor:pointer;font-size:0.95rem" @click.stop="openConversionModal(product)" fill />
                                </div>
                                <!-- Precio con descuento -->
                                <div v-if="getProductPriceAfterDiscount(product.price_initial || product.price, product.discount)" class="d-flex align-items-center flex-wrap text-success" style="font-size:0.9em;">
                                  <small class="text-muted mr-1">c/desc {{ product.discount }}%:</small>
                                  <template v-if="getProductPriceWithIva(product, getProductPriceAfterDiscount(product.price_initial || product.price, product.discount))">
                                    <span>$ {{ formatPrice(getProductPriceWithIva(product, getProductPriceAfterDiscount(product.price_initial || product.price, product.discount))) }} <small class="font-weight-normal">c/IVA</small></span>
                                    <span class="mx-1 text-muted">·</span>
                                    <span class="text-muted" style="font-size:0.9em">$ {{ formatPrice(getProductPriceAfterDiscount(product.price_initial || product.price, product.discount)) }} <small class="font-weight-normal">s/IVA</small></span>
                                  </template>
                                  <template v-else>
                                    <span>$ {{ formatPrice(getProductPriceAfterDiscount(product.price_initial || product.price, product.discount)) }} <small class="font-weight-normal">s/IVA</small></span>
                                  </template>
                                </div>
                              </template>
                              <span v-else class="text-muted">Precio al agregar</span>
                            </div>

                            <!-- Cantidad y Agregar -->
                            <div class="mt-2" style="display:grid;grid-template-columns:1fr 1fr;gap:6px;align-items:center">
                              <b-input-group size="sm" style="flex-wrap:nowrap">
                                <b-input-group-prepend>
                                  <b-button variant="outline-secondary" @click="decrementQty(product)" :disabled="isProductInCart(product)">-</b-button>
                                </b-input-group-prepend>
                                <b-form-input
                                  v-model="product.quantity"
                                  type="number"
                                  min="1"
                                  class="text-center"
                                  autocomplete="off"
                                  :disabled="isProductInCart(product)"
                                  @focus="handleMobileInputFocus($event, product, 'quantity')"
                                  @blur="handleMobileInputBlur($event, product, 'quantity')"
                                  @keydown.up.prevent="incrementQty(product)"
                                  @keydown.down.prevent="decrementQty(product)"
                                  @input="onProductQuantityInput(product, $event)"
                                  @change="onProductQuantityChange(product, $event)">
                                </b-form-input>
                                <b-input-group-append>
                                  <b-button variant="outline-secondary" @click="incrementQty(product)" :disabled="isProductInCart(product)">+</b-button>
                                </b-input-group-append>
                              </b-input-group>
                              <b-input-group size="sm" class="mobile-discount-input w-100"
                                             :class="{ 'discount-exceeded': isProductDiscountExceeded(product) }">
                                <b-form-input
                                  v-model="product.discount"
                                  type="text"
                                  inputmode="decimal"
                                  autocomplete="off"
                                  placeholder="Desc"
                                  :disabled="isProductInCart(product)"
                                  @focus="handleMobileInputFocus($event, product, 'discount')"
                                  @blur="handleMobileInputBlur($event, product, 'discount')"
                                  @keyup.enter="addToCart(product)"
                                  @input="onProductDiscountInput(product, $event)"
                                  @change="onProductDiscountChange(product, $event)">
                                </b-form-input>
                                <b-input-group-append>
                                  <b-input-group-text>%</b-input-group-text>
                                </b-input-group-append>
                              </b-input-group>
                            </div>
                            <b-row class="mt-2" v-if="isProductDiscountExceeded(product)">
                              <b-col cols="12">
                                <small class="text-danger">
                                  <AppIcon name="exclamation-triangle-fill" class="mr-1" />
                                  Desc. máx {{ formatPercent(getProductMaxDiscount(product)) }} · Asignado {{ formatPercent(product.discount) }}
                                </small>
                              </b-col>
                            </b-row>
                            <b-row class="mt-2" v-if="allowExtraDiscount">
                              <b-col cols="12">
                                <b-input-group v-if="!isProductInCart(product)" class="extra-input-group">
                                  <b-input-group-prepend>
                                    <b-input-group-text class="extra-check-addon">
                                      <b-form-checkbox v-model="product.extraEnabled" class="mb-0 extra-check"></b-form-checkbox>
                                      Extra
                                    </b-input-group-text>
                                  </b-input-group-prepend>
                                  <b-form-input
                                    type="number"
                                    min="1"
                                    v-model.number="product.extraQuantity"
                                    placeholder="Cant."
                                    :disabled="!product.extraEnabled"
                                    class="text-center">
                                  </b-form-input>
                                </b-input-group>
                                <b-badge v-else-if="product.extraEnabled" variant="success">EXTRA {{ extraDiscountPercentage }}% x{{ product.extraQuantity || 1 }}</b-badge>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col cols="12">
                                <b-button
                                  :variant="isProductInCart(product) ? 'secondary' : 'primary'"
                                  block
                                  class="add-to-cart-btn"
                                  @click="addToCart(product)"
                                  :disabled="loading.addingProductId === product.id || isProductInCart(product)">
                                  <b-spinner small v-if="loading.addingProductId === product.id"></b-spinner>
                                  <span v-else-if="isProductInCart(product)">
                                    <AppIcon name="check2-circle" /> Agregado
                                  </span>
                                  <span v-else>
                                    <AppIcon name="plus" /> Agregar
                                  </span>
                                </b-button>
                              </b-col>
                            </b-row>
                          </b-col>
                        </b-row>
                      </div>
                      <div v-if="hasMoreProducts(category.key + '-' + subcategory.key, subcategory.products)"
                           :data-product-scope-key="category.key + '-' + subcategory.key"
                           class="product-scroll-sentinel py-2 text-center text-muted small">
                        <b-spinner small class="mr-1"></b-spinner> Cargando más productos...
                      </div>
                      <div v-if="subcategory.products.length && !useLocalData" class="text-center text-muted small product-results-counter">
                        Mostrando {{ visibleProductsFor(category.key + '-' + subcategory.key, subcategory.products).length }} de {{ subcategory.products.length }}
                      </div>
                    </div>
                  </b-collapse>
                </b-card>
              </div>
            </b-collapse>
          </b-card>
        </div>

        <b-row v-else-if="isListView && filteredProducts.length" class="products-grid">
          <b-col cols="12" v-for="product in visibleProductsFor('list', filteredProducts)" :key="'list-' + product.id" class="mb-3">
            <div class="product-card product-card-price p-3 border rounded"
                 :data-product-id="product.id"
                 :class="{ 'product-card-added': isProductInCart(product) }">
              <div class="font-weight-bold">{{ product.name }}</div>
              <div class="d-flex align-items-center justify-content-between">
                <div class="text-muted small" v-if="product.code">{{ product.code }}</div>
                <div
                  v-if="product.stock !== undefined && product.stock !== null && product.stock !== ''"
                  class="product-stock-pill product-stock-pill-inline d-inline-flex align-items-center"
                  :class="{ 'product-stock-pill-empty': getProductStock(product) <= 0 }">
                  <AppIcon name="box-seam" class="mr-1" />
                  <span class="product-stock-value">{{ formatStock(product.stock) }}</span>
                </div>
              </div>
              <div class="text-muted small mt-1">
                <span class="d-inline-block mr-2"><strong>Categoría:</strong> {{ getProductCategoryName(product) }}</span>
                <span class="d-inline-block"><strong>Subcategoría:</strong> {{ getProductSubcategoryName(product) }}</span>
              </div>
              <div class="mt-1">
                <span v-if="product.priceLoading" class="text-primary font-weight-bold">Cargando precio...</span>
                <template v-else-if="product.price !== null">
                  <!-- Precio base -->
                  <div class="d-flex align-items-center flex-wrap font-weight-bold">
                    <template v-if="getProductPriceWithIva(product, product.price_initial || product.price)">
                      <span class="text-primary">$ {{ formatPrice(getProductPriceWithIva(product, product.price_initial || product.price)) }} <small class="font-weight-normal">c/IVA</small></span>
                      <span class="mx-1 text-muted">·</span>
                      <span class="text-muted" style="font-size:0.85em">$ {{ formatPrice(product.price_initial || product.price) }} <small class="font-weight-normal">s/IVA</small></span>
                    </template>
                    <template v-else>
                      <span class="text-primary">$ {{ formatPrice(product.price_initial || product.price) }} <small class="text-muted font-weight-normal">s/IVA</small></span>
                    </template>
                    <span v-if="getProductPurchaseUnitLabel(product)" class="text-muted font-weight-normal small ml-2">x {{ getProductPurchaseUnitLabel(product) }}</span>
                    <AppIcon v-if="product.unit_measurement_purchase_id && product.unit_measurement_sale_id && product.unit_measurement_purchase_id !== product.unit_measurement_sale_id" name="info-circle" class="text-info ml-1" style="cursor:pointer;font-size:0.95rem" @click.stop="openConversionModal(product)" fill />
                  </div>
                  <!-- Precio con descuento -->
                  <div v-if="getProductPriceAfterDiscount(product.price_initial || product.price, product.discount)" class="d-flex align-items-center flex-wrap text-success" style="font-size:0.9em;">
                    <small class="text-muted mr-1">c/desc {{ product.discount }}%:</small>
                    <template v-if="getProductPriceWithIva(product, getProductPriceAfterDiscount(product.price_initial || product.price, product.discount))">
                      <span>$ {{ formatPrice(getProductPriceWithIva(product, getProductPriceAfterDiscount(product.price_initial || product.price, product.discount))) }} <small class="font-weight-normal">c/IVA</small></span>
                      <span class="mx-1 text-muted">·</span>
                      <span class="text-muted" style="font-size:0.9em">$ {{ formatPrice(getProductPriceAfterDiscount(product.price_initial || product.price, product.discount)) }} <small class="font-weight-normal">s/IVA</small></span>
                    </template>
                    <template v-else>
                      <span>$ {{ formatPrice(getProductPriceAfterDiscount(product.price_initial || product.price, product.discount)) }} <small class="font-weight-normal">s/IVA</small></span>
                    </template>
                  </div>
                </template>
                <span v-else class="text-muted">Precio al agregar</span>
              </div>

              <div class="mt-2" style="display:grid;grid-template-columns:1fr 1fr;gap:6px;align-items:center">
                <b-input-group size="sm" style="flex-wrap:nowrap">
                  <b-input-group-prepend>
                    <b-button variant="outline-secondary" @click="decrementQty(product)" :disabled="isProductInCart(product)">-</b-button>
                  </b-input-group-prepend>
                  <b-form-input
                    v-model="product.quantity"
                    type="number"
                    min="1"
                    class="text-center"
                    autocomplete="off"
                    :disabled="isProductInCart(product)"
                    @focus="handleMobileInputFocus($event, product, 'quantity')"
                    @blur="handleMobileInputBlur($event, product, 'quantity')"
                    @keydown.up.prevent="incrementQty(product)"
                    @keydown.down.prevent="decrementQty(product)"
                    @input="onProductQuantityInput(product, $event)"
                    @change="onProductQuantityChange(product, $event)">
                  </b-form-input>
                  <b-input-group-append>
                    <b-button variant="outline-secondary" @click="incrementQty(product)" :disabled="isProductInCart(product)">+</b-button>
                  </b-input-group-append>
                </b-input-group>
                <b-input-group size="sm" class="mobile-discount-input w-100"
                               :class="{ 'discount-exceeded': isProductDiscountExceeded(product) }">
                  <b-form-input
                    v-model="product.discount"
                    type="text"
                    inputmode="decimal"
                    autocomplete="off"
                    placeholder="Desc"
                    :disabled="isProductInCart(product)"
                    @focus="handleMobileInputFocus($event, product, 'discount')"
                    @blur="handleMobileInputBlur($event, product, 'discount')"
                    @keyup.enter="addToCart(product)"
                    @input="onProductDiscountInput(product, $event)"
                    @change="onProductDiscountChange(product, $event)">
                  </b-form-input>
                  <b-input-group-append>
                    <b-input-group-text>%</b-input-group-text>
                  </b-input-group-append>
                </b-input-group>
              </div>
              <b-row class="mt-2" v-if="isProductDiscountExceeded(product)">
                <b-col cols="12">
                  <small class="text-danger">
                    <AppIcon name="exclamation-triangle-fill" class="mr-1" />
                    Desc. máx {{ formatPercent(getProductMaxDiscount(product)) }} · Asignado {{ formatPercent(product.discount) }}
                  </small>
                </b-col>
              </b-row>
              <b-row class="mt-2" v-if="allowExtraDiscount">
                <b-col cols="12">
                  <b-input-group v-if="!isProductInCart(product)" class="extra-input-group">
                    <b-input-group-prepend>
                      <b-input-group-text class="extra-check-addon">
                        <b-form-checkbox v-model="product.extraEnabled" class="mb-0 extra-check"></b-form-checkbox>
                        Extra
                      </b-input-group-text>
                    </b-input-group-prepend>
                    <b-form-input
                      type="number"
                      min="1"
                      v-model.number="product.extraQuantity"
                      placeholder="Cant."
                      :disabled="!product.extraEnabled"
                      class="text-center">
                    </b-form-input>
                  </b-input-group>
                  <b-badge v-else-if="product.extraEnabled" variant="success">
                    <AppIcon name="check2-circle" class="mr-1" />Extra incluido
                  </b-badge>
                </b-col>
              </b-row>
              <b-row class="mt-2">
                <b-col cols="12">
                  <b-button
                    :variant="isProductInCart(product) ? 'secondary' : 'primary'"
                    block
                    class="add-to-cart-btn"
                    @click="addToCart(product)"
                    :disabled="loading.addingProductId === product.id || isProductInCart(product)">
                    <b-spinner small v-if="loading.addingProductId === product.id"></b-spinner>
                    <span v-else-if="isProductInCart(product)">
                      <AppIcon name="check2-circle" /> Agregado
                    </span>
                    <span v-else>
                      <AppIcon name="plus" /> Agregar
                    </span>
                  </b-button>
                </b-col>
              </b-row>
            </div>
          </b-col>
          <b-col cols="12" v-if="hasMoreProducts('list', filteredProducts)">
            <div data-product-scope-key="list" class="product-scroll-sentinel py-2 text-center text-muted small">
              <b-spinner small class="mr-1"></b-spinner> Cargando más productos...
            </div>
          </b-col>
          <b-col cols="12" v-if="filteredProducts.length && !useLocalData" class="text-center text-muted small product-results-counter">
            Mostrando {{ visibleProductsFor('list', filteredProducts).length }} de {{ filteredProducts.length }}
          </b-col>
        </b-row>

        <div v-if="useLocalData && filteredProducts.length && localProductSearch.hasMore"
             data-product-scope-key="local-results"
             class="product-scroll-sentinel py-3 text-center text-muted small">
          <b-spinner small class="mr-1"></b-spinner> Cargando más productos...
        </div>

        <EmptyState v-if="!arr.products.length && !loading.products && !trimmedProductSearch"
                    icon="search"
                    title="Buscá productos"
                    description="Ingresá un código o nombre para comenzar." />

        <b-alert v-if="!useLocalData && !loading.products && trimmedProductSearch && trimmedProductSearch.length < minProductSearchChars && !productSearchHasExecuted"
                 variant="info"
                 show>
          <AppIcon name="info-circle" class="mr-2" />
          Escriba al menos 4 caracteres o presione Buscar/Enter
        </b-alert>

        <b-alert v-if="!useLocalData && !loading.products && trimmedProductSearch && trimmedProductSearch.length >= minProductSearchChars && !productSearchHasExecuted"
                 variant="info"
                 show>
          <AppIcon name="search" class="mr-2" />
          Presione Buscar/Enter para ejecutar la búsqueda
        </b-alert>

        <EmptyState v-if="!filteredProducts.length && !loading.products && trimmedProductSearch && productSearchHasExecuted"
                    icon="search"
                    title="No se encontraron productos"
                    :description="'Sin resultados para: ' + trimmedProductSearch" />
      </div>

      <!-- Footer fijo con carrito -->
      <div class="cart-footer fixed-bottom mobile-footer mobile-footer--cart">
        <b-row align-v="stretch" no-gutters class="m-0">
          <b-col cols="6" class="mobile-cart-summary">
            <div class="font-weight-bold">{{ crud.form.detail.length }} items</div>
            <div class="mb-0">
              <template v-if="getTotalOrderWithIva !== getTotalOrder">
                <div class="text-primary h5 mb-0 font-weight-bold">$ {{ formatPrice(getTotalOrderWithIva) }} <small class="font-weight-normal">c/IVA</small></div>
                <div class="text-muted" style="font-size:0.8em">$ {{ formatPrice(getTotalOrder) }} <small>s/IVA</small></div>
              </template>
              <template v-else>
                <div class="text-primary h5 mb-0">$ {{ formatPrice(getTotalOrder) }} <small class="font-weight-normal">s/IVA</small></div>
              </template>
            </div>
          </b-col>
          <b-col cols="6" class="p-0">
            <b-button variant="success" block size="lg" class="mobile-cart-continue" @click="nextStep()">
              Continuar <AppIcon name="arrow-right" />
            </b-button>
          </b-col>
        </b-row>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- PASO 3: Revisión y Confirmación -->
    <!-- ============================================ -->
    <div v-if="currentStep === 3" class="mobile-step mobile-step--review p-3 pb-5">
      <!-- Cliente -->
      <b-card class="mb-3 review-customer-card">
        <template #header>
          <div class="d-flex justify-content-between align-items-center">
            <strong>Cliente</strong>
            <b-button variant="link" size="sm" @click="currentStep = 1" class="mobile-text-action-btn p-0">Cambiar</b-button>
          </div>
        </template>
        <div class="customer-summary" v-if="crud.form.customers">
          <div class="customer-summary__heading d-flex align-items-center">
            <b-avatar
              :src="crud.form.customers.image || null"
              :text="avatarFromName(crud.form.customers.name).text"
              :style="avatarFromName(crud.form.customers.name).style"
              variant="dark"
              size="36px"
              class="customer-avatar customer-summary__avatar">
            </b-avatar>
            <div class="customer-summary__name flex-grow-1">
              <span v-if="crud.form.customers.code" class="mr-1">({{ crud.form.customers.code }})</span>{{ crud.form.customers.name }}
            </div>
            <b-button
              size="sm"
              variant="outline-secondary"
              class="customer-summary__toggle"
              :title="customerDetailsExpanded ? 'Minimizar datos del cliente' : 'Ver datos del cliente'"
              :aria-label="customerDetailsExpanded ? 'Minimizar datos del cliente' : 'Ver datos del cliente'"
              :aria-expanded="customerDetailsExpanded ? 'true' : 'false'"
              aria-controls="customer-summary-details"
              @click="customerDetailsExpanded = !customerDetailsExpanded">
              <AppIcon :name="customerDetailsExpanded ? 'chevron-up' : 'chevron-down'" />
            </b-button>
          </div>
          <div id="customer-summary-details" v-show="customerDetailsExpanded" class="customer-summary__details">
              <div v-if="selectedPointSaleLabel" class="customer-summary__detail" title="Punto de venta">
                <AppIcon name="shop" />
                <span>{{ selectedPointSaleLabel }}</span>
              </div>
              <div v-if="selectedMethodsPaymentLabel" class="customer-summary__detail" title="Medio de pago">
                <AppIcon name="cash-stack" />
                <span>{{ selectedMethodsPaymentLabel }}</span>
              </div>
              <div v-if="selectedPriceListLabel" class="customer-summary__detail" title="Lista de precios">
                <AppIcon name="list-ul" />
                <span>{{ selectedPriceListLabel }}</span>
              </div>
              <div v-if="customerDocumentLabel" class="customer-summary__detail" title="Documento">
                <AppIcon name="person-badge" />
                <span>{{ customerDocumentLabel }}</span>
              </div>
              <div v-if="customerAddressLabel" class="customer-summary__detail" title="Dirección">
                <AppIcon name="geo-alt" />
                <span>{{ customerAddressLabel }}</span>
              </div>
              <div v-if="selectedCustomerZone" class="customer-summary__detail" title="Zona">
                <AppIcon name="geo-alt-fill" />
                <span>{{ selectedCustomerZone.name }}</span>
              </div>
              <div v-if="customerPhoneLabel" class="customer-summary__detail" title="Teléfono">
                <AppIcon name="call" />
                <span>{{ customerPhoneLabel }}</span>
              </div>
              <div v-if="crud.form.customers.email" class="customer-summary__detail" title="Correo electrónico">
                <AppIcon name="envelope" />
                <span>{{ crud.form.customers.email }}</span>
              </div>
          </div>
        </div>
      </b-card>

      <!-- Productos -->
      <b-card no-body class="mb-3 review-products-card">
        <template #header>
          <div class="d-flex justify-content-between align-items-center">
            <strong>Productos ({{ crud.form.detail.length }})</strong>
            <div class="d-flex align-items-center">
              <b-button variant="link" size="sm" @click="currentStep = 2" class="mobile-text-action-btn p-0 mr-2">Editar</b-button>
              <b-button
                size="sm"
                variant="outline-secondary"
                class="customer-summary__toggle"
                :title="reviewProductsExpanded ? 'Minimizar productos' : 'Ver productos'"
                :aria-label="reviewProductsExpanded ? 'Minimizar productos' : 'Ver productos'"
                :aria-expanded="reviewProductsExpanded ? 'true' : 'false'"
                aria-controls="review-products-details"
                @click="reviewProductsExpanded = !reviewProductsExpanded">
                <AppIcon :name="reviewProductsExpanded ? 'chevron-up' : 'chevron-down'" />
              </b-button>
            </div>
          </div>
        </template>
        <b-card-body id="review-products-details" v-show="reviewProductsExpanded">
          <EmptyState v-if="!crud.form.detail.length"
                      icon="box-seam"
                      title="No hay productos cargados"
                      description="Volvé al paso anterior para agregarlos." />
          <div v-else>
          <div class="d-flex mb-2">
            <b-button-group size="sm" class="product-view-toggle">
              <b-button
                :variant="isReviewAccordionView ? 'secondary' : 'outline-secondary'"
                title="Vista acordeón"
                aria-label="Vista acordeón"
                @click="setReviewViewMode('accordion')">
                <AppIcon name="list-nested" />
              </b-button>
              <b-button
                :variant="isReviewListView ? 'secondary' : 'outline-secondary'"
                title="Vista listado"
                aria-label="Vista listado"
                @click="setReviewViewMode('list')">
                <AppIcon name="grid3x3-gap" />
              </b-button>
            </b-button-group>
          </div>

          <div v-if="isReviewAccordionView" class="products-accordion products-accordion-review" role="tablist">
            <b-card no-body class="mb-2" v-for="(category, catIndex) in groupedCartItems" :key="category.key">
              <b-card-header header-tag="header" class="p-0" role="tab">
                <b-button
                  block
                  v-b-toggle="'cart-category-collapse-' + category.key"
                  variant="light"
                  class="d-flex align-items-center justify-content-between text-left py-3 px-3 category-header">
                  <div class="d-flex align-items-center">
                    <AppIcon name="folder-fill" class="mr-2 text-primary" />
                    <span class="font-weight-bold">{{ category.name }}</span>
                  </div>
                  <div class="d-flex align-items-center">
                    <b-badge variant="dark" pill class="mr-2">{{ getCartCategoryItemCount(category) }}</b-badge>
                    <AppIcon name="chevron-down" class="zone-chevron" />
                  </div>
                </b-button>
              </b-card-header>
              <b-collapse :id="'cart-category-collapse-' + category.key"
                          :visible="isCartCategoryExpanded(catIndex)"
                          role="tabpanel">
                <div class="review-category-body p-2">
                  <b-card no-body class="mb-2 subcategory-card"
                          v-for="(subcategory, subIndex) in category.subcategories"
                          :key="subcategory.key">
                    <b-card-header header-tag="header" class="p-0" role="tab">
                      <b-button
                        block
                        v-b-toggle="'cart-subcategory-collapse-' + category.key + '-' + subcategory.key"
                        variant="light"
                        class="d-flex align-items-center justify-content-between text-left py-2 px-3 subcategory-header">
                        <div class="d-flex align-items-center">
                          <AppIcon name="folder2-open" class="mr-2 text-muted" />
                          <span class="font-weight-bold">{{ subcategory.name }}</span>
                        </div>
                        <div class="d-flex align-items-center">
                          <b-badge variant="secondary" pill class="mr-2">{{ getCartSubcategoryItemCount(subcategory) }}</b-badge>
                          <AppIcon name="chevron-down" class="zone-chevron" />
                        </div>
                      </b-button>
                    </b-card-header>
                    <b-collapse :id="'cart-subcategory-collapse-' + category.key + '-' + subcategory.key"
                                :visible="isCartSubcategoryExpanded(catIndex, subIndex)"
                                role="tabpanel">
                      <div class="review-subcategory-body p-2">
                        <div v-for="entry in subcategory.items"
                            :key="entry.key"
                            class="review-accordion-item py-2 border-bottom"
                            :style="entry.item.is_extra ? 'background-color: var(--app-color-primary-soft); border-radius: 0; padding-left: 8px; padding-right: 8px;' : ''">
                          <div class="d-flex justify-content-between align-items-start review-item-header">
                            <div class="flex-grow-1 review-item-main">
                              <div>
                                {{ entry.item.product ? entry.item.product.name : '' }}
                                <b-badge v-if="entry.item.is_extra" variant="success" class="ml-1">EXTRA {{ extraDiscountPercentage }}%</b-badge>
                              </div>
                              <div class="text-muted small" v-if="entry.item.product && entry.item.product.code">
                                {{ entry.item.product.code }}
                              </div>
                              <small class="d-block mt-1">
                                <template v-if="getProductPriceWithIva(entry.item.product, entry.item.amount)">
                                  <strong class="text-primary">$ {{ formatPrice(getProductPriceWithIva(entry.item.product, entry.item.amount)) }} <span class="font-weight-normal">c/IVA</span></strong>
                                  <span class="mx-1 text-muted">·</span>
                                  <span class="text-muted">$ {{ formatPrice(entry.item.amount) }} <span class="font-weight-normal">s/IVA</span></span>
                                </template>
                                <template v-else>
                                  <strong class="text-dark">$ {{ formatPrice(entry.item.amount) }} <span class="font-weight-normal">s/IVA</span></strong>
                                </template>
                                <span v-if="entry.item.unit_measurement && entry.item.unit_measurement.reference">
                                  x {{ entry.item.unit_measurement.reference }}
                                </span>
                                <AppIcon v-if="entry.item.product && entry.item.product.unit_measurement_purchase_id && entry.item.product.unit_measurement_sale_id && entry.item.product.unit_measurement_purchase_id !== entry.item.product.unit_measurement_sale_id" name="info-circle" class="text-info ml-1" style="cursor:pointer;font-size:0.95rem" @click.stop="openConversionModal(entry.item.product)" fill />
                                <span v-if="entry.item.discount">
                                  (Desc {{ entry.item.discount }}%)
                                </span>
                              </small>
                            </div>
                            <div class="text-right review-item-price">
                              <div class="review-item-total">
                                <template v-if="getProductPriceWithIva(entry.item.product, entry.item.amount)">
                                  <div class="font-weight-bold text-primary">$ {{ formatPrice(entry.item.quantity * getProductPriceWithIva(entry.item.product, entry.item.amount)) }} <small class="font-weight-normal">c/IVA</small></div>
                                  <div class="text-muted" style="font-size:0.8em">$ {{ formatPrice(entry.item.quantity * entry.item.amount) }} <small>s/IVA</small></div>
                                </template>
                                <template v-else>
                                  <div class="font-weight-bold">$ {{ formatPrice(entry.item.quantity * entry.item.amount) }} <small class="font-weight-normal text-muted">s/IVA</small></div>
                                </template>
                              </div>
                              <div
                                v-if="getItemStock(entry.item) !== null"
                                class="product-stock-pill product-stock-pill-inline d-inline-flex align-items-center mt-1"
                                :class="{ 'product-stock-pill-empty': getItemStock(entry.item) <= 0 }">
                                <AppIcon name="box-seam" class="mr-1" />
                                <span class="product-stock-value">{{ formatStock(getItemStock(entry.item)) }}</span>
                              </div>
                              <div class="mt-1">
                                <b-button
                                  variant="link"
                                  size="sm"
                                  class="mobile-icon-action-btn cart-remove-btn p-0"
                                  :class="{ 'cart-remove-btn--confirm': isCartRemovalPending(entry.item, entry.index) }"
                                  :title="isCartRemovalPending(entry.item, entry.index) ? 'Confirmar quitar producto' : 'Quitar producto'"
                                  :aria-label="isCartRemovalPending(entry.item, entry.index) ? 'Confirmar quitar producto' : 'Quitar producto'"
                                  @click="requestCartRemoval(entry.index)">
                                  <template v-if="isCartRemovalPending(entry.item, entry.index)">OK</template>
                                  <AppIcon v-else name="trash" />
                                </b-button>
                              </div>
                            </div>
                          </div>
                          <div class="mt-2" style="display:grid;grid-template-columns:1fr 1fr;gap:6px;align-items:center">
                            <b-input-group size="sm" style="flex-wrap:nowrap">
                              <b-input-group-prepend>
                                <b-button variant="outline-secondary" @click="decrementCartItem(entry.item)">-</b-button>
                              </b-input-group-prepend>
                              <b-form-input
                                  v-model.number="entry.item.quantity"
                                  type="number"
                                  min="1"
                                  class="text-center"
                                  @focus="handleMobileInputFocus($event, entry.item, 'quantity')"
                                  @blur="handleMobileInputBlur($event, entry.item, 'quantity')"
                                  @keydown.up.prevent="incrementCartItem(entry.item)"
                                  @keydown.down.prevent="decrementCartItem(entry.item)"
                                  @change="onCartQuantityChange(entry.item, $event)"
                                  @input="onCartQuantityInput(entry.item, $event)">
                              </b-form-input>
                              <b-input-group-append>
                                <b-button variant="outline-secondary" @click="incrementCartItem(entry.item)">+</b-button>
                              </b-input-group-append>
                            </b-input-group>
                            <b-input-group size="sm" class="mobile-discount-input w-100"
                                          :class="{ 'discount-exceeded': isCartItemDiscountExceeded(entry.item) }">
                              <b-form-input
                                  v-model="entry.item.discount"
                                  type="text"
                                  inputmode="decimal"
                                  :disabled="!!entry.item.is_extra"
                                  placeholder="Desc"
                                  @focus="handleMobileInputFocus($event, entry.item, 'discount')"
                                  @blur="handleMobileInputBlur($event, entry.item, 'discount')"
                                  @change="onCartDiscountChange(entry.item, $event)"
                                  @input="onCartDiscountInput(entry.item, $event)">
                              </b-form-input>
                              <b-input-group-append>
                                <b-input-group-text>%</b-input-group-text>
                              </b-input-group-append>
                            </b-input-group>
                          </div>
                          <div class="mt-2" v-if="isCartItemDiscountExceeded(entry.item) && !entry.item.is_extra">
                            <small class="text-danger">
                              <AppIcon name="exclamation-triangle-fill" class="mr-1" />
                              Desc. máx {{ formatPercent(getItemMaxDiscount(entry.item)) }} · Asignado {{ formatPercent(entry.item.discount) }}
                            </small>
                          </div>
                        </div>
                      </div>
                    </b-collapse>
                  </b-card>
                </div>
              </b-collapse>
            </b-card>
          </div>

          <div v-else-if="isReviewListView" class="review-products-list">
            <div
              v-for="(item, index) in crud.form.detail"
              :key="'review-list-' + (item.product && item.product.id ? item.product.id : 'item') + '-' + index"
              class="product-card mb-3 p-3 border rounded"
              :style="item.is_extra ? 'background-color: var(--app-color-primary-soft);' : ''">
              <div class="d-flex justify-content-between align-items-start review-item-header">
                <div class="flex-grow-1 pr-2 review-item-main">
                  <div class="font-weight-bold">
                    {{ item.product ? item.product.name : '' }}
                    <b-badge v-if="item.is_extra" variant="success" class="ml-1">EXTRA {{ extraDiscountPercentage }}%</b-badge>
                  </div>
                  <div class="text-muted small" v-if="item.product && item.product.code">{{ item.product.code }}</div>
                  <div class="text-muted small mt-1">
                    <span class="d-inline-block mr-2"><strong>Categoría:</strong> {{ getProductCategoryName(item.product) }}</span>
                    <span class="d-inline-block"><strong>Subcategoría:</strong> {{ getProductSubcategoryName(item.product) }}</span>
                  </div>
                  <small class="d-block mt-1">
                    <template v-if="getProductPriceWithIva(item.product, item.amount)">
                      <strong class="text-primary">$ {{ formatPrice(getProductPriceWithIva(item.product, item.amount)) }} <span class="font-weight-normal">c/IVA</span></strong>
                      <span class="mx-1 text-muted">·</span>
                      <span class="text-muted">$ {{ formatPrice(item.amount) }} <span class="font-weight-normal">s/IVA</span></span>
                    </template>
                    <template v-else>
                      <strong class="text-dark">$ {{ formatPrice(item.amount) }} <span class="font-weight-normal">s/IVA</span></strong>
                    </template>
                    <span v-if="item.unit_measurement && item.unit_measurement.reference">
                      x {{ item.unit_measurement.reference }}
                    </span>
                    <AppIcon v-if="item.product && item.product.unit_measurement_purchase_id && item.product.unit_measurement_sale_id && item.product.unit_measurement_purchase_id !== item.product.unit_measurement_sale_id" name="info-circle" class="text-info ml-1" style="cursor:pointer;font-size:0.95rem" @click.stop="openConversionModal(item.product)" fill />
                    <span v-if="item.discount">
                      (Desc {{ item.discount }}%)
                    </span>
                  </small>
                </div>
                <div class="text-right review-item-price">
                  <div class="review-item-total">
                    <template v-if="getProductPriceWithIva(item.product, item.amount)">
                      <div class="font-weight-bold text-primary">$ {{ formatPrice(item.quantity * getProductPriceWithIva(item.product, item.amount)) }} <small class="font-weight-normal">c/IVA</small></div>
                      <div class="text-muted" style="font-size:0.8em">$ {{ formatPrice(item.quantity * item.amount) }} <small>s/IVA</small></div>
                    </template>
                    <template v-else>
                      <div class="font-weight-bold">$ {{ formatPrice(item.quantity * item.amount) }} <small class="font-weight-normal text-muted">s/IVA</small></div>
                    </template>
                  </div>
                  <div
                    v-if="getItemStock(item) !== null"
                    class="product-stock-pill product-stock-pill-inline d-inline-flex align-items-center mt-1"
                    :class="{ 'product-stock-pill-empty': getItemStock(item) <= 0 }">
                    <AppIcon name="box-seam" class="mr-1" />
                    <span class="product-stock-value">{{ formatStock(getItemStock(item)) }}</span>
                  </div>
                  <div class="mt-1">
                    <b-button
                      variant="link"
                      size="sm"
                      class="mobile-icon-action-btn cart-remove-btn p-0"
                      :class="{ 'cart-remove-btn--confirm': isCartRemovalPending(item, index) }"
                      :title="isCartRemovalPending(item, index) ? 'Confirmar quitar producto' : 'Quitar producto'"
                      :aria-label="isCartRemovalPending(item, index) ? 'Confirmar quitar producto' : 'Quitar producto'"
                      @click="requestCartRemoval(index)">
                      <template v-if="isCartRemovalPending(item, index)">OK</template>
                      <AppIcon v-else name="trash" />
                    </b-button>
                  </div>
                </div>
              </div>
              <div class="mt-2" style="display:grid;grid-template-columns:1fr 1fr;gap:6px;align-items:center">
                <b-input-group size="sm" style="flex-wrap:nowrap">
                  <b-input-group-prepend>
                    <b-button variant="outline-secondary" @click="decrementCartItem(item)">-</b-button>
                  </b-input-group-prepend>
                  <b-form-input
                    v-model.number="item.quantity"
                    type="number"
                    min="1"
                    class="text-center"
                    @focus="handleMobileInputFocus($event, item, 'quantity')"
                    @blur="handleMobileInputBlur($event, item, 'quantity')"
                    @keydown.up.prevent="incrementCartItem(item)"
                    @keydown.down.prevent="decrementCartItem(item)"
                    @change="onCartQuantityChange(item, $event)"
                    @input="onCartQuantityInput(item, $event)">
                  </b-form-input>
                  <b-input-group-append>
                    <b-button variant="outline-secondary" @click="incrementCartItem(item)">+</b-button>
                  </b-input-group-append>
                </b-input-group>
                <b-input-group size="sm" class="mobile-discount-input w-100"
                              :class="{ 'discount-exceeded': isCartItemDiscountExceeded(item) }">
                  <b-form-input
                    v-model="item.discount"
                    type="text"
                    inputmode="decimal"
                    :disabled="!!item.is_extra"
                    placeholder="Desc"
                    @focus="handleMobileInputFocus($event, item, 'discount')"
                    @blur="handleMobileInputBlur($event, item, 'discount')"
                    @change="onCartDiscountChange(item, $event)"
                    @input="onCartDiscountInput(item, $event)">
                  </b-form-input>
                  <b-input-group-append>
                    <b-input-group-text>%</b-input-group-text>
                  </b-input-group-append>
                </b-input-group>
              </div>
              <div class="mt-2" v-if="isCartItemDiscountExceeded(item) && !item.is_extra">
                <small class="text-danger">
                  <AppIcon name="exclamation-triangle-fill" class="mr-1" />
                  Desc. máx {{ formatPercent(getItemMaxDiscount(item)) }} · Asignado {{ formatPercent(item.discount) }}
                </small>
              </div>
            </div>
          </div>
          </div>
        </b-card-body>
      </b-card>

      <!-- Observaciones -->
      <b-card class="mb-3" v-if="parameters.haveReason">
        <b-form-group label="Motivo del pedido" label-size="sm">
          <b-form-select
            v-model="crud.form.reason_id"
            :options="reasonOptions"
            size="lg">
          </b-form-select>
        </b-form-group>
      </b-card>

      <!-- Observaciones -->
      <b-card class="mb-3">
        <b-form-group label="Observaciones (opcional)" label-size="sm">
          <app-textarea
            v-model="crud.form.observations"
            rows="6"
            placeholder="Agregar observaciones...">
          </app-textarea>
        </b-form-group>
      </b-card>

      <!-- Total -->
      <b-card class="mb-5">
        <b-row class="mb-2">
          <b-col cols="6">Subtotal</b-col>
          <b-col cols="6" class="text-right">
            <template v-if="getSubTotalOrderWithIva !== getSubTotalOrder">
              <div class="font-weight-bold text-primary">$ {{ formatPrice(getSubTotalOrderWithIva) }} <small class="font-weight-normal">c/IVA</small></div>
              <div class="text-muted" style="font-size:0.85em">$ {{ formatPrice(getSubTotalOrder) }} <small>s/IVA</small></div>
            </template>
            <template v-else>$ {{ formatPrice(getSubTotalOrder) }} <small class="text-muted">s/IVA</small></template>
          </b-col>
        </b-row>
        <b-row class="mb-2 align-items-center">
          <b-col cols="6">Descuento general</b-col>
          <b-col cols="6" class="text-right">
            <b-input-group size="sm">
                <b-form-input
                  v-model="crud.form.percentage_discount"
                  type="text"
                  inputmode="decimal"
                  @focus="handleMobileInputFocus($event, crud.form, 'percentage_discount')"
                  @blur="handleMobileInputBlur($event, crud.form, 'percentage_discount')"
                  @change="onGeneralDiscountChange($event)"
                  @input="onGeneralDiscountInput($event)">
                </b-form-input>
              <b-input-group-append>
                <b-input-group-text>%</b-input-group-text>
              </b-input-group-append>
            </b-input-group>
          </b-col>
        </b-row>
        <b-row class="mb-2" v-if="crud.form.discounts_price">
          <b-col cols="6">Descuento</b-col>
          <b-col cols="6" class="text-right text-danger">- $ {{ formatPrice(crud.form.discounts_price) }}</b-col>
        </b-row>
        <hr class="my-2">
        <b-row class="font-weight-bold h5 mb-0">
          <b-col cols="6">Total</b-col>
          <b-col cols="6" class="text-right">
            <template v-if="getTotalOrderWithIva !== getTotalOrder">
              <div class="font-weight-bold text-primary h5 mb-0">$ {{ formatPrice(getTotalOrderWithIva) }} <small class="font-weight-normal">c/IVA</small></div>
              <div class="text-muted" style="font-size:0.8em">$ {{ formatPrice(getTotalOrder) }} <small>s/IVA</small></div>
            </template>
            <template v-else>
              <span class="text-primary">$ {{ formatPrice(getTotalOrder) }} <small class="font-weight-normal">s/IVA</small></span>
            </template>
          </b-col>
        </b-row>
      </b-card>

      <!-- Botón Confirmar -->
      <div class="fixed-bottom mobile-footer mobile-footer--action">
        <b-button
          variant="success"
          block
          size="lg"
          @click="save()"
          :disabled="loading.saving">
          <b-spinner small v-if="loading.saving"></b-spinner>
          <span v-else>
            <AppIcon name="check2-circle" /> {{ saveButtonLabel }}
          </span>
        </b-button>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- Modal: Duplicar pedido -->
    <!-- ============================================ -->
    <b-modal
      v-model="modal.duplicateOrder"
      dialog-class="mobile-order-dialog"
      size="lg"
      centered
      scrollable
      hide-footer
      @show="loadDuplicateOrders()">
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="files" /></span>
        <h5 class="app-modal-header__title">Duplicar pedido</h5>
        <button type="button" class="app-modal-header__close mobile-modal-close-btn" aria-label="Cerrar" @click="modal.duplicateOrder = false">
          <AppIcon name="x" />
        </button>
      </div>
      <div v-if="duplicateOrder.loading" class="text-center py-5">
        <b-spinner variant="primary"></b-spinner>
        <p class="mt-2 text-muted">Buscando últimos pedidos...</p>
      </div>
      <EmptyState v-else-if="!duplicateOrder.items.length"
                  icon="inbox"
                  title="No hay pedidos anteriores para este cliente" />
      <div v-else>
        <p class="text-muted mb-3">Seleccioná un pedido para usarlo como base del nuevo pedido. Los precios se actualizarán al de hoy.</p>
        <div
          v-for="order in duplicateOrder.items"
          :key="order.id"
          class="border rounded mb-3 overflow-hidden">
          <!-- Encabezado del pedido -->
          <div class="px-3 py-3 d-flex justify-content-between align-items-start bg-light">
            <div>
              <div class="font-weight-bold" style="font-size:1.05rem">Pedido #{{ order.id }}</div>
              <div class="text-muted" style="font-size:0.9rem">{{ formatOrderDate(order.date) }}</div>
              <div class="text-muted mt-1" style="font-size:0.85rem">
                <AppIcon name="shop" class="mr-1" />
                <span v-if="duplicateOrder.loadedDetails[order.id] && duplicateOrder.loadedDetails[order.id].points_sales">
                  {{ duplicateOrder.loadedDetails[order.id].points_sales.name }}
                </span>
                <span v-else-if="duplicateOrder.loadedDetails[order.id] === undefined">
                  <b-spinner style="width:10px;height:10px"></b-spinner>
                </span>
                <span v-else>—</span>
              </div>
            </div>
            <div class="text-right">
              <div class="font-weight-bold text-primary" style="font-size:1.1rem">$ {{ formatPrice(getOrderNetAmount(order)) }}</div>
              <b-button
                variant="primary"
                class="mt-2"
                style="min-width:130px;font-size:0.95rem"
                :disabled="duplicateOrder.selecting"
                @click="selectOrderForDuplicate(order)">
                <b-spinner small v-if="duplicateOrder.selecting && duplicateOrder.selectedId === order.id"></b-spinner>
                <span v-else><AppIcon name="files" class="mr-1" />Repetir pedido</span>
              </b-button>
            </div>
          </div>
          <!-- Lista de productos -->
          <div class="px-3 py-2 border-top" style="background: var(--app-color-surface-subtle)">
            <!-- Cargando detalle -->
            <div v-if="duplicateOrder.loadedDetails[order.id] === undefined" class="text-muted py-2">
              <b-spinner small class="mr-1"></b-spinner> Cargando productos...
            </div>
            <!-- Sin productos -->
            <div v-else-if="!duplicateOrder.loadedDetails[order.id] || !duplicateOrder.loadedDetails[order.id].detail || !duplicateOrder.loadedDetails[order.id].detail.length" class="text-muted py-2">
              Sin productos
            </div>
            <!-- Productos -->
            <div v-else>
              <div
                v-for="(item, idx) in duplicateOrder.loadedDetails[order.id].detail"
                :key="idx"
                class="d-flex justify-content-between align-items-center py-2"
                :class="idx < duplicateOrder.loadedDetails[order.id].detail.length - 1 ? 'border-bottom' : ''"
                style="font-size:0.9rem">
                <span class="text-truncate mr-2" style="max-width:55%">{{ item.product ? item.product.name : 'Producto' }}</span>
                <span class="text-muted text-nowrap">
                  x{{ item.quantity }}
                  <span v-if="item.unit_measurement && item.unit_measurement.reference" class="text-secondary"> {{ item.unit_measurement.reference }}</span>
                  · $ {{ formatPrice(item.amount) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- ============================================ -->
    <!-- Modal: Cobranza -->
    <!-- ============================================ -->
    <b-modal
      v-model="modal.cobranza"
      dialog-class="mobile-order-dialog"
      centered
      scrollable
      hide-footer>
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="cash-stack" /></span>
        <h5 class="app-modal-header__title">Registrar cobranza</h5>
        <button type="button" class="app-modal-header__close mobile-modal-close-btn" aria-label="Cerrar" @click="modal.cobranza = false">
          <AppIcon name="x" />
        </button>
      </div>

      <div v-if="crud.form.customers" class="mobile-report-customer mb-3">
        <b-avatar
          :src="crud.form.customers.image || null"
          :text="avatarFromName(crud.form.customers.name).text"
          :style="avatarFromName(crud.form.customers.name).style"
          variant="dark"
          size="44px"
          class="customer-avatar mobile-report-customer__avatar">
        </b-avatar>
        <div>
          <small class="text-muted d-block">Cliente</small>
          <div class="font-weight-bold">
            <span v-if="crud.form.customers.code" class="mr-1">({{ crud.form.customers.code }})</span>{{ crud.form.customers.name }}
          </div>
          <small v-if="getCustomerFullAddress(crud.form.customers)" class="text-muted d-block">
            <AppIcon name="geo-alt" class="mr-1" />{{ getCustomerFullAddress(crud.form.customers) }}
          </small>
        </div>
      </div>

      <!-- Agregar ítem -->
      <div class="d-flex align-items-center mb-3" style="gap:8px">
        <b-form-select
          v-model="cobranza.form.newItem.type"
          :options="cobranza.typeOptions"
          class="cobranza-new-item-field">
        </b-form-select>
        <b-input-group class="cobranza-new-item-field">
          <b-input-group-prepend>
            <b-input-group-text>$</b-input-group-text>
          </b-input-group-prepend>
          <app-number-input
            v-model="cobranza.form.newItem.amount"
            placeholder="Importe"
            aria-label="Importe">
          </app-number-input>
        </b-input-group>
        <b-button variant="outline-primary" class="mobile-modal-icon-action"
          :disabled="!cobranza.form.newItem.amount || parseFloat(cobranza.form.newItem.amount) <= 0 || (cobranza.form.newItem.type === 'Cheque' && (!cobranza.form.newItem.check.type || !cobranza.form.newItem.check.number || !cobranza.form.newItem.check.cuit))"
          @click="cobranzaAddItem()">
          <AppIcon name="plus" />
        </b-button>
      </div>

      <!-- Campos adicionales para cheque -->
      <div v-if="cobranza.form.newItem.type === 'Cheque'" class="border rounded p-3 mb-3 bg-light">
        <div class="font-weight-bold mb-2">Datos del cheque</div>
        <div class="d-flex mb-2" style="gap:8px">
          <div style="flex:1">
            <div class="text-muted mb-1" style="font-size:0.85rem">Tipo</div>
            <b-form-select v-model="cobranza.form.newItem.check.type"
              :options="[{value:'de_terceros', text:'De terceros'},{value:'propios', text:'Propios'}]">
            </b-form-select>
          </div>
          <div style="flex:1">
            <div class="text-muted mb-1" style="font-size:0.85rem">Banco</div>
            <b-form-input v-model="cobranza.form.newItem.check.bank" placeholder="Banco"></b-form-input>
          </div>
        </div>
        <div class="d-flex mb-2" style="gap:8px">
          <div style="flex:1">
            <div class="text-muted mb-1" style="font-size:0.85rem">Número <span class="text-danger">*</span></div>
            <b-form-input v-model="cobranza.form.newItem.check.number" placeholder="Nro cheque"></b-form-input>
          </div>
          <div style="flex:1">
            <div class="text-muted mb-1" style="font-size:0.85rem">CUIT <span class="text-danger">*</span></div>
            <b-form-input v-model="cobranza.form.newItem.check.cuit" placeholder="CUIT"></b-form-input>
          </div>
        </div>
        <div class="d-flex" style="gap:8px">
          <div style="flex:1">
            <div class="text-muted mb-1" style="font-size:0.85rem">Fecha</div>
            <b-form-input v-model="cobranza.form.newItem.check.date_movement" type="date"></b-form-input>
          </div>
          <div style="flex:1">
            <div class="text-muted mb-1" style="font-size:0.85rem">Observaciones</div>
            <b-form-input v-model="cobranza.form.newItem.check.observations" placeholder="Obs. cheque"></b-form-input>
          </div>
        </div>
        <div v-if="!cobranza.form.newItem.check.number || !cobranza.form.newItem.check.cuit"
          class="text-danger mt-2" style="font-size:0.85rem">
          Complete los datos del cheque (número y CUIT) para poder agregar.
        </div>
      </div>

      <!-- Lista de ítems -->
      <div v-if="cobranza.form.items.length" class="mb-3 border rounded">
        <div v-for="(item, idx) in cobranza.form.items" :key="idx"
          class="px-3 py-2"
          :class="idx < cobranza.form.items.length - 1 ? 'border-bottom' : ''">
          <div class="d-flex align-items-center justify-content-between">
            <span>{{ cobranza.typeOptions.find(o => o.value === item.type).text }}</span>
            <div class="d-flex align-items-center" style="gap:10px">
              <span class="font-weight-bold">$ {{ parseFloat(item.amount).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</span>
              <b-button variant="link" class="mobile-modal-icon-action text-danger p-0" title="Quitar importe" @click="cobranza.form.items.splice(idx, 1)">
                <AppIcon name="trash" font-scale="1.1" />
              </b-button>
            </div>
          </div>
          <div v-if="item.check" class="text-muted mt-1" style="font-size:0.8rem">
            <span v-if="item.check.bank">{{ item.check.bank }}</span>
            <span v-if="item.check.number"> · Nº {{ item.check.number }}</span>
            <span v-if="item.check.cuit"> · CUIT {{ item.check.cuit }}</span>
            <span v-if="item.check.date_movement"> · {{ item.check.date_movement }}</span>
          </div>
        </div>
        <div class="d-flex justify-content-between px-3 py-2 border-top bg-light">
          <span class="font-weight-bold">Total</span>
          <span class="font-weight-bold text-primary" style="font-size:1.05rem">$ {{ cobranzaTotal }}</span>
        </div>
      </div>
      <EmptyState v-else icon="cash-stack" title="Agregá al menos un medio de pago" class="mb-3" />

      <!-- Observaciones -->
      <app-textarea
        v-model="cobranza.form.observations"
        placeholder="Observaciones (opcional)"
        rows="2"
        class="mb-3">
      </app-textarea>

      <div class="d-flex mt-3 mobile-report-modal-actions">
        <b-button class="mobile-report-modal-action" variant="outline-secondary" @click="modal.cobranza = false">Cancelar</b-button>
        <b-button class="mobile-report-modal-action" variant="primary" :disabled="cobranza.loading || !cobranza.form.items.length" @click="saveCobranza()">
          <b-spinner small v-if="cobranza.loading" class="mr-1"></b-spinner>
          Guardar
        </b-button>
      </div>
    </b-modal>

    <!-- ============================================ -->
    <!-- Modal: Observaciones -->
    <!-- ============================================ -->
    <b-modal
      v-model="modal.observations"
      dialog-class="mobile-order-dialog"
      centered
      hide-footer>
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="chat" /></span>
        <h5 class="app-modal-header__title">Nueva observación</h5>
        <button type="button" class="app-modal-header__close mobile-modal-close-btn" aria-label="Cerrar" @click="modal.observations = false">
          <AppIcon name="x" />
        </button>
      </div>
      <div v-if="crud.form.customers" class="mobile-report-customer mb-3">
        <b-avatar
          :src="crud.form.customers.image || null"
          :text="avatarFromName(crud.form.customers.name).text"
          :style="avatarFromName(crud.form.customers.name).style"
          variant="dark"
          size="44px"
          class="customer-avatar mobile-report-customer__avatar">
        </b-avatar>
        <div>
          <small class="text-muted d-block">Cliente</small>
          <div class="font-weight-bold">
            <span v-if="crud.form.customers.code" class="mr-1">({{ crud.form.customers.code }})</span>{{ crud.form.customers.name }}
          </div>
          <small v-if="getCustomerFullAddress(crud.form.customers)" class="text-muted d-block">
            <AppIcon name="geo-alt" class="mr-1" />{{ getCustomerFullAddress(crud.form.customers) }}
          </small>
        </div>
      </div>
      <app-textarea
        v-model="observaciones.form.observations"
        placeholder="Ingresar las observaciones..."
        rows="5"
        autofocus>
      </app-textarea>
      <div class="d-flex mt-3 mobile-report-modal-actions">
        <b-button class="mobile-report-modal-action" variant="outline-secondary" @click="modal.observations = false">Cancelar</b-button>
        <b-button class="mobile-report-modal-action" variant="primary" :disabled="observaciones.loading || !observaciones.form.observations.trim()" @click="saveObservaciones()">
          <b-spinner small v-if="observaciones.loading" class="mr-1"></b-spinner>
          Guardar
        </b-button>
      </div>
    </b-modal>

    <!-- ============================================ -->
    <!-- Modal: Éxito cobranza -->
    <!-- ============================================ -->
    <b-modal v-model="modal.cobranzaSuccess" dialog-class="mobile-order-dialog" centered hide-header hide-footer no-close-on-backdrop>
      <div class="text-center py-4">
        <AppIcon name="check2-circle" font-scale="4" class="text-success" fill />
        <h4 class="mt-3">¡Cobranza registrada!</h4>
        <p class="text-muted">La cobranza fue guardada correctamente.</p>
        <b-button variant="success" block size="lg" @click="modal.cobranzaSuccess = false">Aceptar</b-button>
      </div>
    </b-modal>

    <!-- ============================================ -->
    <!-- Modal: Éxito observaciones -->
    <!-- ============================================ -->
    <b-modal v-model="modal.observacionesSuccess" dialog-class="mobile-order-dialog" centered hide-header hide-footer no-close-on-backdrop>
      <div class="text-center py-4">
        <AppIcon name="check2-circle" font-scale="4" class="text-success" fill />
        <h4 class="mt-3">¡Observación guardada!</h4>
        <p class="text-muted">La observación fue registrada correctamente.</p>
        <b-button variant="success" block size="lg" @click="modal.observacionesSuccess = false">Aceptar</b-button>
      </div>
    </b-modal>

    <!-- ============================================ -->
    <!-- Modal de edición -->
    <!-- ============================================ -->
    <b-modal
      v-model="modal.editOrder"
      dialog-class="mobile-order-dialog"
      size="lg"
      centered
      hide-footer
      @show="onEditOrderModalShow">
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="search" /></span>
        <h5 class="app-modal-header__title">Buscar pedido cerrado para editar</h5>
        <button type="button" class="app-modal-header__close mobile-modal-close-btn" aria-label="Cerrar" @click="modal.editOrder = false">
          <AppIcon name="x" />
        </button>
      </div>
      <div class="mobile-edit-orders-help mb-3">
        Se muestran pedidos cerrados del vendedor actual. Seleccioná uno para cargarlo y continuar la edición.
      </div>
      <div class="mb-3">
        <b-input-group>
          <b-form-input
            v-model="editOrder.search"
            placeholder="Buscar por N° de pedido o cliente..."
            :disabled="editOrder.loading || editOrder.selecting">
          </b-form-input>
          <b-input-group-append>
            <b-button variant="outline-secondary" @click="loadEditOrders(editOrder.pagination.currentPage)" :disabled="editOrder.loading || editOrder.selecting">
              <b-spinner small v-if="editOrder.loading"></b-spinner>
              <AppIcon name="refresh" v-else />
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </div>

      <div class="mobile-edit-orders-grid">
        <b-table
          class="mb-0 mobile-edit-orders-table app-table app-table--compact"
          :items="filteredEditOrders"
          :fields="editOrdersTableFields"
          :hover="true"
          :small="true"
          responsive
          :busy="editOrder.loading"
          :tbody-tr-class="editOrderRowClass"
          show-empty
          @row-clicked="selectOrderForEdit">
          <template #table-busy>
            <div class="text-center py-3">
              <b-spinner small class="mr-2"></b-spinner>
              Cargando pedidos...
            </div>
          </template>
          <template #empty>
            <EmptyState icon="assignment" title="No hay pedidos cerrados para mostrar" />
          </template>
          <template #cell(order)="data">
            <b-badge v-if="data.item.isQueueItem" :variant="data.item.queueStatus === 'error' ? 'danger' : 'warning'">{{ isQueueItemExhausted(data.item) ? 'REVISAR' : 'PENDIENTE' }}</b-badge>
            <small v-else class="font-weight-bold d-block">#{{ data.item.id }}</small>
          </template>
          <template #cell(info)="data">
            <small class="d-block text-muted">{{ formatOrderDate(data.item.date) }}</small>
            <small class="d-block font-weight-bold mobile-edit-order-customer">{{ getOrderCustomerName(data.item) }}</small>
          </template>
          <template #cell(total)="data">
            <div v-if="getOrderAmountWithIva(data.item) !== null" class="text-right">
              <small class="d-block font-weight-bold text-primary">
                {{ formatOrderAmount(getOrderAmountWithIva(data.item)) }}
                <span class="font-weight-normal">c/IVA</span>
              </small>
              <small class="d-block text-muted" style="font-size:0.8em">
                {{ formatOrderAmount(getOrderNetAmount(data.item)) }}
                <span>s/IVA</span>
              </small>
            </div>
            <small v-else class="font-weight-bold">
              {{ formatOrderAmount(getOrderNetAmount(data.item)) }}
              <span class="font-weight-normal text-muted">s/IVA</span>
            </small>
          </template>
          <template #cell(actions)="data">
            <div class="d-flex mobile-equal-height-actions" style="gap:4px">
              <b-button
                size="sm"
                variant="outline-primary"
                class="mobile-edit-select-btn"
                @click.stop="selectOrderForEdit(data.item)"
                :disabled="editOrder.selecting || (!data.item.isQueueItem && !isOnline)">
                <AppIcon name="check2-circle" />
              </b-button>
              <b-button
                v-if="data.item.isQueueItem || (access.elements.deleteOrders && data.item.status === 'closed' && isOnline)"
                size="sm"
                variant="outline-danger"
                class="mobile-edit-select-btn"
                @click.stop="removeOrder(data.item)"
                :disabled="editOrder.selecting">
                <AppIcon name="trash" />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>

      <div class="d-flex align-items-center justify-content-between mt-2">
        <small class="text-muted">
          Mostrando {{ filteredEditOrders.length }} de {{ editOrder.pagination.total }} pedidos
        </small>
        <div class="mobile-equal-height-actions">
          <b-button
            size="sm"
            variant="outline-secondary"
            class="mr-1"
            @click="loadEditOrders(editOrder.pagination.currentPage - 1)"
            :disabled="!hasPreviousEditOrdersPage || editOrder.loading || editOrder.selecting">
            Anterior
          </b-button>
          <b-button
            size="sm"
            variant="outline-secondary"
            @click="loadEditOrders(editOrder.pagination.currentPage + 1)"
            :disabled="!hasNextEditOrdersPage || editOrder.loading || editOrder.selecting">
            Siguiente
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- ============================================ -->
    <!-- Modal gestión cola offline -->
    <!-- ============================================ -->
    <b-modal v-model="modal.queue" dialog-class="mobile-order-dialog" size="md" hide-footer>
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="cloud-upload" /></span>
        <h5 class="app-modal-header__title">Pedidos pendientes de sincronizar</h5>
        <button type="button" class="app-modal-header__close mobile-modal-close-btn" aria-label="Cerrar" @click="modal.queue = false">
          <AppIcon name="x" />
        </button>
      </div>
      <EmptyState v-if="offlineQueue.length === 0" icon="cloud-upload" title="No hay pedidos pendientes" />
      <div v-for="item in offlineQueue" :key="item.localId" class="d-flex justify-content-between align-items-start border-bottom py-2">
        <div>
          <small class="text-muted d-block">{{ formatQueueDate(item.savedAt) }}</small>
          <span>Cliente ID: <strong>{{ item.data.customers_id }}</strong></span>
          <span class="ml-2"><strong>{{ item.data.detail ? item.data.detail.length : 0 }} producto(s)</strong></span>
          <b-badge :variant="item.status === 'error' ? 'danger' : 'warning'" class="ml-2">{{ item.status }}</b-badge>
          <small v-if="item.error" class="text-danger d-block">{{ item.error }}</small>
          <small v-if="isQueueItemExhausted(item)" class="text-muted d-block">
            Reintentos agotados: abrí el pedido desde la lista, corregilo y guardalo de nuevo.
          </small>
        </div>
        <b-button size="sm" variant="outline-danger" class="mobile-modal-icon-action" @click="removeQueueItem(item.localId)">
          <AppIcon name="trash" />
        </b-button>
      </div>
    </b-modal>

    <!-- ============================================ -->
    <!-- Modal de éxito -->
    <!-- ============================================ -->
    <b-modal v-model="modal.success"
             dialog-class="mobile-order-dialog"
             centered
             hide-header
             hide-footer
             no-close-on-backdrop
             no-close-on-esc>
      <div class="text-center py-4">
        <AppIcon :name="offline.queued ? 'cloud-upload' : 'check2-circle'"
                :class="offline.queued ? 'text-warning' : 'text-success'"
                fill
                font-scale="4" />
        <h4 class="mt-3">{{ successModalTitle }}</h4>
        <p class="text-muted">{{ successModalMessage }}</p>
        <b-button variant="primary" block size="lg" @click="createNewOrder()">
          Continuar
        </b-button>
      </div>
    </b-modal>

    <!-- ============================================ -->
    <!-- Modal de recuperación de borrador -->
    <!-- ============================================ -->
    <b-modal v-model="draft.showRecoveryModal"
             dialog-class="mobile-order-dialog"
             footer-class="mobile-draft-recovery-footer"
             centered
             hide-header-close
             no-close-on-backdrop
             no-close-on-esc>
      <template #modal-header>
        <div class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="exclamation-triangle-fill" /></span>
          <h5 class="app-modal-header__title">Trabajo sin confirmar</h5>
        </div>
      </template>
      <div v-if="!draft.discardConfirming" class="py-2">
        <p class="mb-3">Se encontró un estado de trabajo que todavía no fue confirmado.</p>
        <div v-if="draft.snapshots[0]" class="draft-recovery-summary">
          <div class="draft-recovery-summary__row draft-recovery-summary__row--customer">
            <AppIcon name="person-badge" />
            <strong>{{ getDraftCustomerName(draft.snapshots[0]) }}</strong>
          </div>
          <div v-if="getDraftPointSaleLabel(draft.snapshots[0])" class="draft-recovery-summary__row">
            <AppIcon name="shop" />
            <span>{{ getDraftPointSaleLabel(draft.snapshots[0]) }}</span>
          </div>
          <div v-if="getDraftMethodsPaymentLabel(draft.snapshots[0])" class="draft-recovery-summary__row">
            <AppIcon name="cash-stack" />
            <span>{{ getDraftMethodsPaymentLabel(draft.snapshots[0]) }}</span>
          </div>
          <div v-if="getDraftPriceListLabel(draft.snapshots[0])" class="draft-recovery-summary__row">
            <AppIcon name="list-ul" />
            <span>{{ getDraftPriceListLabel(draft.snapshots[0]) }}</span>
          </div>
        </div>
        <div v-if="draft.snapshots[0]" class="draft-recovery-products">
          <span class="draft-recovery-products__icon"><AppIcon name="box-seam" /></span>
          <div>
            <strong>{{ getDraftItemCount(draft.snapshots[0]) }}</strong>
            {{ getDraftItemCount(draft.snapshots[0]) === 1 ? 'producto cargado' : 'productos cargados' }}
          </div>
        </div>
        <p class="text-muted small mb-0 mt-3" v-if="draft.timestamp">
          Guardado: {{ formatDraftTimestamp(draft.timestamp) }}
        </p>
      </div>
      <div v-else class="py-2">
        <p class="font-weight-bold text-danger mb-2">¿Descartar definitivamente este trabajo?</p>
        <p class="mb-0">
          Se eliminará el borrador de <strong>{{ getDraftCustomerName(draft.snapshots[0]) }}</strong> y se abrirá un <strong>pedido nuevo vacío</strong>. Esta acción no se puede deshacer.
        </p>
      </div>
      <template #modal-footer>
        <template v-if="!draft.discardConfirming">
          <b-button variant="outline-danger" @click="requestDraftDiscard()">
            <AppIcon name="trash" class="mr-1" />
            Descartar y empezar nuevo
          </b-button>
          <b-button variant="primary" @click="recoverDraft()">
            <AppIcon name="arrow-counterclockwise" class="mr-1" />
            Recuperar
          </b-button>
        </template>
        <template v-else>
          <b-button variant="outline-secondary"
                    class="mobile-draft-discard-back-btn"
                    :disabled="draft.discarding"
                    @click="draft.discardConfirming = false">
            Volver
          </b-button>
          <b-button variant="danger"
                    :disabled="draft.discarding"
                    @click="discardDraftAndStartNew()">
            <b-spinner small v-if="draft.discarding" class="mr-1" />
            <span v-else><AppIcon name="trash" class="mr-1" /></span>
            Sí, descartar
          </b-button>
        </template>
      </template>
    </b-modal>

    <!-- Modal: detalle de conversión de precio -->
    <b-modal v-model="conversionModal.show" dialog-class="mobile-order-dialog" size="sm" hide-footer centered>
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="calculator" /></span>
        <h5 class="app-modal-header__title">Detalle de precio</h5>
        <button type="button" class="app-modal-header__close mobile-modal-close-btn" aria-label="Cerrar" @click="conversionModal.show = false">
          <AppIcon name="x" />
        </button>
      </div>
      <div v-if="conversionModal.product">
        <div class="mb-3">
          <div class="text-muted small mb-1">Precio por {{ conversionModal.purchaseUnit }}</div>
          <div class="font-weight-bold text-muted" style="font-size:1.1rem">
            <template v-if="getProductPriceWithIva(conversionModal.product, conversionModal.product.price_initial)">
              $ {{ formatPrice(getProductPriceWithIva(conversionModal.product, conversionModal.product.price_initial)) }} <small class="font-weight-normal">c/IVA</small>
              <div class="font-weight-normal" style="font-size:0.85rem">$ {{ formatPrice(conversionModal.product.price_initial) }} s/IVA</div>
            </template>
            <template v-else>
              $ {{ formatPrice(conversionModal.product.price_initial) }}
            </template>
          </div>
        </div>
        <div class="mb-3">
          <div class="text-muted small mb-1">Precio por {{ conversionModal.saleUnit }}</div>
          <div class="font-weight-bold text-primary" style="font-size:1.1rem">
            <template v-if="getProductPriceWithIva(conversionModal.product, conversionModal.product.price)">
              $ {{ formatPrice(getProductPriceWithIva(conversionModal.product, conversionModal.product.price)) }} <small class="font-weight-normal">c/IVA</small>
              <div class="text-muted font-weight-normal" style="font-size:0.85rem">$ {{ formatPrice(conversionModal.product.price) }} s/IVA</div>
            </template>
            <template v-else>
              $ {{ formatPrice(conversionModal.product.price) }}
            </template>
          </div>
        </div>
        <div class="border-top pt-2">
          <div class="text-muted small mb-1">Factor de conversión</div>
          <div class="font-weight-bold">
            1 {{ conversionModal.saleUnit }} = {{ conversionModal.product.unit_measurement_sale_conversion }} {{ conversionModal.purchaseUnit }}
          </div>
        </div>
      </div>
    </b-modal>

    <Printer v-if="print.id"
             reference="reports-payment"
             :registerID="print.id"
             :key="print.key"/>

  </div>
</template>

<script>
import serviceAPI from './services'
import Session from '@/handler/session'
import Profiles from '@/config/profiles'
import Modules from '@/config/modules'
import Helper from '@/handler/helper'
import Funciones from '@/handler/funciones'
import Error from '@/handler/error'
import Param from '@/config/parameters'
import moment from 'moment'
import LocationTracker from '@/handler/locationTracker'
import Printer from '@/components/inc/printer/printer'
import OfflineManager from './mobileOrderCreate/offlineManager'
import DraftStorage from './mobileOrderCreate/draftStorage'
import Storage from '@/handler/storageLocal'

const DRAFT_FALLBACK_STORAGE_PREFIX = 'mobile_order_draft_fallback'
const DRAFT_SAVE_DELAY = 1000 // Debounce de 1 segundo para guardar
const getMinProductSearchChars = (term) => /^\d+$/.test(term) ? 3 : 4
const PRODUCT_VIEW_STORAGE_KEY = 'mobile_order_products_view_mode'
const PRODUCT_VIEW_MODE_ACCORDION = 'accordion'
const PRODUCT_VIEW_MODE_LIST = 'list'
const PRODUCT_FILTERS_STORAGE_KEY = 'mobile_order_products_query_filters'
const REVIEW_VIEW_STORAGE_KEY = 'mobile_order_review_view_mode'
const PRODUCT_SEARCH_KEYBOARD_KEY = 'mobile_order_product_search_numeric_keyboard'
const CONNECTED_SYNC_INTERVAL_HOURS = 24
const LOCAL_FIRST_SYNC_INTERVAL_MS = 15 * 60 * 1000
const LOCAL_DATA_WARNING_AGE_HOURS = 24
const LOCAL_DATA_CRITICAL_AGE_HOURS = 48
const generateClientRequestId = () => `${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 9)}`

// Tope de reintentos automáticos de sincronización por pedido. Solo cuentan
// los rechazos reales del servidor (no los cortes de conexión). Superado el
// tope, el pedido queda en la lista para que el vendedor lo cargue, corrija
// y guarde de nuevo (eso lo re-encola desde cero).
const SYNC_MAX_ATTEMPTS = 5

// Chequeo de conectividad real (mismo patrón/valores que salesCounter.vue).
const CONNECTIVITY_CHECK_INTERVAL_MS = 5000
const API_CHECK_TIMEOUT_MS = 3500

// Paginado incremental de resultados de búsqueda de productos: una búsqueda
// amplia puede matchear cientos/miles de productos contra un catálogo
// grande, y renderizarlos todos de una causaba caídas de memoria (pantalla
// en blanco) en celulares gama media/baja al scrollear. Se renderiza de a
// tandas, revelando más a medida que el vendedor se acerca al final.
const PRODUCT_PAGE_SIZE = 40
const createEmptyOrderForm = () => ({
  id: 0,
  client_request_id: generateClientRequestId(),
  date: '',
  observations: '',
  sellers_id: 0,
  customers_id: 0,
  customers: null,
  selected_zone: null,
  price_list_id: 0,
  price_list: null,
  erp_points_sales_id: 0,
  erp_points_sales: null,
  methods_payment_id: 0,
  methods_payment: null,
  detail: [],
  is_retail: false,
  reason_id: 0,
  discounts_price: 0,
  percentage_discount: 0,
})

export default {
  components: {
    Printer,
  },
  data: () => {
    return {
      // Control de acceso
      access: {
        module_id: Modules.PEDIDOS,
        profile_id: Profiles.PERSONAL,
        view_reference: 'mobileOrderCreate',
        elements: {
          deleteOrders: true,
        }
      },

      // Navegación de pasos
      currentStep: 1,
      totalSteps: 3,
      customerDetailsExpanded: false,
      reviewProductsExpanded: true,

      // Parámetros del módulo
      parameters: {
        haveReason: Helper.hasParametersAccess(Param.P54),
        salePriceCalc: Helper.hasParametersAccess(Param.P75),
      },

      modulesActive: [],

      statsHoy: {
        cobranzas:    { count: 0, total: 0 },
        observaciones: { count: 0 },
      },

      // Formulario del pedido
      crud: {
        form: createEmptyOrderForm()
      },

      // Arrays de datos
      arr: {
        zones: [],
        customers: [],
        products: [],
        configurations: [],
        reasons: [],
        pointsSales: [],
        methodsPayments: [],
        priceLists: [],
        unitMeasurements: [],
        unitMeasurementsMap: {},
        ivaConditions: [],
      },

      customerGroupBy: 'city',
      expandedGroups: {},
      // Paginado incremental de resultados de productos. Clave = 'list' (vista
      // lista) o 'catKey-subKey' (vista agrupada, una por subcategoría).
      productPageSize: {},
      groupPageSize: {},
      // Eliminar exige una segunda pulsación breve. El estado se identifica
      // por producto e índice para reflejarlo simultáneamente en ambas vistas.
      pendingCartRemovalKey: null,

      // Búsqueda
      search: {
        customer: '',
        product: '',
      },

      // Estados de carga
      loading: {
        customers: false,
        products: false,
        saving: false,
        addingProductId: null,
        reasons: false,
        pointsSales: false,
        methodsPayments: false,
        priceLists: false,
      },

      // Modales
      modal: {
        success: false,
        editOrder: false,
        duplicateOrder: false,
        observations: false,
        cobranza: false,
        cobranzaSuccess: false,
        observacionesSuccess: false,
        queue: false,
      },

      print: {
        id: null,
        key: null,
      },

      observaciones: {
        loading: false,
        form: {
          date: '',
          observations: '',
        },
      },

      cobranza: {
        loading: false,
        form: {
          items: [],
          newItem: { type: 'Efectivo', amount: '', check: { type: 'de_terceros', bank: '', number: '', cuit: '', date_movement: '', observations: '' } },
          observations: '',
        },
        typeOptions: [
          { value: 'Efectivo',      text: 'Efectivo' },
          { value: 'Cheque',        text: 'Cheque' },
        ],
      },

      duplicateOrder: {
        loading: false,
        selecting: false,
        selectedId: 0,
        items: [],
        loadedDetails: {},  // cache orderId -> full orderData
      },

      conversionModal: {
        show: false,
        product: null,
        saleUnit: '',
        purchaseUnit: '',
      },

      // ID del pedido creado
      orderCreatedId: 0,
      lastSavedWasEdit: false,

      // Timer para debounce de búsqueda
      searchTimer: null,

      // Control de requests de productos
      productsRequestId: 0,
      localProductSearch: {
        query: '',
        nextOffset: 0,
        hasMore: false,
        loadingMore: false,
      },

      // Touch swipe
      touch: {
        startX: 0,
        startY: 0,
      },
      priceObserver: null,
      mobileClearedInput: null,

      // Sistema de borrador/respaldo
      draft: {
        timestamp: null,
        showRecoveryModal: false,
        snapshots: [],
        discarding: false,
        discardConfirming: false,
      },
      orderContext: {
        draftRecovered: false,
      },
      draftSaveTimer: null,
      customerChange: {
        active: false,
      },
      // isOnline refleja disponibilidad real del backend (no la fuente de
      // lectura elegida) y controla actualización y envío de la cola.
      isOnline: navigator.onLine,
      apiStatus: {
        checking: false,
        online: navigator.onLine,
        lastCheckedAt: null,
      },
      offline: {
        // Esta preferencia controla de dónde se leen los datos del pedido.
        // La conectividad real se mantiene separada: define cuándo refrescar
        // la caché y cuándo enviar pedidos pendientes.
        localFirst: false,
        syncCardCollapsed: true,
        presyncing: false,
        presyncStep: '',
        presyncProgress: 0,
        syncing: false,
        pendingCount: 0,
        queued: false,
        lastSyncTs: 0,
        lastSyncLabelTick: 0,
        connectivityConfirmed: false,
      },
      focusMode: false,
      numericKeyboard: false,
      mountedSubcategories: {},
      productViewMode: PRODUCT_VIEW_MODE_ACCORDION,
      reviewViewMode: PRODUCT_VIEW_MODE_ACCORDION,
      productSearchHasExecuted: false,
      productQueryFilters: {
        onlyWithPrice: false,
        onlyWithStock: false,
      },
      editOrder: {
        loading: false,
        selecting: false,
        activeOrderId: 0,
        hasOrders: false,
        selectedId: 0,
        editingQueueLocalId: null,
        search: '',
        items: [],
        pagination: {
          currentPage: 1,
          lastPage: 1,
          total: 0,
        },
      },
    }
  },

  computed: {
    haveReportsPayment() {
      var items = Session.getSession().auth.user.permissions.items
      return items.some(function(item) { return item.module_id == Modules.INFORME_COBRANZAS })
    },
    haveReportsNews() {
      return this.modulesActive.some(function(m) { return m.id == Modules.INFORME_NOVEDADES })
    },

    cobranzaTotalValue() {
      return this.cobranza.form.items
        .reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)
    },
    cobranzaTotal() {
      return this.cobranzaTotalValue.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    // Grupos por ciudad
    groupsByCity() {
      const map = {}
      for (const customer of this.arr.customers) {
        const name = (customer.data_fiscal && customer.data_fiscal.city) ? customer.data_fiscal.city : 'Sin ciudad'
        const key = name.replace(/\s+/g, '_')
        if (!map[key]) map[key] = { key, name, customers: [] }
        map[key].customers.push(customer)
      }
      return Object.values(map)
        .sort((a, b) => {
          if (a.name === 'Sin ciudad') return 1
          if (b.name === 'Sin ciudad') return -1
          return a.name.localeCompare(b.name)
        })
        .map(group => ({
          ...group,
          customers: [...group.customers].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        }))
    },

    // Grupos por zona
    groupsByZone() {
      const extractNumber = (name) => {
        const match = (name || '').match(/\d+/)
        return match ? parseInt(match[0], 10) : Infinity
      }
      return [...this.arr.zones]
        .sort((a, b) => {
          const nA = extractNumber(a.name)
          const nB = extractNumber(b.name)
          if (nA !== nB) return nA - nB
          return (a.name || '').localeCompare(b.name || '')
        })
        .map(zone => ({
          key: 'z_' + zone.id,
          name: zone.name,
          customers: (zone.customers || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')),
          zone,
        }))
    },

    // Clientes filtrados por búsqueda, pre-calculados una sola vez
    filteredCustomersByGroup() {
      const groups = this.customerGroupBy === 'city' ? this.groupsByCity : this.groupsByZone
      const result = {}
      const searchLower = this.search.customer ? this.search.customer.toLowerCase() : ''
      for (const group of groups) {
        if (!group.customers) { result[group.key] = []; continue }
        if (!searchLower) { result[group.key] = group.customers; continue }
        result[group.key] = group.customers.filter(c => {
          if (c.name && c.name.toLowerCase().includes(searchLower)) return true
          if (c.code && c.code.toLowerCase().includes(searchLower)) return true
          if (group.name && group.name.toLowerCase().includes(searchLower)) return true
          if (c.data_fiscal) {
            if (c.data_fiscal.documents_number && c.data_fiscal.documents_number.toLowerCase().includes(searchLower)) return true
            if (c.data_fiscal.city && c.data_fiscal.city.toLowerCase().includes(searchLower)) return true
            if (c.data_fiscal.state && c.data_fiscal.state.toLowerCase().includes(searchLower)) return true
            if (c.data_fiscal.address && c.data_fiscal.address.toLowerCase().includes(searchLower)) return true
          }
          return false
        })
      }
      return result
    },

    // Grupos activos según selector, filtrados por búsqueda
    activeGroups() {
      const groups = this.customerGroupBy === 'city' ? this.groupsByCity : this.groupsByZone
      if (!this.search.customer) return groups
      return groups.filter(g => (this.filteredCustomersByGroup[g.key] || []).length > 0)
    },

    // Verificar si hay clientes filtrados
    hasFilteredCustomers() {
      return this.activeGroups.some(g => (this.filteredCustomersByGroup[g.key] || []).length > 0)
    },

    // Clientes filtrados por búsqueda (mantener para compatibilidad)
    filteredCustomers() {
      if (!this.search.customer) {
        return this.arr.customers
      }
      const searchLower = this.search.customer.toLowerCase()
      return this.arr.customers.filter(c => {
        if (c.name && c.name.toLowerCase().includes(searchLower)) return true
        if (c.code && c.code.toLowerCase().includes(searchLower)) return true
        if (c.data_fiscal) {
          if (c.data_fiscal.documents_number && c.data_fiscal.documents_number.toLowerCase().includes(searchLower)) return true
          if (c.data_fiscal.city && c.data_fiscal.city.toLowerCase().includes(searchLower)) return true
          if (c.data_fiscal.state && c.data_fiscal.state.toLowerCase().includes(searchLower)) return true
        }
        return false
      })
    },

    // Productos filtrados por búsqueda
    filteredProducts() {
      if (this.useLocalData) {
        return this.arr.products
      }
      if (!this.trimmedProductSearch) {
        return [...this.arr.products].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      }
      const searchExact = this.trimmedProductSearch.toLowerCase()
      const words = searchExact.split(' ').filter(w => w)
      return this.arr.products
        .filter(p => {
          const name = p.name.toLowerCase()
          const code = (p.code || '').toLowerCase()
          return words.every(w => name.includes(w) || code.includes(w))
        })
        .sort((a, b) => {
          const aExact = (a.code || '').toLowerCase() === searchExact ? -1 : 0
          const bExact = (b.code || '').toLowerCase() === searchExact ? -1 : 0
          if (aExact !== bExact) return aExact - bExact
          return (a.name || '').localeCompare(b.name || '')
        })
    },

    trimmedProductSearch() {
      return (this.search.product || '').trim()
    },

    // La fuente de lectura depende exclusivamente de la elección del usuario.
    // La conectividad se utiliza por separado para sincronizar y enviar.
    useLocalData() {
      return this.offline.localFirst
    },

    minProductSearchChars() {
      return getMinProductSearchChars(this.trimmedProductSearch)
    },

    allowExtraDiscount() {
      return this.getOrdersConfigBoolean(27, false)
    },

    extraDiscountPercentage() {
      return this.getOrdersConfigNumber(28, 100)
    },

    allowSaveWithoutConfirmation() {
      return this.getOrdersConfigBoolean(29, false)
    },


    addedProductIdSet() {
      const ids = new Set()
      this.crud.form.detail.forEach(item => {
        if (item && item.product && item.product.source === 'product' && item.product.id) {
          ids.add(item.product.id)
        }
      })
      return ids
    },

    groupedProducts() {
      const list = this.filteredProducts || []
      if (!list.length) {
        return []
      }

      const normalizeKey = (value) => {
        const raw = String(value || 'none').toLowerCase()
        const cleaned = raw.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
        return cleaned || 'none'
      }

      const categories = new Map()

      list.forEach((product) => {
        const category = product.subcategory && product.subcategory.category ? product.subcategory.category : null
        const subcategory = product.subcategory || null

        const categoryName = category && category.name ? category.name : 'Sin categoría'
        const categoryKey = `cat-${normalizeKey(category && category.id ? category.id : categoryName)}`

        let categoryGroup = categories.get(categoryKey)
        if (!categoryGroup) {
          categoryGroup = {
            key: categoryKey,
            name: categoryName,
            subcategories: new Map()
          }
          categories.set(categoryKey, categoryGroup)
        }

        const subcategoryName = subcategory && subcategory.name ? subcategory.name : 'Sin subcategoría'
        const subcategoryKey = `sub-${normalizeKey(subcategory && subcategory.id ? subcategory.id : subcategoryName)}`

        let subcategoryGroup = categoryGroup.subcategories.get(subcategoryKey)
        if (!subcategoryGroup) {
          subcategoryGroup = {
            key: subcategoryKey,
            name: subcategoryName,
            products: []
          }
          categoryGroup.subcategories.set(subcategoryKey, subcategoryGroup)
        }

        subcategoryGroup.products.push(product)
      })

      return Array.from(categories.values())
        .sort((a, b) => {
          if (a.name === 'Sin categoría') return 1
          if (b.name === 'Sin categoría') return -1
          return a.name.localeCompare(b.name)
        })
        .map(categoryGroup => ({
          key: categoryGroup.key,
          name: categoryGroup.name,
          subcategories: Array.from(categoryGroup.subcategories.values())
            .sort((a, b) => {
              if (a.name === 'Sin subcategoría') return 1
              if (b.name === 'Sin subcategoría') return -1
              return a.name.localeCompare(b.name)
            })
            .map(sub => ({
              ...sub,
              products: [...sub.products].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            }))
        }))
    },

    groupedCartItems() {
      const list = this.crud.form.detail || []
      if (!list.length) {
        return []
      }

      const normalizeKey = (value) => {
        const raw = String(value || 'none').toLowerCase()
        const cleaned = raw.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
        return cleaned || 'none'
      }

      const categories = new Map()

      list.forEach((detailItem, index) => {
        const product = detailItem && detailItem.product ? detailItem.product : null
        const category = product && product.subcategory && product.subcategory.category
          ? product.subcategory.category
          : null
        const subcategory = product && product.subcategory ? product.subcategory : null

        const categoryName = category && category.name ? category.name : 'Sin categoría'
        const categoryKey = `cart-cat-${normalizeKey(category && category.id ? category.id : categoryName)}`

        let categoryGroup = categories.get(categoryKey)
        if (!categoryGroup) {
          categoryGroup = {
            key: categoryKey,
            name: categoryName,
            subcategories: new Map()
          }
          categories.set(categoryKey, categoryGroup)
        }

        const subcategoryName = subcategory && subcategory.name ? subcategory.name : 'Sin subcategoría'
        const subcategoryKey = `cart-sub-${normalizeKey(subcategory && subcategory.id ? subcategory.id : subcategoryName)}`

        let subcategoryGroup = categoryGroup.subcategories.get(subcategoryKey)
        if (!subcategoryGroup) {
          subcategoryGroup = {
            key: subcategoryKey,
            name: subcategoryName,
            items: []
          }
          categoryGroup.subcategories.set(subcategoryKey, subcategoryGroup)
        }

        subcategoryGroup.items.push({
          key: `${product && product.id ? product.id : 'item'}-${index}`,
          item: detailItem,
          index: index
        })
      })

      return Array.from(categories.values()).map(categoryGroup => ({
        key: categoryGroup.key,
        name: categoryGroup.name,
        subcategories: Array.from(categoryGroup.subcategories.values())
      }))
    },

    // Subtotal del pedido
    getSubTotalOrder() {
      let total = 0
      this.crud.form.detail.forEach(item => {
        total += item.quantity * item.amount
      })
      return total
    },

    // Total del pedido con descuento general
    getTotalOrder() {
      const subtotal = this.getSubTotalOrder
      const discount = parseFloat(this.crud.form.discounts_price || 0)
      return subtotal - discount
    },

    getSubTotalOrderWithIva() {
      let total = 0
      this.crud.form.detail.forEach(item => {
        const priceWithIva = this.getProductPriceWithIva(item.product, item.amount)
        const lineTotal = item.quantity * (priceWithIva !== null ? priceWithIva : item.amount)
        total += this.parameters.salePriceCalc ? Math.round(lineTotal * 100) / 100 : lineTotal
      })
      return total
    },

    getTotalOrderWithIva() {
      const subtotalNet = this.getSubTotalOrder
      const subtotalIva = this.getSubTotalOrderWithIva
      if (subtotalNet === 0) return subtotalIva
      const ratio = (subtotalNet - parseFloat(this.crud.form.discounts_price || 0)) / subtotalNet
      return subtotalIva * ratio
    },

    // Obtener vendedor actual
    getSeller() {
      return Helper.getSeller()
    },

    // Los borradores creados por versiones anteriores pueden no incluir la
    // zona. Si las zonas ya están cargadas, se resuelve nuevamente desde el
    // cliente para no ocultar ese dato en la revisión.
    selectedCustomerZone() {
      if (this.crud.form.selected_zone) {
        return this.crud.form.selected_zone
      }
      return this.findZoneByCustomerId(this.crud.form.customers_id)
    },

    pointsSalesOptions() {
      const options = [{ value: 0, text: 'Seleccionar punto de venta...' }]
      this.arr.pointsSales.forEach(point => {
        if (!this.customerHasValidDocument && !point.nd) return
        const label = point.full_identification
          ? point.full_identification
          : `${point.point_sale ? point.point_sale.toString().padStart(4, '0') : ''} - ${point.name || ''}`.trim()
        options.push({ value: point.id, text: label })
      })
      return options
    },

    priceListOptions() {
      const options = [{ value: 0, text: 'Seleccionar lista de precios...' }]
      this.arr.priceLists.forEach(list => {
        const reference = list.reference ? `${list.reference} - ` : ''
        const name = list.name || ''
        options.push({ value: list.id, text: `${reference}${name}`.trim() })
      })
      return options
    },

    methodsPaymentOptions() {
      const options = [{ value: 0, text: 'Seleccionar medio de pago...' }]
      this.arr.methodsPayments.forEach(method => {
        const reference = method.reference ? `${method.reference} - ` : ''
        const name = method.name || ''
        options.push({ value: method.id, text: `${reference}${name}`.trim() })
      })
      return options
    },

    selectedPointSale() {
      if (!this.crud.form.erp_points_sales_id) {
        return null
      }
      const fromList = this.arr.pointsSales.find(point => point.id === this.crud.form.erp_points_sales_id)
      if (fromList) {
        return fromList
      }
      const dataFiscal = this.crud.form.customers ? this.crud.form.customers.data_fiscal : null
      if (dataFiscal && dataFiscal.points_sales && dataFiscal.points_sales.id === this.crud.form.erp_points_sales_id) {
        return dataFiscal.points_sales
      }
      return null
    },

    selectedPointSaleLabel() {
      if (!this.selectedPointSale) {
        return ''
      }
      if (this.selectedPointSale.full_identification) {
        return this.selectedPointSale.full_identification
      }
      const code = this.selectedPointSale.point_sale
        ? this.selectedPointSale.point_sale.toString().padStart(4, '0')
        : ''
      const name = this.selectedPointSale.name || ''
      return `${code} - ${name}`.trim()
    },

    selectedPriceListLabel() {
      if (this.crud.form.price_list) {
        const reference = this.crud.form.price_list.reference ? `${this.crud.form.price_list.reference} - ` : ''
        const name = this.crud.form.price_list.name || ''
        return `${reference}${name}`.trim()
      }
      if (!this.crud.form.price_list_id) {
        return ''
      }
      const list = this.arr.priceLists.find(item => item.id === this.crud.form.price_list_id)
      if (!list) return ''
      const reference = list.reference ? `${list.reference} - ` : ''
      const name = list.name || ''
      return `${reference}${name}`.trim()
    },

    selectedMethodsPayment() {
      if (!this.crud.form.methods_payment_id) {
        return null
      }
      const fromList = this.arr.methodsPayments.find(method => method.id === this.crud.form.methods_payment_id)
      if (fromList) {
        return fromList
      }
      if (this.crud.form.methods_payment && this.crud.form.methods_payment.id === this.crud.form.methods_payment_id) {
        return this.crud.form.methods_payment
      }
      const dataFiscal = this.crud.form.customers ? this.crud.form.customers.data_fiscal : null
      if (dataFiscal && dataFiscal.methods_payment && dataFiscal.methods_payment.id === this.crud.form.methods_payment_id) {
        return dataFiscal.methods_payment
      }
      return null
    },

    selectedMethodsPaymentLabel() {
      if (!this.selectedMethodsPayment) {
        return ''
      }
      const reference = this.selectedMethodsPayment.reference ? `${this.selectedMethodsPayment.reference} - ` : ''
      const name = this.selectedMethodsPayment.name || ''
      return `${reference}${name}`.trim()
    },

    customerHasValidDocument() {
      const dataFiscal = this.crud.form.customers ? this.crud.form.customers.data_fiscal : null
      return this.isValidDocumentNumber(dataFiscal ? dataFiscal.documents_number : null)
    },

    customerDocumentLabel() {
      const dataFiscal = this.crud.form.customers ? this.crud.form.customers.data_fiscal : null
      if (!dataFiscal || !this.isValidDocumentNumber(dataFiscal.documents_number)) {
        return ''
      }
      const docType = dataFiscal.document_type || dataFiscal.types_documents
      const docTypeName = docType ? docType.name : ''
      if (docTypeName) {
        return `${docTypeName}: ${dataFiscal.documents_number}`
      }
      return `${dataFiscal.documents_number}`
    },

    customerAddressLabel() {
      const dataFiscal = this.crud.form.customers ? this.crud.form.customers.data_fiscal : null
      if (!dataFiscal) return ''
      const parts = []
      if (dataFiscal.address) parts.push(dataFiscal.address)
      const locationParts = []
      if (dataFiscal.postal_code) locationParts.push(`CP ${dataFiscal.postal_code}`)
      if (dataFiscal.city) locationParts.push(dataFiscal.city)
      if (dataFiscal.state) locationParts.push(dataFiscal.state)
      if (locationParts.length) parts.push(locationParts.join(' - '))
      return parts.join(', ')
    },

    customerPhoneLabel() {
      const customer = this.crud.form.customers
      if (customer && customer.phone) {
        return customer.phone
      }
      return ''
    },

    reasonOptions() {
      const options = [{ value: 0, text: 'Seleccionar motivo...' }]
      this.arr.reasons.forEach(reason => {
        const label = reason.reference ? `${reason.reference} - ${reason.name}` : reason.name
        options.push({ value: reason.id, text: label })
      })
      return options
    },

    canContinueStep1() {
      return !!(this.crud.form.customers_id &&
        this.hasSelectedAvailableOption(this.pointsSalesOptions, this.crud.form.erp_points_sales_id) &&
        this.hasSelectedAvailableOption(this.methodsPaymentOptions, this.crud.form.methods_payment_id) &&
        this.hasSelectedAvailableOption(this.priceListOptions, this.crud.form.price_list_id))
    },

    isEditingOrder() {
      return !!this.editOrder.editingQueueLocalId || this.getActiveEditOrderId > 0
    },

    getActiveEditOrderId() {
      return this.normalizeOrderId(this.editOrder.activeOrderId || this.crud.form.id)
    },

    headerTitle() {
      if (this.editOrder.editingQueueLocalId) {
        return 'Editar Pedido Pendiente'
      }
      if (this.isEditingOrder) {
        return `Editar Pedido #${this.getActiveEditOrderId}`
      }
      return 'Nuevo Pedido'
    },

    saveButtonLabel() {
      return this.isEditingOrder ? 'Guardar Cambios' : 'Confirmar Pedido'
    },

    successModalTitle() {
      if (this.offline.queued) return 'Pedido guardado localmente'
      return this.lastSavedWasEdit ? 'Pedido Actualizado' : 'Pedido Creado'
    },

    successModalMessage() {
      if (this.offline.queued) {
        return this.isOnline
          ? 'El pedido se guardó y se está enviando al servidor.'
          : 'Sin conexión. El pedido se enviará automáticamente cuando vuelva internet.'
      }
      if (!this.orderCreatedId) {
        return this.lastSavedWasEdit
          ? 'El pedido fue actualizado correctamente'
          : 'El pedido fue creado correctamente'
      }
      return this.lastSavedWasEdit
        ? `El pedido #${this.orderCreatedId} fue actualizado correctamente`
        : `El pedido #${this.orderCreatedId} fue creado correctamente`
    },

    offlineLastSync() {
      if (!this.getSeller) return null
      void this.offline.lastSyncTs // dependencia reactiva
      void this.offline.lastSyncLabelTick
      const timestamp = OfflineManager.getLastSync(this.getSeller.id)
      return timestamp ? this.formatRelativeSyncTime(timestamp) : null
    },

    offlineLastSyncExact() {
      if (!this.getSeller) return null
      void this.offline.lastSyncTs
      void this.offline.lastSyncLabelTick
      return OfflineManager.formatLastSync(this.getSeller.id)
    },

    // Aviso no bloqueante: el vendedor puede continuar sin red, pero conoce
    // la antigüedad de los precios, stock y referencias que está utilizando.
    localDataFreshness() {
      if (!this.offline.localFirst || !this.getSeller) return null
      void this.offline.lastSyncTs
      void this.offline.lastSyncLabelTick

      const timestamp = OfflineManager.getLastSync(this.getSeller.id)
      if (!timestamp) {
        return {
          variant: 'danger',
          title: 'No hay una sincronización local registrada',
          message: 'Los datos del teléfono pueden estar incompletos. Actualizalos apenas se recupere la conexión.',
        }
      }

      const ageHours = (Date.now() - timestamp) / (60 * 60 * 1000)
      if (ageHours >= LOCAL_DATA_CRITICAL_AGE_HOURS) {
        return {
          variant: 'danger',
          title: 'Datos descargados con más de 48 horas',
          message: `Última sincronización ${this.formatRelativeSyncTime(timestamp)}. Precios y stock pueden haber cambiado.`,
        }
      }
      if (ageHours >= LOCAL_DATA_WARNING_AGE_HOURS) {
        return {
          variant: 'warning',
          title: 'Datos descargados desactualizados',
          message: `Última sincronización ${this.formatRelativeSyncTime(timestamp)}. Actualizalos cuando haya conexión.`,
        }
      }
      return null
    },

    offlineQueue() {
      // eslint-disable-next-line no-unused-expressions
      this.offline.pendingCount // trigger reactivo para que Vue recalcule cuando cambia el contador
      return OfflineManager.getQueue().filter(i => i.status === 'pending' || i.status === 'error')
    },

    filteredEditOrders() {
      if (!this.editOrder.search) {
        return this.editOrder.items
      }
      const search = this.editOrder.search.toLowerCase().trim()
      if (!search) {
        return this.editOrder.items
      }
      return this.editOrder.items.filter(item => {
        const number = String(item.id || '')
        const customerName = this.getOrderCustomerName(item).toLowerCase()
        return number.includes(search) || customerName.includes(search)
      })
    },

    editOrdersTableFields() {
      return [
        { key: 'order', label: 'ID', class: 'align-middle text-nowrap' },
        { key: 'info', label: 'Fecha / Cliente', class: 'align-middle' },
        { key: 'total', label: 'Monto', class: 'align-middle text-right' },
        { key: 'actions', label: '', class: 'align-middle text-center' },
      ]
    },

    hasPreviousEditOrdersPage() {
      return this.editOrder.pagination.currentPage > 1
    },

    hasNextEditOrdersPage() {
      return this.editOrder.pagination.currentPage < this.editOrder.pagination.lastPage
    },

    isAccordionView() {
      return this.productViewMode === PRODUCT_VIEW_MODE_ACCORDION
    },

    isListView() {
      return this.productViewMode === PRODUCT_VIEW_MODE_LIST
    },

    showEditOrderCard() {
      return !this.isEditingOrder && !this.crud.form.customers_id && this.editOrder.hasOrders
    },

    isReviewAccordionView() {
      return this.reviewViewMode === PRODUCT_VIEW_MODE_ACCORDION
    },

    isReviewListView() {
      return this.reviewViewMode === PRODUCT_VIEW_MODE_LIST
    }
  },

  watch: {
    customerGroupBy() {
      this.initExpandedGroups()
    },
    activeGroups(newVal) {
      if (newVal.length && !Object.keys(this.expandedGroups).length) {
        this.initExpandedGroups()
      }
    },
    filteredProducts(newVal) {
      // Nueva búsqueda/resultado: reiniciar el paginado incremental de la
      // vista lista (las vistas por subcategoría se reinician solas, cada
      // una arranca en PRODUCT_PAGE_SIZE la primera vez que se expande).
      this.$set(this.productPageSize, 'list', PRODUCT_PAGE_SIZE)
      if (newVal && newVal.length) {
        this.$nextTick(() => {
          this.observePriceCards()
          this.observeProductSentinel(this.useLocalData ? 'local-results' : 'list')
        })
      }
    },
    'crud.form': {
      handler() {
        this.saveDraftDebounced()
      },
      deep: true
    },
    'search.product'(newValue, oldValue) {
      const normalizedNew = (newValue || '').trim()
      const normalizedOld = (oldValue || '').trim()
      if (normalizedNew !== normalizedOld) {
        this.productSearchHasExecuted = false
      }
      // En modo local una búsqueda sólo se ejecuta con Enter/botón. Al
      // cambiar el texto se invalidan resultados y lecturas anteriores.
      if (this.useLocalData) {
        if (normalizedNew !== this.localProductSearch.query) {
          this.productsRequestId = Date.now()
          this.arr.products = []
          this.loading.products = false
          this.localProductSearch = { query: '', nextOffset: 0, hasMore: false, loadingMore: false }
        }
      }
    },
    currentStep() {
      this.saveDraftDebounced()
    }
  },

  created() {
    Helper.hasAccessView(this.access)
    this.access.elements.deleteOrders = Helper.hasAccessElement(this.access, 'delete_orders')
  },

  mounted() {
    this.modulesActive = Session.getSession().auth.user.permissions.modules
    this.loadStatsHoy()

    // Verificar que sea vendedor
    if (!this.getSeller) {
      this.$router.push({ name: 'OrdersStaffDashboard' })
      return
    }

    this.loadLocalFirstMode()
    // Sin backend disponible no existe un modo conectado utilizable. Se
    // fuerza la fuente local antes de hidratar la pantalla inicial.
    if (!this.isOnline) this.forceLocalModeForNoConnection({ reload: false, notify: false })
    this.loadProductViewMode()
    this.loadReviewViewMode()
    this.loadProductQueryFilters()
    this.loadNumericKeyboard()
    this.requestPersistentDraftStorage()
    this.loadEditOrders(1, true)
    this.disableFocusMode()
    this.loadConfigurations()
    this.loadCustomers()
    this.loadPointsSales()
    this.loadMethodsPayments()
    this.loadPriceLists()
    this.loadUnitMeasurements()
    this.loadIvaConditions()
    if (this.parameters.haveReason) {
      this.loadReasons()
    }

    // Offline: listeners de red, contador de pendientes y auto-sync inicial
    window.addEventListener('online', this.handleOnline)
    window.addEventListener('offline', this.handleOffline)
    window.addEventListener('sw-activated', this.onSwActivated)
    document.addEventListener('visibilitychange', this.handleDraftVisibilityChange)
    window.addEventListener('pagehide', this.flushDraftOnExit)

    // Recarga por deploy diferida: mientras haya un pedido a medio cargar,
    // main.js no recarga la app y avisa con 'app-update-deferred'.
    window.__appBusy = this.isAppBusyForUpdate
    window.addEventListener('app-update-deferred', this.onAppUpdateDeferred)

    OfflineManager.restoreQueueFromBackup().then(() => {
      this.offline.pendingCount = OfflineManager.pendingCount()
      if (this.isOnline && this.offline.pendingCount > 0) {
        this.processSyncQueue()
      }
      this.loadEditOrders(1, true)
    })
    // Reintento periódico de la cola: cubre al vendedor con conexión mala o
    // intermitente que se queda en la pantalla sin salir ni volver a entrar
    // (los eventos 'online'/mount/activated no alcanzan a cubrir ese caso).
    this._syncSweepInterval = setInterval(() => {
      if (this.isOnline && !this.offline.syncing) {
        this.processSyncQueue()
      }
    }, 45000)
    this.startLocalFirstSyncTimer()
    this.startLastSyncLabelTimer()
    // Cargar/migrar los snapshots luego de contar con el vendedor actual.
    this.$nextTick(() => {
      this.loadDraftSnapshots()
    })
  },

  activated() {
    window.__appBusy = this.isAppBusyForUpdate
    // Cada apertura/reactivación confirma de nuevo el backend y evalúa si la
    // fotografía local ya venció según el modo de uso actual.
    this.offline.connectivityConfirmed = false
    this.startConnectivityWatch()
    this.startLocalFirstSyncTimer()
    this.startLastSyncLabelTimer()
    this.loadEditOrders(1, true)
    OfflineManager.restoreQueueFromBackup().then(() => {
      const pending = OfflineManager.pendingCount()
      if (this.isOnline && pending > 0) {
        this.offline.pendingCount = pending
        this.processSyncQueue()
      }
    })
  },

  deactivated() {
    // Al salir de la pantalla, el borrador ya protege el estado: no seguir
    // bloqueando la actualización de la app desde otra pantalla.
    if (window.__appBusy === this.isAppBusyForUpdate) {
      window.__appBusy = null
    }
    // No tiene sentido pingear la API cada 5s con la pantalla en segundo plano.
    this.stopConnectivityWatch()
    this.stopLocalFirstSyncTimer()
    this.stopLastSyncLabelTimer()
    this.exitBrowserFullscreen()
    this.disableFocusMode()
  },

  beforeDestroy() {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect()
      this.scrollObserver = null
    }
    if (this.productScrollObserver) {
      this.productScrollObserver.disconnect()
      this.productScrollObserver = null
    }
    this.stopConnectivityWatch()
    this.stopLocalFirstSyncTimer()
    this.stopLastSyncLabelTimer()
    window.removeEventListener('online', this.handleOnline)
    window.removeEventListener('offline', this.handleOffline)
    window.removeEventListener('sw-activated', this.onSwActivated)
    document.removeEventListener('visibilitychange', this.handleDraftVisibilityChange)
    window.removeEventListener('pagehide', this.flushDraftOnExit)
    window.removeEventListener('app-update-deferred', this.onAppUpdateDeferred)
    if (window.__appBusy === this.isAppBusyForUpdate) {
      window.__appBusy = null
    }
    if (this._syncSweepInterval) {
      clearInterval(this._syncSweepInterval)
      this._syncSweepInterval = null
    }
    if (this.searchTimer) {
      clearTimeout(this.searchTimer)
      this.searchTimer = null
    }
    if (this.draftSaveTimer) {
      clearTimeout(this.draftSaveTimer)
      this.draftSaveTimer = null
    }
    if (this._cartRemovalTimer) {
      clearTimeout(this._cartRemovalTimer)
      this._cartRemovalTimer = null
    }
    this.saveDraftNow('destroy')
    if (this.priceObserver) {
      this.priceObserver.disconnect()
      this.priceObserver = null
    }
    this.exitBrowserFullscreen()
    this.disableFocusMode()
  },

  methods: {
    moment,

    // Valida que el número de documento sea un CUIT válido:
    // 11 dígitos sin guiones o con guiones
    isValidDocumentNumber(doc) {
      if (!doc) return false
      const stripped = String(doc).replace(/-/g, '').trim()
      return /^\d{11}$/.test(stripped)
    },

    avatarFromName(name) {
      return Funciones.avatarFromName(name)
    },

    normalizeProductViewMode(mode) {
      const value = String(mode || '').toLowerCase()
      if (value === PRODUCT_VIEW_MODE_LIST) {
        return PRODUCT_VIEW_MODE_LIST
      }
      return PRODUCT_VIEW_MODE_ACCORDION
    },

    loadProductViewMode() {
      if (typeof window === 'undefined' || !window.localStorage) {
        this.productViewMode = PRODUCT_VIEW_MODE_ACCORDION
        return
      }
      try {
        const savedMode = localStorage.getItem(PRODUCT_VIEW_STORAGE_KEY)
        this.productViewMode = this.normalizeProductViewMode(savedMode)
      } catch (error) {
        this.productViewMode = PRODUCT_VIEW_MODE_ACCORDION
      }
    },

    setProductViewMode(mode) {
      const normalizedMode = this.normalizeProductViewMode(mode)
      this.productViewMode = normalizedMode

      try {
        localStorage.setItem(PRODUCT_VIEW_STORAGE_KEY, normalizedMode)
      } catch (error) {}

      this.observePriceCards()
    },

    loadReviewViewMode() {
      if (typeof window === 'undefined' || !window.localStorage) {
        this.reviewViewMode = PRODUCT_VIEW_MODE_ACCORDION
        return
      }
      try {
        const savedMode = localStorage.getItem(REVIEW_VIEW_STORAGE_KEY)
        this.reviewViewMode = this.normalizeProductViewMode(savedMode)
      } catch (error) {
        this.reviewViewMode = PRODUCT_VIEW_MODE_ACCORDION
      }
    },

    setReviewViewMode(mode) {
      const normalizedMode = this.normalizeProductViewMode(mode)
      this.reviewViewMode = normalizedMode

      try {
        localStorage.setItem(REVIEW_VIEW_STORAGE_KEY, normalizedMode)
      } catch (error) {}
    },

    loadLocalFirstMode() {
      if (!this.getSeller) return
      this.offline.localFirst = OfflineManager.getLocalFirstMode(this.getSeller.id)
    },

    onLocalFirstModeChange(enabled) {
      const seller = this.getSeller
      if (!seller) return

      // Sin backend, volver al modo conectado dejaría la pantalla en un
      // estado que no puede consultar ni precios ni referencias actuales.
      if (!this.isOnline && !enabled) {
        this.offline.localFirst = true
        return
      }
      this.offline.localFirst = !!enabled
      if (!OfflineManager.setLocalFirstMode(seller.id, this.offline.localFirst)) {
        this.$awn.warning('No se pudo guardar la preferencia de uso local')
      }

      // Cambiar la fuente de lectura no debe resetear el pedido en curso.
      // Sólo vuelve a hidratar los selectores y el catálogo desde la fuente
      // elegida; el formulario, sus ítems y su borrador permanecen intactos.
      this.reloadOrderDataFromCurrentSource()
      this.startLocalFirstSyncTimer()
    },

    setReadingMode(localFirst) {
      if (!this.isOnline || this.offline.localFirst === localFirst) return
      this.onLocalFirstModeChange(localFirst)
    },

    forceLocalModeForNoConnection({ reload = true, notify = true } = {}) {
      if (this.offline.localFirst) return

      this.offline.localFirst = true
      const seller = this.getSeller
      if (seller && seller.id) {
        OfflineManager.setLocalFirstMode(seller.id, true)
      }

      if (reload) {
        // El pedido que ya está en curso no se toca; únicamente se cambia la
        // fuente de catálogos, clientes y precios para que siga operable.
        this.reloadOrderDataFromCurrentSource()
        this.startLocalFirstSyncTimer()
      }

      if (notify) {
        this.$bvToast.toast('Se usarán los datos descargados hasta recuperar conexión con el servidor.', {
          title: 'Sin conexión',
          variant: 'warning',
          solid: true,
          autoHideDelay: 4500,
        })
      }
    },

    reloadOrderDataFromCurrentSource() {
      this.loadConfigurations()
      this.loadCustomers()
      this.loadPointsSales()
      this.loadMethodsPayments()
      this.loadPriceLists()
      this.loadUnitMeasurements()
      this.loadIvaConditions()
      if (this.parameters.haveReason) this.loadReasons()

      if (this.currentStep > 1) {
        // Una búsqueda pertenece a la fuente que la originó. Al cambiar de
        // modo no se reutiliza ni se consulta filter='' (catálogo completo):
        // se limpia y la siguiente búsqueda usa API o IndexedDB según toque.
        this.resetProductSearchForCurrentSource()
      }
    },

    resetProductSearchForCurrentSource() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
        this.searchTimer = null
      }
      this.productsRequestId = 0
      this.search.product = ''
      this.arr.products = []
      this.loading.products = false
      this.productSearchHasExecuted = false
      this.mountedSubcategories = {}
      this.localProductSearch = { query: '', nextOffset: 0, hasMore: false, loadingMore: false }
      if (this.priceObserver) {
        this.priceObserver.disconnect()
      }
    },

    normalizeStoredBoolean(value) {
      return value === true || value === 1 || value === '1' || value === 'true'
    },

    normalizeProductQueryFilters(raw) {
      const defaults = {
        onlyWithPrice: false,
        onlyWithStock: false,
      }
      if (!raw || typeof raw !== 'object') {
        return defaults
      }
      return {
        onlyWithPrice: this.normalizeStoredBoolean(raw.onlyWithPrice),
        onlyWithStock: this.normalizeStoredBoolean(raw.onlyWithStock),
      }
    },

    loadNumericKeyboard() {
      try {
        const saved = localStorage.getItem(PRODUCT_SEARCH_KEYBOARD_KEY)
        this.numericKeyboard = saved === 'true'
      } catch (error) {
        this.numericKeyboard = false
      }
    },

    saveNumericKeyboard() {
      try {
        localStorage.setItem(PRODUCT_SEARCH_KEYBOARD_KEY, String(this.numericKeyboard))
      } catch (error) {}
    },

    loadProductQueryFilters() {
      if (typeof window === 'undefined' || !window.localStorage) {
        this.productQueryFilters = this.normalizeProductQueryFilters(null)
        return
      }
      try {
        const raw = localStorage.getItem(PRODUCT_FILTERS_STORAGE_KEY)
        const parsed = raw ? JSON.parse(raw) : null
        this.productQueryFilters = this.normalizeProductQueryFilters(parsed)
      } catch (error) {
        this.productQueryFilters = this.normalizeProductQueryFilters(null)
      }
    },

    saveProductQueryFilters() {
      if (typeof window === 'undefined' || !window.localStorage) {
        return
      }
      try {
        localStorage.setItem(PRODUCT_FILTERS_STORAGE_KEY, JSON.stringify({
          onlyWithPrice: !!this.productQueryFilters.onlyWithPrice,
          onlyWithStock: !!this.productQueryFilters.onlyWithStock,
        }))
      } catch (error) {}
    },

    onProductQueryFiltersChange() {
      this.saveProductQueryFilters()
      this.forceSearchProducts()
    },

    onProductSearchFocus() {
      if (!this.trimmedProductSearch) {
        return
      }
      this.search.product = ''
      this.productSearchHasExecuted = false
      this.forceSearchProducts()
    },

    // ==========================================
    // Edición de pedidos cerrados
    // ==========================================
    openEditOrderModal() {
      this.modal.editOrder = true
    },

    openQueueModal() {
      this.modal.queue = true
    },

    removeQueueItem(localId) {
      OfflineManager.removeFromQueue(localId)
      this.offline.pendingCount = OfflineManager.pendingCount()
      if (this.offline.pendingCount === 0) {
        this.modal.queue = false
      }
    },

    formatQueueDate(isoStr) {
      if (!isoStr) return ''
      const d = new Date(isoStr)
      return d.toLocaleDateString('es-AR') + ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
    },

    onEditOrderModalShow() {
      this.editOrder.search = ''
      this.editOrder.selectedId = 0
      this.loadEditOrders(1)
    },

    buildEditOrdersFilters() {
      return {
        sellers: [{
          code: this.getSeller.id,
          label: this.getSeller.name
        }],
        status: {
          code: 'closed',
          label: 'Cerrado'
        },
        date_start: moment().subtract(10, 'years').format('YYYY-MM-DD'),
        date_end: moment().format('YYYY-MM-DD'),
        source: 'product',
        typeOperations: 'wholesaler',
      }
    },

    loadEditOrders(page = 1, silent = false) {
      if (!this.getSeller || !this.getSeller.id) return Promise.resolve()

      const queueItems = this.formatQueueItemsForTable()

      if (!this.isOnline) {
        this.editOrder.items = queueItems
        this.editOrder.hasOrders = queueItems.length > 0
        this.editOrder.pagination.total = queueItems.length
        this.editOrder.pagination.currentPage = 1
        this.editOrder.pagination.lastPage = 1
        return Promise.resolve()
      }

      this.editOrder.loading = true
      const filters = this.buildEditOrdersFilters()

      return serviceAPI.filtrarPedido(filters, page)
        .then((response) => {
          const paginate = response && response.data ? response.data : {}
          const serverItems = (Array.isArray(paginate.data) ? paginate.data : []).filter(item => item && item.status === 'closed')
          this.editOrder.items = [...queueItems, ...serverItems]
          this.editOrder.pagination.currentPage = parseInt(paginate.current_page, 10) || 1
          this.editOrder.pagination.lastPage = parseInt(paginate.last_page, 10) || 1
          this.editOrder.pagination.total = (parseInt(paginate.total, 10) || serverItems.length) + queueItems.length
          this.editOrder.hasOrders = this.editOrder.pagination.total > 0 || this.editOrder.items.length > 0
        })
        .catch((error) => {
          console.error('Error cargando pedidos cerrados:', error)
          this.editOrder.items = queueItems
          this.editOrder.hasOrders = queueItems.length > 0
          if (!silent) {
            this.$bvToast.toast('Error al cargar pedidos cerrados', { title: 'Error', variant: 'danger', solid: true })
          }
        })
        .finally(() => {
          this.editOrder.loading = false
        })
    },

    selectOrderForEdit(order) {
      if (!order || this.editOrder.selecting) return
      if (order.isQueueItem) {
        this.selectQueueItemForEdit(order)
        return
      }
      if (!order.id) return
      this.editOrder.selectedId = parseInt(order.id, 10) || 0
      this.preloadOrderForEdit(order)
    },

    selectQueueItemForEdit(item) {
      const data = item.data
      const customerId = data.customers_id
      const customer = this.findCustomerById(customerId)

      this.editOrder.activeOrderId = 0
      this.crud.form.id = 0
      this.crud.form.client_request_id = data.client_request_id || null
      this.crud.form.date = data.date || moment().format('YYYY-MM-DD')
      this.crud.form.observations = data.observations || ''
      this.crud.form.sellers_id = data.sellers_id || this.getSeller.id
      this.crud.form.customers_id = customerId
      this.crud.form.customers = customer
      this.crud.form.selected_zone = this.findZoneByCustomerId(customerId)
      this.crud.form.price_list_id = data.price_list_id || 0
      this.crud.form.price_list = (this.arr.priceLists || []).find(pl => pl.id === data.price_list_id) || null
      this.crud.form.erp_points_sales_id = data.erp_points_sales_id || 0
      this.crud.form.methods_payment_id = data.methods_payment_id || 0
      this.crud.form.detail = data.detail || []
      this.crud.form.reason_id = data.reason_id || 0
      this.crud.form.discounts_price = data.discounts_price || 0
      this.crud.form.percentage_discount = data.percentage_discount || 0

      this.syncPriceListFromId()
      this.syncMethodsPaymentFromId()
      this.updateGeneralDiscount()

      this.editOrder.editingQueueLocalId = item.localId
      this.editOrder.selectedId = 0
      this.modal.editOrder = false
      this.currentStep = 3
      window.scrollTo(0, 0)

      // Si el vendedor vuelve al paso 2, deja la búsqueda preparada para
      // consultar la fuente correspondiente (API o IndexedDB).
      this.loadProducts()

      this.$bvToast.toast('Pedido pendiente cargado para edición', {
        title: 'Edición offline',
        variant: 'warning',
        solid: true,
        autoHideDelay: 2500
      })
    },

    findCustomerById(id) {
      return (this.arr.customers || []).find(customer => (
        parseInt(customer.id, 10) === parseInt(id, 10)
      )) || null
    },

    isQueueItemExhausted(item) {
      const status = item.queueStatus || item.status
      return status === 'error' && (item.attempts || 0) >= SYNC_MAX_ATTEMPTS
    },

    formatQueueItemsForTable() {
      return OfflineManager.getQueue()
        .filter(i => i.status === 'pending' || i.status === 'error')
        .map(item => {
          const customerId = item.data.customers_id
          const customer = this.findCustomerById(customerId)
          const total = (item.data.detail || []).reduce((sum, d) => sum + (parseFloat(d.amount || 0) * parseFloat(d.quantity || 1)), 0)
          return {
            id: null,
            date: item.data.date,
            total,
            customers_id: customerId,
            customer: customer || null,
            customer_name: customer ? (customer.name || customer.business_name || `Cliente #${customerId}`) : `Cliente #${customerId}`,
            status: 'queue',
            isQueueItem: true,
            localId: item.localId,
            queueStatus: item.status,
            attempts: item.attempts || 0,
            isEdit: item.isEdit,
            error: item.error,
            data: item.data,
          }
        })
    },

    confirmDialog(message, options) {
      const danger = !!options.danger
      const icon = options.icon || (danger ? 'warning' : 'help')
      const title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
        this.$createElement('AppIcon', { props: { name: icon } }),
        options.title
      ])
      return this.$bvModal.msgBoxConfirm(message, {
        title: title,
        size: 'sm',
        buttonSize: 'sm',
        okVariant: danger ? 'danger' : 'dark',
        okTitle: options.okTitle,
        cancelTitle: options.cancelTitle || 'Cancelar',
        dialogClass: 'mobile-order-confirm-dialog',
        bodyClass: 'mobile-order-confirm-body',
        footerClass: options.footerClass ? 'p-2 mobile-order-confirm-footer ' + options.footerClass : 'p-2 mobile-order-confirm-footer',
        headerClass: danger ? 'app-confirm-header app-confirm-header--danger mobile-order-confirm-header' : 'app-confirm-header mobile-order-confirm-header',
        hideHeaderClose: false,
        centered: true
      })
    },

    removeOrder(item) {
      if (item.isQueueItem) {
        this.confirmDialog('¿Confirma que desea eliminar el pedido pendiente de sincronizar?', {
          title: 'Eliminar Pedido Pendiente',
          icon: 'delete',
          danger: true,
          okTitle: 'Eliminar'
        }).then(value => {
          if (value) {
            OfflineManager.removeFromQueue(item.localId)
            this.offline.pendingCount = OfflineManager.pendingCount()
            this.loadEditOrders(1, true)
          }
        })
        return
      }
      this.confirmDialog('¿Confirma que desea eliminar el pedido #' + item.id + '?', {
        title: 'Eliminar Pedido',
        icon: 'delete',
        danger: true,
        okTitle: 'Eliminar'
      }).then(value => {
        if (value) {
          let loader = this.$loading.show()
          serviceAPI.eliminarPedido(item.id)
            .then(() => {
              loader.hide()
              this.loadEditOrders(1, true)
              this.$awn.success('Pedido eliminado correctamente')
            })
            .catch(error => {
              loader.hide()
              const msg = (error.response && error.response.data && error.response.data.message) ||
                (error.response ? 'Error al eliminar el pedido' : 'Sin conexión: no se puede eliminar el pedido ahora')
              this.$awn.alert(msg)
            })
        }
      })
    },

    async preloadOrderForEdit(order) {
      this.editOrder.selecting = true

      try {
        const response = await serviceAPI.mostrarPedido(order.id)
        const orderData = response && response.data ? response.data : null

        if (!orderData) {
          throw new window.Error('No se pudo obtener el pedido')
        }

        const loadedOrderId = this.normalizeOrderId(orderData.id)
        if (!loadedOrderId) {
          throw new window.Error('No se pudo cargar el identificador del pedido a editar')
        }

        const detailRows = Array.isArray(order.details) ? order.details : []
        const detailFormatted = Array.isArray(orderData.detail)
          ? orderData.detail.map((detailItem, index) => this.mapOrderDetailForEdit(detailItem, detailRows[index]))
          : []

        // Identificar extras: mismo producto repetido → el de id más alto es el extra
        // Usa detailRows (datos crudos del modelo) que tienen id y products_*_id
        if (detailRows.length > 0) {
          const productIdMap = {}
          detailRows.forEach((row, index) => {
            const pid = row.products_id || row.products_colors_id || row.products_waist_id || row.products_color_waist_id
            if (!pid) return
            if (!productIdMap[pid]) productIdMap[pid] = []
            productIdMap[pid].push({ index, id: row.id })
          })
          Object.values(productIdMap).forEach(entries => {
            if (entries.length > 1) {
              const maxEntry = entries.reduce((max, e) => e.id > max.id ? e : max)
              if (detailFormatted[maxEntry.index]) {
                detailFormatted[maxEntry.index].is_extra = true
              }
            }
          })
        }

        const customerId = parseInt(orderData.customers_id || (orderData.customer ? orderData.customer.id : 0), 10) || 0
        let customer = orderData.customer || null

        if (customerId) {
          try {
            const customerResponse = await serviceAPI.mostrarCliente(customerId)
            if (customerResponse && customerResponse.data) {
              customer = customerResponse.data
            }
          } catch (error) {}
        }

        this.editOrder.activeOrderId = loadedOrderId
        this.crud.form.id = loadedOrderId
        this.crud.form.client_request_id = null
        this.crud.form.date = orderData.date || moment().format('YYYY-MM-DD')
        this.crud.form.observations = this.stripAutoDiscountObservations(orderData.observation || '')
        this.crud.form.sellers_id = this.getSeller.id
        this.crud.form.customers_id = customerId
        this.crud.form.customers = customer
        this.crud.form.selected_zone = this.findZoneByCustomerId(customerId)
        this.crud.form.price_list_id = parseInt(orderData.price_list_id || (orderData.price_list ? orderData.price_list.id : 0), 10) || 0
        this.crud.form.price_list = orderData.price_list || null
        this.crud.form.erp_points_sales_id = parseInt(orderData.erp_points_sales_id || (orderData.points_sales ? orderData.points_sales.id : 0), 10) || 0
        this.crud.form.erp_points_sales = orderData.points_sales || null
        this.crud.form.methods_payment_id = parseInt(orderData.methods_payment_id || (orderData.method_payment ? orderData.method_payment.id : 0), 10) || 0
        this.crud.form.methods_payment = orderData.method_payment || null
        this.crud.form.detail = detailFormatted
        this.crud.form.is_retail = !!orderData.is_retail
        this.crud.form.reason_id = parseInt(orderData.reason_id || (orderData.reason ? orderData.reason.id : 0), 10) || 0
        this.crud.form.discounts_price = this.normalizeNumber(orderData.discounts_price, 0)
        this.crud.form.percentage_discount = this.normalizeDiscountValue(orderData.percentage_discount)

        this.syncPriceListFromId()
        this.syncMethodsPaymentFromId()
        this.updateGeneralDiscount()
        this.search.product = ''
        this.arr.products = []
        this.productsRequestId = 0
        this.productSearchHasExecuted = false

        this.editOrder.selectedId = loadedOrderId
        this.modal.editOrder = false
        this.currentStep = 3
        window.scrollTo(0, 0)

        this.$bvToast.toast(`Pedido #${loadedOrderId} cargado para edición`, {
          title: 'Edición',
          variant: 'success',
          solid: true,
          autoHideDelay: 2500
        })
      } catch (error) {
        this.resetForm()
        console.error('Error precargando pedido para edición:', error)
        this.$bvToast.toast(Error.showError(error) || 'Error al cargar el pedido para edición', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.editOrder.selecting = false
      }
    },

    mapOrderDetailForEdit(detailItem, detailRow) {
      const quantity = Math.max(1, this.normalizeNumber(detailItem && detailItem.quantity, 1))
      const amount = this.normalizeNumber(detailItem && detailItem.amount, 0)
      const discount = this.normalizeDiscountValue(detailRow && detailRow.discount_percentage)
      let amountOriginal = this.normalizeNumber(detailRow && detailRow.unit_price_original, 0)

      if (!amountOriginal) {
        if (discount > 0 && discount < 100) {
          amountOriginal = parseFloat((amount / (1 - (discount / 100))).toFixed(2))
        } else {
          amountOriginal = amount
        }
      }

      const product = detailItem && detailItem.product ? { ...detailItem.product } : null
      if (product && !product.source) {
        product.source = 'product'
      }

      const maxDiscount = this.getProductMaxDiscount(product)

      const mapped = {
        product,
        quantity,
        quantityOld: quantity,
        currency: detailItem && detailItem.currency
          ? detailItem.currency
          : (product && product.list ? product.list.currency : null),
        amount,
        amount_original: amountOriginal,
        discount,
        unit_measurement: detailItem && detailItem.unit_measurement
          ? detailItem.unit_measurement
          : (product ? product.unit_measurement_sale : null),
        price_list: detailItem && detailItem.price_list
          ? detailItem.price_list
          : (product ? product.list : null),
        max_discount: maxDiscount,
        is_extra: false,
      }

      if (mapped.product && maxDiscount !== null && maxDiscount !== undefined) {
        mapped.product.max_discount = maxDiscount
      }

      return mapped
    },

    findZoneByCustomerId(customerId) {
      if (!customerId || !Array.isArray(this.arr.zones)) {
        return null
      }
      return this.arr.zones.find(zone => {
        if (!zone || !Array.isArray(zone.customers)) {
          return false
        }
        return zone.customers.some(customer => parseInt(customer.id, 10) === parseInt(customerId, 10))
      }) || null
    },

    getOrderCustomerName(order) {
      if (!order) return ''
      if (order.customer_name) return order.customer_name
      if (order.customer && order.customer.name) return order.customer.name
      if (order.customer && order.customer.business_name) return order.customer.business_name
      return 'Sin cliente'
    },

    formatOrderDate(date) {
      if (!date) return '-'
      return moment(date).format('DD/MM/YYYY')
    },

    formatOrderAmount(value) {
      return `$ ${this.formatPrice(this.normalizeNumber(value, 0))}`
    },

    getOrderNetAmount(order) {
      if (!order) return 0

      if (order.isQueueItem && order.data) {
        const detail = Array.isArray(order.data.detail) ? order.data.detail : []
        if (detail.length) {
          return detail.reduce((sum, item) => {
            const quantity = Math.max(1, this.normalizeNumber(item && item.quantity, 1))
            const amount = this.normalizeNumber(item && item.amount, 0)
            return sum + (quantity * amount)
          }, 0) - this.normalizeNumber(order.data.discounts_price, 0)
        }
      }

      return this.normalizeNumber(order.total, 0)
    },

    getOrderAmountWithIva(order) {
      if (!order) return null

      const netAmount = this.getOrderNetAmount(order)

      if (order.totalIva !== undefined && order.totalIva !== null && order.totalIva !== '') {
        const totalIva = this.normalizeNumber(order.totalIva, 0)
        return Math.abs(totalIva - netAmount) < 0.001 ? null : totalIva
      }

      if (order.total_iva !== undefined && order.total_iva !== null && order.total_iva !== '') {
        const totalIva = this.normalizeNumber(order.total_iva, 0)
        return Math.abs(totalIva - netAmount) < 0.001 ? null : totalIva
      }

      const detail = this.getOrderDetailItems(order)
      if (!detail.length) return null

      let subtotalNet = 0
      let subtotalIva = 0

      detail.forEach(item => {
        const quantity = Math.max(1, this.normalizeNumber(item && item.quantity, 1))
        const amount = this.normalizeNumber(item && item.amount, 0)
        const totalNet = this.getOrderDetailNetTotal(item, quantity, amount)
        const totalIva = this.getOrderDetailTotalWithIva(item, quantity, amount)

        subtotalNet += totalNet
        subtotalIva += totalIva
      })

      if (subtotalIva <= 0) return null

      const discount = this.getOrderDiscountAmount(order)
      let totalWithIva = subtotalIva

      if (subtotalNet <= 0 || discount <= 0) {
        return Math.abs(totalWithIva - netAmount) < 0.001 ? null : totalWithIva
      }

      const ratio = Math.max((subtotalNet - discount) / subtotalNet, 0)
      totalWithIva = subtotalIva * ratio
      return Math.abs(totalWithIva - netAmount) < 0.001 ? null : totalWithIva
    },

    getOrderDetailItems(order) {
      if (!order) return []
      if (Array.isArray(order.details) && order.details.length) return order.details
      if (Array.isArray(order.detail) && order.detail.length) return order.detail
      if (order.data && Array.isArray(order.data.detail) && order.data.detail.length) return order.data.detail
      return []
    },

    getOrderDiscountAmount(order) {
      if (!order) return 0
      if (order.isQueueItem && order.data) {
        return this.normalizeNumber(order.data.discounts_price, 0)
      }
      return this.normalizeNumber(order.discounts_price, 0)
    },

    getOrderDetailNetTotal(item, quantity, amount) {
      const total = this.normalizeNumber(item && item.total, NaN)
      if (!isNaN(total) && total > 0) {
        return total
      }
      return quantity * amount
    },

    getOrderDetailTotalWithIva(item, quantity, amount) {
      const totalIva = this.normalizeNumber(item && item.total_iva, NaN)
      if (!isNaN(totalIva) && totalIva > 0) {
        return totalIva
      }

      const amountIva = this.normalizeNumber(item && item.amount_iva, NaN)
      const netTotal = this.getOrderDetailNetTotal(item, quantity, amount)
      if (!isNaN(amountIva) && amountIva > 0) {
        return netTotal + amountIva
      }

      const product = item && item.product ? item.product : null
      const amountWithIva = this.getProductPriceWithIva(product, amount)
      return quantity * (amountWithIva !== null ? amountWithIva : amount)
    },

    editOrderRowClass(item, type) {
      if (type !== 'row' || !item) return ''
      const id = parseInt(item.id, 10) || 0
      const selectedId = parseInt(this.editOrder.selectedId, 10) || 0
      return id && selectedId === id
        ? 'mobile-edit-order-row-selected'
        : 'mobile-edit-order-row'
    },

    // ==========================================
    // Navegación
    // ==========================================
    goBack() {
      if (this.currentStep > 1) {
        this.currentStep--
      } else {
        this.$router.push({ name: 'OrdersStaffDashboard' })
      }
    },

    nextStep() {
      if (this.currentStep < this.totalSteps) {
        this.currentStep++
        this.saveDraftNow('step-change')
        window.scrollTo(0, 0)
      }
    },

    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--
        this.saveDraftNow('step-change')
        window.scrollTo(0, 0)
      }
    },

    goToDashboard() {
      this.modal.success = false
      this.resetForm()
      this.currentStep = 1
      this.$router.push({ name: 'OrdersStaffDashboard' })
    },

    createNewOrder() {
      this.modal.success = false
      this.offline.queued = false
      this.resetForm()
      this.currentStep = 1
      this.loadEditOrders(1, true)
      window.scrollTo(0, 0)
    },

    cancelEditingOrder() {
      if (!this.isEditingOrder) {
        return
      }

      this.confirmDialog(
        '¿Desea cancelar la edición del pedido y comenzar uno nuevo?',
        {
          title: 'Cancelar edición',
          icon: 'warning',
          okTitle: 'Cancelar edición',
          cancelTitle: 'Volver'
        })
        .then(value => {
          if (!value) return
          this.resetForm()
          this.currentStep = 1
          window.scrollTo(0, 0)
          this.$bvToast.toast('Se canceló la edición del pedido', {
            title: 'Edición cancelada',
            variant: 'info',
            solid: true,
            autoHideDelay: 2500
          })
        })
        .catch(() => {})
    },

    async toggleFocusMode() {
      const enable = !this.focusMode
      if (enable) {
        this.enableFocusMode()
        await this.requestBrowserFullscreen()
      } else {
        await this.exitBrowserFullscreen()
        this.disableFocusMode()
      }
    },

    enableFocusMode() {
      this.focusMode = true
      if (typeof document === 'undefined') return
      if (document.body) {
        document.body.classList.add('mobile-order-focus-mode')
      }
    },

    disableFocusMode() {
      this.focusMode = false
      if (typeof document === 'undefined') return
      if (document.body) {
        document.body.classList.remove('mobile-order-focus-mode')
      }
    },

    async requestBrowserFullscreen() {
      if (typeof document === 'undefined') return
      if (document.fullscreenElement) return
      const target = document.documentElement
      if (target && typeof target.requestFullscreen === 'function') {
        try {
          await target.requestFullscreen()
        } catch (error) {}
      }
    },

    async exitBrowserFullscreen() {
      if (typeof document === 'undefined') return
      if (!document.fullscreenElement) return
      if (typeof document.exitFullscreen === 'function') {
        try {
          await document.exitFullscreen()
        } catch (error) {}
      }
    },

    // ==========================================
    // Swipe navigation
    // ==========================================
    onTouchStart(event) {
      if (!event.touches || !event.touches.length) return
      this.touch.startX = event.touches[0].clientX
      this.touch.startY = event.touches[0].clientY
    },

    onTouchEnd(event) {
      if (!event.changedTouches || !event.changedTouches.length) return
      const endX = event.changedTouches[0].clientX
      const endY = event.changedTouches[0].clientY
      const dx = endX - this.touch.startX
      const dy = endY - this.touch.startY

      const threshold = 60
      if (Math.abs(dx) < threshold) return
      if (Math.abs(dx) < Math.abs(dy) * 1.5) return

      if (dx < 0) {
        this.handleSwipeNext()
      } else {
        this.handleSwipePrev()
      }
    },

    handleSwipeNext() {
      if (this.currentStep === 1) {
        this.nextStepFromCustomer()
        return
      }
      if (this.currentStep === 2) {
        this.nextStep()
      }
    },

    handleSwipePrev() {
      if (this.currentStep > 1) {
        this.prevStep()
      }
    },

    // ==========================================
    // Offline: handlers de red y sincronización
    // ==========================================
    onSwActivated() {
    },

    handleOnline() {
      // Optimista: el navegador dice que hay red. runConnectivityCheck
      // confirma con un ping real y corrige isOnline si la conexión sigue
      // sin servir (ej: wifi asociado pero sin salida a internet).
      this.isOnline = true
      this.runConnectivityCheck({ silent: true }).then(online => {
        if (!online) return
        this.offline.pendingCount = OfflineManager.pendingCount()
        if (this.offline.pendingCount > 0) {
          this.processSyncQueue()
        }
      })
    },

    // ── Conectividad real ───────────────────────────────────────────────
    // navigator.onLine solo informa si hay una interfaz de red asociada, no
    // si esa red funciona. El ping real mantiene confiable el indicador y
    // decide cuándo actualizar datos o enviar pedidos; si falla, obliga a
    // usar la copia local para no dejar una fuente de datos inutilizable.
    syncBrowserOnlineState() {
      this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true
      if (!this.isOnline) {
        this.apiStatus.online = false
        this.offline.connectivityConfirmed = false
        this.forceLocalModeForNoConnection()
      }
    },

    startConnectivityWatch() {
      this.stopConnectivityWatch()
      this.runConnectivityCheck({ silent: true })
      this._connectivityTimer = window.setInterval(() => {
        this.runConnectivityCheck({ silent: true })
      }, CONNECTIVITY_CHECK_INTERVAL_MS)
    },

    stopConnectivityWatch() {
      if (this._connectivityTimer) {
        window.clearInterval(this._connectivityTimer)
        this._connectivityTimer = null
      }
    },

    async runConnectivityCheck() {
      this.syncBrowserOnlineState()
      // Sin interfaz de red: ni vale la pena pingear, ya es offline real.
      if (!this.isOnline) return false
      const apiOnline = await this.checkApiStatus()
      this.isOnline = apiOnline
      if (!apiOnline) {
        this.offline.connectivityConfirmed = false
        this.forceLocalModeForNoConnection()
      } else if (!this.offline.connectivityConfirmed) {
        // Sólo al abrir/reactivar o recuperar backend. El intervalo de red es
        // de segundos, pero esta evaluación no debe iniciar un pre-sync cada
        // vez que el ping responde.
        this.offline.connectivityConfirmed = true
        this.autoPreSync()
      }
      return apiOnline
    },

    async checkApiStatus() {
      if (this.apiStatus.checking) return this.apiStatus.online === true
      this.apiStatus.checking = true
      try {
        // Doble resguardo: el timeout de axios (config.timeout) corta el XHR
        // a nivel nativo vía xhr.ontimeout, atado al ciclo de vida real de
        // esa request. El Promise.race es una segunda red por si esa request
        // nunca dispara ningún evento de red (ej: paquetes descartados en
        // silencio por un portal cautivo, sin RST ni timeout de socket).
        await Promise.race([
          serviceAPI.obtenerConfiguracion({ timeout: API_CHECK_TIMEOUT_MS }),
          new Promise((resolve, reject) => setTimeout(() => reject(new window.Error('Timeout de API')), API_CHECK_TIMEOUT_MS + 500)),
        ])
        this.apiStatus.online = true
        this.apiStatus.lastCheckedAt = new Date().toISOString()
        return true
      } catch (e) {
        this.apiStatus.online = false
        this.apiStatus.lastCheckedAt = new Date().toISOString()
        return false
      } finally {
        this.apiStatus.checking = false
      }
    },

    prepareLocalProducts(raw) {
      return (raw || []).map(p => ({
        ...p,
        quantity: 1,
        price: null,
        priceLoading: false,
        discount: 0,
        extraEnabled: false,
        extraQuantity: 1,
      }))
    },

    handleOffline() {
      this.isOnline = false
      this.apiStatus.online = false
      this.offline.connectivityConfirmed = false
      this.forceLocalModeForNoConnection()
    },

    async autoPreSync() {
      // No alcanza con navigator.onLine: la actualización sólo puede salir
      // después de que el ping confirmó que el backend responde.
      if (!this.isOnline || !this.offline.connectivityConfirmed || !this.getSeller) return
      const lastSync = OfflineManager.getLastSync(this.getSeller.id)
      const ageMs = lastSync ? Date.now() - lastSync : Infinity
      const maxAgeMs = this.offline.localFirst
        ? LOCAL_FIRST_SYNC_INTERVAL_MS
        : CONNECTED_SYNC_INTERVAL_HOURS * 60 * 60 * 1000
      const hasCompleteData = await this.hasCompleteLocalData()
      if (!hasCompleteData || ageMs >= maxAgeMs) {
        this.preSync(true)
      }
    },

    async hasCompleteLocalData() {
      if (!this.getSeller) return false
      const sellerId = this.getSeller.id
      const requiredBlocks = [
        OfflineManager.getZones(this.getSeller.id),
        OfflineManager.getPriceLists(sellerId),
        OfflineManager.getUnitMeasurements(sellerId),
        OfflineManager.getPointsSales(sellerId),
        OfflineManager.getMethodsPayments(sellerId),
        OfflineManager.getIvaConditions(sellerId),
        OfflineManager.getConfigurations(sellerId),
      ]
      if (this.parameters.haveReason) requiredBlocks.push(OfflineManager.getReasons(sellerId))
      const [dataBlocks, hasProducts] = await Promise.all([
        Promise.all(requiredBlocks),
        OfflineManager.hasProducts(sellerId),
      ])
      return dataBlocks.every(block => block !== null) && hasProducts
    },

    formatRelativeSyncTime(timestamp) {
      const elapsedMs = Math.max(0, Date.now() - timestamp)
      const elapsedMinutes = Math.floor(elapsedMs / 60000)
      if (elapsedMinutes < 1) return 'hace menos de un minuto'
      if (elapsedMinutes < 60) return `hace ${elapsedMinutes} ${elapsedMinutes === 1 ? 'minuto' : 'minutos'}`

      const elapsedHours = Math.floor(elapsedMinutes / 60)
      if (elapsedHours < 24) return `hace ${elapsedHours} ${elapsedHours === 1 ? 'hora' : 'horas'}`

      const elapsedDays = Math.floor(elapsedHours / 24)
      return `hace ${elapsedDays} ${elapsedDays === 1 ? 'día' : 'días'}`
    },

    startLastSyncLabelTimer() {
      this.stopLastSyncLabelTimer()
      this.offline.lastSyncLabelTick = Date.now()
      this._lastSyncLabelTimer = window.setInterval(() => {
        this.offline.lastSyncLabelTick = Date.now()
      }, 60000)
    },

    stopLastSyncLabelTimer() {
      if (this._lastSyncLabelTimer) {
        window.clearInterval(this._lastSyncLabelTimer)
        this._lastSyncLabelTimer = null
      }
    },

    startLocalFirstSyncTimer() {
      this.stopLocalFirstSyncTimer()
      if (!this.offline.localFirst) return

      // Se revisa con frecuencia corta, pero sólo descarga si la última
      // sincronización completa ya superó los 15 minutos.
      this.autoPreSync()
      this._localFirstSyncTimer = window.setInterval(() => {
        this.autoPreSync()
      }, 60000)
    },

    stopLocalFirstSyncTimer() {
      if (this._localFirstSyncTimer) {
        window.clearInterval(this._localFirstSyncTimer)
        this._localFirstSyncTimer = null
      }
    },

    async preSync(silent = false) {
      if (this.offline.presyncing || !this.getSeller) return
      this.offline.presyncing = true
      this.offline.presyncProgress = 0
      const sellerId = this.getSeller.id

      // Siempre se descarga una foto completa, pero nunca se borra el último
      // bloque válido antes de tener su reemplazo disponible.
      const totalSteps = this.parameters.haveReason ? 10 : 9
      let completedSteps = 0
      let hasPartialFailures = false

      const advance = (stepName) => {
        completedSteps++
        this.offline.presyncProgress = Math.round((completedSteps / totalSteps) * 100)
        if (!silent) this.offline.presyncStep = stepName
      }

      try {
        // Clientes: se reemplazan solo luego de recibir una respuesta válida.
        advance('Clientes...')
        const zonesResp = await serviceAPI.obtenerZonasConClientes({ sellers_id: sellerId })
        if (!(await OfflineManager.saveZones(sellerId, zonesResp.data))) hasPartialFailures = true

        advance('Listas de precios...')
        const plResp = await serviceAPI.obtenerListasPrecios()
        const priceLists = (plResp.data || []).filter(pl => pl.active)
        if (!(await OfflineManager.savePriceLists(sellerId, priceLists))) hasPartialFailures = true

        advance('Precios...')
        const pricesByList = {}
        const previousPricesByList = {}
        const cachedProducts = await OfflineManager.getProducts(sellerId) || []
        cachedProducts.forEach(product => {
          Object.entries(product._offlinePrices || {}).forEach(([priceListId, price]) => {
            if (!price) return
            if (!previousPricesByList[priceListId]) previousPricesByList[priceListId] = {}
            previousPricesByList[priceListId][product.id] = price
          })
        })
        for (const pl of priceLists) {
          try {
            const pricesResp = await serviceAPI.obtenerProductosListaPrecio({ price_list_id: pl.id, slim: true })
            const prices = pricesResp.data || []
            pricesByList[pl.id] = {}
            prices.forEach(p => {
              pricesByList[pl.id][p.products_id] = {
                products_id: p.products_id,
                amount_initial: p.amount_initial,
                amount_total: p.amount_total,
              }
            })
          } catch (e) {
            console.warn(`[preSync] No se pudieron cargar precios de lista ${pl.id}`, e)
            // Mantener los precios ya utilizables para esa lista: nunca se
            // reemplazan por null debido a una caída parcial de red.
            pricesByList[pl.id] = previousPricesByList[pl.id] || {}
            hasPartialFailures = true
          }
        }

        advance('Productos...')
        const productsResp = await serviceAPI.filtrarProductos(
          { filter: '', where: [{ field: 'm_products.active', condition: true }], withCategoryRelation: true, include_stock: true, only_with_price: false, only_with_stock: false, price_list_id: null }
        )
        const products = (productsResp.data || []).map(p => ({
          id: p.id,
          name: p.name,
          code: p.code,
          active: p.active,
          stock: p.stock,
          max_discount: p.max_discount !== undefined ? p.max_discount : null,
          erp_iva_condition_id: p.erp_iva_condition_id || null,
          unit_measurement_purchase_id: p.unit_measurement_purchase_id,
          unit_measurement_sale_id: p.unit_measurement_sale_id,
          unit_measurement_sale_conversion: p.unit_measurement_sale_conversion || null,
          unit_measurement_sale: p.unit_measurement_sale
            ? { id: p.unit_measurement_sale.id, reference: p.unit_measurement_sale.reference, name: p.unit_measurement_sale.name }
            : null,
          subcategory: p.subcategory
            ? { id: p.subcategory.id, name: p.subcategory.name, category: p.subcategory.category
                ? { id: p.subcategory.category.id, name: p.subcategory.category.name }
                : null }
            : null,
          colors_waist: p.colors_waist || [],
          colors: p.colors || [],
          waist: p.waist || [],
          _offlinePrices: Object.fromEntries(
            Object.entries(pricesByList).map(([plId, priceMap]) => [plId, priceMap[p.id] || null])
          )
        }))
        const saved = await OfflineManager.saveProducts(sellerId, products)
        if (!saved) {
          hasPartialFailures = true
          if (!silent) this.$awn.warning('No se pudieron guardar los productos offline (almacenamiento lleno)')
        }

        advance('Unidades de medida...')
        const umResp = await serviceAPI.obtenerUnidadesMedida({ filter: '', where: [] })
        if (!(await OfflineManager.saveUnitMeasurements(sellerId, umResp.data || []))) hasPartialFailures = true

        advance('Puntos de venta...')
        const psResp = await serviceAPI.obtenerPuntosVentas()
        if (!(await OfflineManager.savePointsSales(sellerId, (psResp.data || []).filter(p => p.active)))) hasPartialFailures = true

        advance('Medios de pago...')
        const mpResp = await serviceAPI.obtenerMetodosPagos()
        if (!(await OfflineManager.saveMethodsPayments(sellerId, mpResp.data || []))) hasPartialFailures = true

        if (this.parameters.haveReason) {
          advance('Motivos de pedido...')
          const reasonsResp = await serviceAPI.obtenerMotivos()
          if (!(await OfflineManager.saveReasons(sellerId, reasonsResp.data || []))) hasPartialFailures = true
        }

        advance('Condiciones de IVA...')
        const ivaResp = await serviceAPI.obtenerCondicionesIva()
        this.arr.ivaConditions = ivaResp.data || []
        if (!(await OfflineManager.saveIvaConditions(sellerId, ivaResp.data || []))) hasPartialFailures = true

        advance('Configuración...')
        const cfgResp = await serviceAPI.obtenerConfiguracion()
        if (!(await OfflineManager.saveConfigurations(sellerId, cfgResp.data || []))) hasPartialFailures = true

        if (!hasPartialFailures) {
          OfflineManager.setLastSync(sellerId)
          this.offline.lastSyncTs = Date.now()
        }
        if (typeof window.__checkAppVersion === 'function') {
          window.__checkAppVersion()
        }
        // En modo local la interfaz no consulta la API durante el uso: al
        // terminar el refresco, adopta la copia recién guardada.
        if (this.offline.localFirst) {
          this.reloadOrderDataFromCurrentSource()
        }
        if (!silent && !hasPartialFailures) {
          this.$awn.success('Datos actualizados para uso offline')
        } else if (!silent && hasPartialFailures) {
          this.$awn.warning('Se actualizaron datos disponibles; se conservó la última copia de los que fallaron')
        }
      } catch (error) {
        console.error('[preSync] Error:', error)
        if (!silent) this.$awn.warning('No se pudieron actualizar todos los datos offline')
      } finally {
        this.offline.presyncing = false
        this.offline.presyncStep = ''
        this.offline.presyncProgress = 0
      }
    },

    // La app está "ocupada" si hay un pedido a medio cargar, guardándose o
    // sincronizándose: main.js consulta esto antes de recargar por un deploy.
    isAppBusyForUpdate() {
      return this.currentStep > 1 ||
        this.loading.saving ||
        this.offline.syncing ||
        !!this.editOrder.editingQueueLocalId
    },

    onAppUpdateDeferred() {
      if (this._updateNoticeShown) return
      this._updateNoticeShown = true
      this.$bvToast.toast('Hay una nueva versión de la aplicación. Se aplicará automáticamente al terminar el pedido.', {
        title: 'Actualización disponible',
        variant: 'info',
        solid: true,
        autoHideDelay: 5000
      })
    },

    async processSyncQueue(manual = false) {
      if (this.offline.syncing) return
      // El barrido automático deja afuera los pedidos que agotaron sus
      // reintentos; el botón manual los incluye por si el error del servidor
      // era transitorio. El item que el vendedor tiene abierto en edición
      // nunca se sincroniza: si se enviara a mitad de la corrección, el
      // pedido se crearía sin los cambios y el guardado posterior fallaría.
      const now = Date.now()
      const pending = OfflineManager.getQueue().filter(i =>
        i.localId !== this.editOrder.editingQueueLocalId &&
        (i.status === 'pending' ||
        (i.status === 'error' && (manual ||
          ((i.attempts || 0) < SYNC_MAX_ATTEMPTS && (!i.nextRetryAt || now >= i.nextRetryAt)))))
      )
      if (!pending.length) return

      this.offline.syncing = true
      let syncedCount = 0

      for (const item of pending) {
        if (!this.isOnline) break
        const idempotencyKey = (item.data && item.data.client_request_id) || item.localId
        try {
          OfflineManager.updateQueueItem(item.localId, { status: 'syncing' })
          const request = item.isEdit ? serviceAPI.editarPedido(item.data) : serviceAPI.agregarPedido(item.data)
          const response = await request
          // Choque de idempotencia sobre un alta corregida: el POST original
          // ya había llegado al servidor, así que las correcciones del
          // vendedor se aplican como edición sobre el pedido existente.
          // Si esta edición falla, el catch deja el item en la cola y el
          // reintento vuelve a pasar por acá (converge, no duplica).
          if (!item.isEdit && item.resaved && response && response.data &&
              response.data.idempotency_hit && response.data.id) {
            await serviceAPI.editarPedido({ ...item.data, id: response.data.id })
          }
          OfflineManager.removeFromQueue(item.localId)
          syncedCount++
          if (response && response.data && response.data.id) {
            LocationTracker.send({ order_id: response.data.id, source: 'order' })
          }
          serviceAPI.deleteSyncErrors(idempotencyKey).catch(() => {})
        } catch (error) {
          if (!error.response) {
            // Sin respuesta del servidor: se cayó la conexión a mitad del
            // envío. No consume intentos ni se loguea (no llegaría), y se
            // corta el barrido porque el resto va a fallar igual.
            OfflineManager.updateQueueItem(item.localId, { status: 'pending' })
            break
          }
          // JsonErrorMessage del backend devuelve el mensaje como string
          // pelado (y siempre con status 500, incluso para validaciones),
          // así que el mensaje puede venir como data directo o como
          // data.message según el origen del error.
          const raw = error.response.data
          const msg = (raw && raw.message) || (typeof raw === 'string' && raw) || 'Error desconocido'
          console.error('[syncQueue] error en pedido', item.localId, msg, raw, 'data enviada:', item.data)
          // Este backend no distingue validación (4xx) de caída (5xx): todo
          // error llega como 500. Cada rechazo consume un intento (tope 5,
          // después queda en REVISAR) con cooldown exponencial entre medio:
          // una caída transitoria del servidor tiene ~80 minutos de gracia
          // antes de agotar los intentos de un pedido válido, y un pedido
          // inválido no martilla ni llena orders-sync-errors.
          const attempts = (item.attempts || 0) + 1
          const COOLDOWNS_SEG = [60, 300, 900, 1800]
          const cooldownMs = COOLDOWNS_SEG[Math.min(attempts, COOLDOWNS_SEG.length) - 1] * 1000
          OfflineManager.updateQueueItem(item.localId, {
            status: 'error', error: msg, attempts,
            nextRetryAt: Date.now() + cooldownMs,
          })
          serviceAPI.logSyncError({
            idempotency_key: idempotencyKey,
            sellers_id: this.getSeller ? this.getSeller.id : null,
            attempt: attempts,
            error_message: msg,
            order_data: item.data,
            occurred_at: new Date().toISOString(),
          }).catch(() => {})
        }
      }

      this.offline.pendingCount = OfflineManager.pendingCount()
      this.offline.syncing = false

      if (syncedCount > 0) {
        this.loadEditOrders(1, true)
      }

      if (this.offline.pendingCount === 0) {
        if (syncedCount > 0) this.$awn.success('Pedidos sincronizados correctamente')
      } else {
        this.$awn.warning(`${this.offline.pendingCount} pedido(s) no se pudieron sincronizar`)
      }
    },

    // ==========================================
    // Carga de datos
    // ==========================================
    async loadConfigurations() {
      const sellerId = this.getSeller.id
      const cached = await OfflineManager.getConfigurations(sellerId)
      if (cached) {
        this.arr.configurations = cached.map(item => ({
          ...item,
          value_boolean: this.normalizeStoredBoolean(item.value_boolean),
        }))
      }
      if (this.useLocalData) return

      serviceAPI.obtenerConfiguracion()
        .then(async (response) => {
          if (this.useLocalData) return
          const data = Array.isArray(response.data) ? response.data : []
          this.arr.configurations = data.map(item => ({
            ...item,
            value_boolean: this.normalizeStoredBoolean(item.value_boolean),
          }))
          await OfflineManager.saveConfigurations(sellerId, data)
        })
        .catch((error) => {
          console.error('Error cargando configuración:', error)
        })
    },

    async loadCustomers() {
      this.loading.customers = true
      const sellerId = this.getSeller.id

      const applyZones = (zones) => {
        this.arr.zones = zones
        const allCustomers = []
        zones.forEach(zone => {
          if (zone.customers) {
            zone.customers.forEach(customer => {
              if (!allCustomers.find(c => c.id === customer.id)) {
                allCustomers.push(customer)
              }
            })
          }
        })
        this.arr.customers = allCustomers
      }

      // Cargar desde caché inmediatamente (funciona online y offline)
      const cached = await OfflineManager.getZones(sellerId)
      if (cached) {
        applyZones(cached)
        this.loading.customers = false
      }

      if (this.useLocalData) {
        this.loading.customers = false
        return
      }

      serviceAPI.obtenerZonasConClientes({ sellers_id: sellerId })
        .then(async (response) => {
          if (this.useLocalData) {
            this.loading.customers = false
            return
          }
          applyZones(response.data)
          await OfflineManager.saveZones(sellerId, response.data)
          this.loading.customers = false
        })
        .catch((error) => {
          console.error('Error cargando clientes:', error)
          this.loading.customers = false
          this.$bvToast.toast('Error al cargar clientes', {
            title: 'Error',
            variant: 'danger',
            solid: true
          })
        })
    },

    // Obtener clientes filtrados de un grupo (ciudad o zona)
    getFilteredCustomersInGroup(group) {
      if (!group || !group.customers) return []
      if (!this.search.customer) return group.customers
      const searchLower = this.search.customer.toLowerCase()
      return group.customers.filter(c => {
        if (c.name && c.name.toLowerCase().includes(searchLower)) return true
        if (c.code && c.code.toLowerCase().includes(searchLower)) return true
        if (group.name && group.name.toLowerCase().includes(searchLower)) return true
        if (c.data_fiscal) {
          if (c.data_fiscal.documents_number && c.data_fiscal.documents_number.toLowerCase().includes(searchLower)) return true
          if (c.data_fiscal.city && c.data_fiscal.city.toLowerCase().includes(searchLower)) return true
          if (c.data_fiscal.state && c.data_fiscal.state.toLowerCase().includes(searchLower)) return true
          if (c.data_fiscal.address && c.data_fiscal.address.toLowerCase().includes(searchLower)) return true
        }
        return false
      })
    },

    toggleGroup(key) {
      this.$set(this.expandedGroups, key, !this.expandedGroups[key])
      if (this.expandedGroups[key]) {
        this.$nextTick(() => this.observeGroupSentinel(key))
      }
    },

    observeGroupSentinel(key) {
      if (!this.scrollObserver) {
        this.scrollObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const groupKey = entry.target.dataset.groupKey
              this.loadMoreGroup(groupKey)
              this.$nextTick(() => {
                // re-observar si aún quedan más
                const sentinel = this.$el.querySelector(`.group-scroll-sentinel[data-group-key="${groupKey}"]`)
                if (sentinel) this.scrollObserver.observe(sentinel)
              })
            }
          })
        }, { threshold: 0.1 })
      }
      const sentinel = this.$el.querySelector(`.group-scroll-sentinel[data-group-key="${key}"]`)
      if (sentinel) this.scrollObserver.observe(sentinel)
    },

    // Paginado incremental de productos (mismo patrón que observeGroupSentinel,
    // observer separado para no mezclar con la paginación de clientes).
    visibleProductsFor(scopeKey, products) {
      if (this.useLocalData) return products || []
      return (products || []).slice(0, this.productPageSize[scopeKey] || PRODUCT_PAGE_SIZE)
    },

    hasMoreProducts(scopeKey, products) {
      if (this.useLocalData) return false
      return (products || []).length > (this.productPageSize[scopeKey] || PRODUCT_PAGE_SIZE)
    },

    async loadMoreProducts(scopeKey) {
      if (this.useLocalData) {
        await this.loadMoreLocalProducts()
        return
      }
      this.$set(this.productPageSize, scopeKey, (this.productPageSize[scopeKey] || PRODUCT_PAGE_SIZE) + PRODUCT_PAGE_SIZE)
    },

    observeProductSentinel(scopeKey) {
      if (!this.productScrollObserver) {
        this.productScrollObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const key = entry.target.dataset.productScopeKey
              this.productScrollObserver.unobserve(entry.target)
              Promise.resolve(this.loadMoreProducts(key)).then(() => {
                this.$nextTick(() => {
                  // Re-observar sólo si todavía queda otro lote por cargar.
                  const sentinel = this.$el.querySelector(`.product-scroll-sentinel[data-product-scope-key="${key}"]`)
                  if (sentinel) this.productScrollObserver.observe(sentinel)
                })
              })
            }
          })
        }, { threshold: 0.1 })
      }
      this.$nextTick(() => {
        const sentinel = this.$el.querySelector(`.product-scroll-sentinel[data-product-scope-key="${scopeKey}"]`)
        if (sentinel) this.productScrollObserver.observe(sentinel)
      })
    },

    initExpandedGroups() {
      this.expandedGroups = {}
      this.groupPageSize = {}
    },

    loadMoreGroup(key) {
      this.$set(this.groupPageSize, key, (this.groupPageSize[key] || 50) + 50)
    },



    isCategoryExpanded(category, index) {
      return index === 0
    },

    isSubcategoryExpanded(category, subcategory, categoryIndex, subcategoryIndex) {
      return false
    },

    getCategoryProductCount(category) {
      if (!category || !Array.isArray(category.subcategories)) {
        return 0
      }
      return category.subcategories.reduce((total, sub) => {
        const count = sub && sub.products ? sub.products.length : 0
        return total + count
      }, 0)
    },

    isCartCategoryExpanded(categoryIndex) {
      return categoryIndex === 0
    },

    isCartSubcategoryExpanded(categoryIndex, subcategoryIndex) {
      return false
    },

    getCartCategoryItemCount(category) {
      if (!category || !Array.isArray(category.subcategories)) {
        return 0
      }
      return category.subcategories.reduce((total, sub) => {
        const count = sub && sub.items ? sub.items.length : 0
        return total + count
      }, 0)
    },

    getCartSubcategoryItemCount(subcategory) {
      if (!subcategory || !Array.isArray(subcategory.items)) {
        return 0
      }
      return subcategory.items.length
    },

    // Obtener la dirección completa del cliente (dirección, CP, ciudad, provincia)
    getCustomerFullAddress(customer) {
      if (!customer || !customer.data_fiscal) return ''
      const parts = []
      if (customer.data_fiscal.address) parts.push(customer.data_fiscal.address)
      if (customer.data_fiscal.zip) parts.push('CP ' + customer.data_fiscal.zip)
      if (customer.data_fiscal.city) parts.push(customer.data_fiscal.city)
      if (customer.data_fiscal.state) parts.push(customer.data_fiscal.state)
      return parts.join(', ')
    },

    async loadReasons() {
      this.loading.reasons = true
      const sellerId = this.getSeller.id
      const cached = await OfflineManager.getReasons(sellerId)
      if (cached) this.arr.reasons = cached

      if (this.useLocalData) {
        this.loading.reasons = false
        return
      }

      serviceAPI.obtenerMotivos()
        .then(async (response) => {
          if (this.useLocalData) {
            this.loading.reasons = false
            return
          }
          this.arr.reasons = response.data || []
          await OfflineManager.saveReasons(sellerId, this.arr.reasons)
          this.loading.reasons = false
        })
        .catch((error) => {
          console.error('Error cargando motivos:', error)
          this.loading.reasons = false
        })
    },

    async loadPointsSales() {
      this.loading.pointsSales = true
      const sellerId = this.getSeller.id

      // Cargar desde caché siempre primero: si isOnline quedó en true por
      // señal débil (navigator.onLine no detecta eso) y el fetch de abajo
      // falla o cuelga, este valor queda como fallback en vez de vaciarse.
      const cached = await OfflineManager.getPointsSales(sellerId)
      if (cached) this.arr.pointsSales = cached

      if (this.useLocalData) {
        this.loading.pointsSales = false
        return
      }

      serviceAPI.obtenerPuntosVentas()
        .then(async (response) => {
          if (this.useLocalData) {
            this.loading.pointsSales = false
            return
          }
          const data = response.data.filter(point => point.active)
          this.arr.pointsSales = data
          await OfflineManager.savePointsSales(sellerId, data)
          this.loading.pointsSales = false
        })
        .catch((error) => {
          console.error('Error cargando puntos de venta:', error)
          this.loading.pointsSales = false
        })
    },

    async loadMethodsPayments() {
      this.loading.methodsPayments = true
      const sellerId = this.getSeller.id

      // Ídem loadPointsSales: caché primero para no quedar vacío si el
      // fetch falla con isOnline todavía en true por señal débil.
      const cached = await OfflineManager.getMethodsPayments(sellerId)
      if (cached) {
        this.arr.methodsPayments = cached
        this.syncMethodsPaymentFromId()
      }

      if (this.useLocalData) {
        this.loading.methodsPayments = false
        return
      }

      serviceAPI.obtenerMetodosPagos()
        .then(async (response) => {
          if (this.useLocalData) {
            this.loading.methodsPayments = false
            return
          }
          const data = Array.isArray(response.data) ? response.data : []
          this.arr.methodsPayments = data
          await OfflineManager.saveMethodsPayments(sellerId, data)
          this.syncMethodsPaymentFromId()
          this.loading.methodsPayments = false
        })
        .catch((error) => {
          console.error('Error cargando medios de pago:', error)
          this.loading.methodsPayments = false
        })
    },

    syncMethodsPaymentFromId() {
      if (!this.crud.form.methods_payment_id) {
        this.crud.form.methods_payment = null
        return
      }
      const selected = this.arr.methodsPayments.find(method => method.id === this.crud.form.methods_payment_id)
      if (selected) {
        this.crud.form.methods_payment = selected
      } else if (!this.crud.form.methods_payment || this.crud.form.methods_payment.id !== this.crud.form.methods_payment_id) {
        this.crud.form.methods_payment = null
      }
    },

    onMethodsPaymentChange() {
      this.syncMethodsPaymentFromId()
      this.saveDraftNow('payment-method-change')
    },

    async loadPriceLists() {
      this.loading.priceLists = true
      const sellerId = this.getSeller.id

      // Ídem loadPointsSales: caché primero para no quedar vacío si el
      // fetch falla con isOnline todavía en true por señal débil.
      const cached = await OfflineManager.getPriceLists(sellerId)
      if (cached) {
        this.arr.priceLists = cached
        this.syncPriceListFromId()
      }

      if (this.useLocalData) {
        this.loading.priceLists = false
        return
      }

      const data = {
        filter: '',
        where: [
          { field: 'active', condition: true }
        ]
      }
      serviceAPI.filtrarListasPrecios(data)
        .then(async (response) => {
          if (this.useLocalData) {
            this.loading.priceLists = false
            return
          }
          this.arr.priceLists = response.data || []
          await OfflineManager.savePriceLists(sellerId, response.data || [])
          this.syncPriceListFromId()
          this.loading.priceLists = false
        })
        .catch((error) => {
          console.error('Error cargando listas de precios:', error)
          this.loading.priceLists = false
        })
    },

    syncPriceListFromId() {
      if (!this.crud.form.price_list_id) {
        this.crud.form.price_list = null
        return
      }
      if (!this.arr.priceLists || !this.arr.priceLists.length) {
        if (this.crud.form.price_list && this.crud.form.price_list.id === this.crud.form.price_list_id) {
          return
        }
        this.crud.form.price_list = null
        return
      }
      const selected = this.arr.priceLists.find(list => list.id === this.crud.form.price_list_id)
      this.crud.form.price_list = selected || null
    },

    onPriceListChange() {
      this.syncPriceListFromId()
      this.refreshProductsPrices()
      this.saveDraftNow('price-list-change')
    },

    refreshProductsPrices() {
      const hasProducts = this.arr.products && this.arr.products.length
      if (!hasProducts) return
      if (hasProducts) {
        this.arr.products.forEach(product => {
          product.price = null
          product.unit_measurement = null
          product.priceLoading = false
        })
      }
      if (!this.priceObserver) {
        this.initPriceObserver()
        return
      }
      this.observePriceCards()
    },

    async loadUnitMeasurements() {
      const sellerId = this.getSeller.id
      // Ídem loadPointsSales: caché primero para no quedar vacío si el
      // fetch falla con isOnline todavía en true por señal débil.
      const cached = await OfflineManager.getUnitMeasurements(sellerId)
      if (cached) {
        this.arr.unitMeasurements = cached
        const map = {}
        cached.forEach(item => { map[item.id] = item })
        this.arr.unitMeasurementsMap = map
      }

      if (this.useLocalData) return

      const data = {
        filter: '',
        where: []
      }
      serviceAPI.obtenerUnidadesMedida(data)
        .then(async (response) => {
          if (this.useLocalData) return
          this.arr.unitMeasurements = response.data
          const map = {}
          this.arr.unitMeasurements.forEach(item => { map[item.id] = item })
          this.arr.unitMeasurementsMap = map
          await OfflineManager.saveUnitMeasurements(sellerId, response.data)
        })
        .catch((error) => {
          console.error('Error cargando unidades de medida:', error)
        })
    },

    async loadIvaConditions() {
      const sellerId = this.getSeller.id
      // Sin esto en caché, offline getProductPriceWithIva() no encuentra
      // ninguna condición y todos los productos muestran solo precio s/IVA
      // (nunca el c/IVA), aunque el resto del catálogo esté disponible.
      // Caché primero (ídem loadPointsSales) para no quedar vacío si el
      // fetch falla con isOnline todavía en true por señal débil.
      const cached = await OfflineManager.getIvaConditions(sellerId)
      if (cached) this.arr.ivaConditions = cached

      if (this.useLocalData) return

      serviceAPI.obtenerCondicionesIva()
        .then(async (response) => {
          if (this.useLocalData) return
          this.arr.ivaConditions = response.data
          await OfflineManager.saveIvaConditions(sellerId, response.data)
        })
        .catch(() => {})
    },

    getProductPriceWithIva(product, priceNet) {
      if (!product || !product.erp_iva_condition_id) return null
      const condition = this.arr.ivaConditions.find(c => c.id === product.erp_iva_condition_id)
      if (!condition || !condition.aliquot || parseFloat(condition.aliquot) === 0) return null
      const aliquot = parseFloat(condition.aliquot)
      const priceWithIva = parseFloat(priceNet) * (1 + aliquot / 100)
      if (Math.abs(priceWithIva - parseFloat(priceNet)) < 0.001) return null
      // Con P75 el importe c/IVA del pedido usa el precio final unitario en
      // centavos, igual que la factura que se emite después.
      return this.parameters.salePriceCalc ? Math.round(priceWithIva * 100) / 100 : priceWithIva
    },

    getProductPriceAfterDiscount(price, discount) {
      const d = parseFloat(discount)
      if (!d || d <= 0) return null
      return parseFloat(price) * (1 - d / 100)
    },

    async loadProducts(force = false) {
      const searchTerm = this.trimmedProductSearch

      // === MODO LOCAL: consultar IndexedDB sin cargar el catálogo completo ===
      if (this.useLocalData) {
        if (!force || !searchTerm || searchTerm.length < getMinProductSearchChars(searchTerm)) {
          this.productsRequestId = 0
          this.arr.products = []
          this.loading.products = false
          this.productSearchHasExecuted = false
          this.localProductSearch = { query: '', nextOffset: 0, hasMore: false, loadingMore: false }
          return
        }

        const requestId = Date.now()
        this.productsRequestId = requestId
        this.loading.products = true
        this.productSearchHasExecuted = true
        this.arr.products = []
        this.mountedSubcategories = {}
        this.localProductSearch = { query: searchTerm, nextOffset: 0, hasMore: false, loadingMore: false }

        const hasCatalog = await OfflineManager.hasProducts(this.getSeller.id)
        if (this.productsRequestId !== requestId) return
        if (!hasCatalog) {
          this.loading.products = false
          this.$bvToast.toast('No hay productos sincronizados. Actualizá los datos cuando tengas conexión.', {
            title: 'Sin datos offline',
            variant: 'warning',
            solid: true,
            toaster: 'b-toaster-bottom-center',
          })
          return
        }

        const result = await OfflineManager.searchProducts(this.getSeller.id, searchTerm, {
          limit: PRODUCT_PAGE_SIZE,
          offset: 0,
          priceListId: this.crud.form.price_list_id || null,
          onlyWithPrice: !!this.productQueryFilters.onlyWithPrice,
          onlyWithStock: !!this.productQueryFilters.onlyWithStock,
        })
        if (this.productsRequestId !== requestId) return
        if (result.error) {
          this.loading.products = false
          this.$bvToast.toast('No se pudieron consultar los productos guardados. Intentá nuevamente o actualizá los datos.', {
            title: 'Error de almacenamiento',
            variant: 'danger',
            solid: true,
            toaster: 'b-toaster-bottom-center',
          })
          return
        }

        this.arr.products = this.prepareLocalProducts(result.items)
        this.localProductSearch.nextOffset = result.nextOffset
        this.localProductSearch.hasMore = result.hasMore
        this.loading.products = false
        this.initPriceObserver()
        return
      }
      // === MODO ONLINE ===

      if (!force && (!searchTerm || searchTerm.length < getMinProductSearchChars(searchTerm))) {
        this.productsRequestId = 0
        this.arr.products = []
        this.loading.products = false
        this.productSearchHasExecuted = false
        if (this.priceObserver) {
          this.priceObserver.disconnect()
        }
        return
      }

      const requestId = Date.now()
      this.productsRequestId = requestId
      this.loading.products = true
      this.arr.products = []
      this.mountedSubcategories = {}
      this.productSearchHasExecuted = true

      const data = {
        filter: searchTerm,
        where: [
          { field: 'm_products.active', condition: true }
        ],
        withCategoryRelation: true,  // Cargar categoría/subcategoría para agrupar en acordeón
        include_stock: true,
        only_with_price: !!this.productQueryFilters.onlyWithPrice,
        only_with_stock: !!this.productQueryFilters.onlyWithStock,
        price_list_id: this.crud.form.price_list_id || null,
      }

      serviceAPI.filtrarProductos(data)
        .then((response) => {
          if (this.productsRequestId !== requestId) {
            return
          }
          // Agregar cantidad por defecto y procesar productos
          this.arr.products = response.data.map(product => {
            return {
              ...product,
              quantity: 1,
              price: null,
              priceLoading: false,
              discount: 0,
              extraEnabled: false,
              extraQuantity: 1,
            }
          })
          this.loading.products = false
          this.initPriceObserver()
        })
        .catch((error) => {
          console.error('Error cargando productos:', error)
          this.loading.products = false
          this.productSearchHasExecuted = false
          this.$bvToast.toast('Error al cargar productos', {
            title: 'Error',
            variant: 'danger',
            solid: true
          })
        })
    },

    async loadMoreLocalProducts() {
      if (!this.useLocalData || !this.localProductSearch.hasMore || this.localProductSearch.loadingMore) return
      const query = this.localProductSearch.query
      if (!query || query !== this.trimmedProductSearch) return

      this.localProductSearch.loadingMore = true
      const requestId = this.productsRequestId
      try {
        const result = await OfflineManager.searchProducts(this.getSeller.id, query, {
          limit: PRODUCT_PAGE_SIZE,
          offset: this.localProductSearch.nextOffset,
          priceListId: this.crud.form.price_list_id || null,
          onlyWithPrice: !!this.productQueryFilters.onlyWithPrice,
          onlyWithStock: !!this.productQueryFilters.onlyWithStock,
        })
        if (this.productsRequestId !== requestId || query !== this.trimmedProductSearch) return
        if (result.error) {
          this.localProductSearch.hasMore = false
          this.$bvToast.toast('No se pudieron cargar más productos guardados. Intentá nuevamente.', {
            title: 'Error de almacenamiento',
            variant: 'danger',
            solid: true,
            toaster: 'b-toaster-bottom-center',
          })
          return
        }

        const existingIds = new Set(this.arr.products.map(product => product.id))
        const nextProducts = this.prepareLocalProducts(result.items)
          .filter(product => !existingIds.has(product.id))
        this.arr.products = this.arr.products.concat(nextProducts)
        this.localProductSearch.nextOffset = result.nextOffset
        this.localProductSearch.hasMore = result.hasMore
        this.initPriceObserver()
      } finally {
        this.localProductSearch.loadingMore = false
      }
    },

    async loadProductsPrices(requestId, products) {
      const list = products && products.length ? products : this.arr.products.slice()
      const CHUNK_SIZE = 3

      for (let i = 0; i < list.length; i += CHUNK_SIZE) {
        if (this.productsRequestId !== requestId) break

        const chunk = list.slice(i, i + CHUNK_SIZE)
        await Promise.all(chunk.map(async (product) => {
          if (this.productsRequestId !== requestId) return

          try {
            const variant = { ...product, source: 'product' }
            const priceData = await this.fetchProductPrice(variant)
            if (priceData && !priceData.undefined) {
              product.price = parseFloat(priceData.price_simple || 0)
              this.$set(product, 'price_initial', parseFloat(priceData.price_initial || 0))
              this.$set(product, 'unit_measurement_sale_conversion', parseFloat(priceData.unit_measurement_sale_conversion || 0) || null)
              product.unit_measurement = priceData.unit_measurement_sale || null
            } else {
              product.price = null
              this.$set(product, 'price_initial', null)
              this.$set(product, 'unit_measurement_sale_conversion', null)
              product.unit_measurement = null
            }
          } catch (error) {
            product.price = null
            this.$set(product, 'price_initial', null)
            this.$set(product, 'unit_measurement_sale_conversion', null)
            product.unit_measurement = null
          } finally {
            product.priceLoading = false
          }
        }))
      }
    },

    onSubcategoryShow(key) {
      setTimeout(() => {
        this.$set(this.mountedSubcategories, key, true)
        if (!this.productPageSize[key]) {
          this.$set(this.productPageSize, key, PRODUCT_PAGE_SIZE)
        }
        this.$nextTick(() => {
          this.observePriceCards()
          this.observeProductSentinel(key)
        })
      }, 200)
    },

    initPriceObserver() {
      if (this.priceObserver) {
        this.priceObserver.disconnect()
      }

      if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
        return
      }

      this.priceObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          const id = parseInt(entry.target.dataset.productId, 10)
          const product = this.arr.products.find(item => item.id === id)
          if (!product || product.priceLoading || product.price !== null) {
            this.priceObserver.unobserve(entry.target)
            return
          }
          product.priceLoading = true
          this.loadProductPrice(product)
          this.priceObserver.unobserve(entry.target)
        })
      }, { root: null, rootMargin: '200px', threshold: 0.1 })

      this.observePriceCards()
    },

    observePriceCards() {
      this.$nextTick(() => {
        if (!this.priceObserver) return
        const nodes = this.$el.querySelectorAll('.product-card-price')
        nodes.forEach((node) => {
          const id = parseInt(node.dataset.productId, 10)
          const product = this.arr.products.find(item => item.id === id)
          if (!product || product.priceLoading || product.price !== null) return
          this.priceObserver.observe(node)
        })
      })
    },

    async loadProductPrice(product) {
      try {
        const variant = { ...product, source: 'product' }
        const priceData = await this.fetchProductPrice(variant)
        if (priceData && !priceData.undefined) {
          product.price = parseFloat(priceData.price_simple || 0)
          this.$set(product, 'price_initial', parseFloat(priceData.price_initial || 0))
          this.$set(product, 'unit_measurement_sale_conversion', parseFloat(priceData.unit_measurement_sale_conversion || 0) || null)
          product.unit_measurement = priceData.unit_measurement_sale || null
        } else {
          product.price = null
          this.$set(product, 'price_initial', null)
          this.$set(product, 'unit_measurement_sale_conversion', null)
          product.unit_measurement = null
        }
      } catch (error) {
        product.price = null
        this.$set(product, 'price_initial', null)
        this.$set(product, 'unit_measurement_sale_conversion', null)
        product.unit_measurement = null
      } finally {
        product.priceLoading = false
      }
    },

    forceSearchProducts() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }
      if (this.loading.products) {
        return
      }
      const searchTerm = this.trimmedProductSearch
      if (!searchTerm) {
        this.productsRequestId = 0
        this.arr.products = []
        this.loading.products = false
        this.productSearchHasExecuted = false
        return
      }
      if (searchTerm.length < getMinProductSearchChars(searchTerm)) {
        return
      }
      // Local: buscar en IndexedDB sólo al presionar Enter o botón.
      if (this.useLocalData) {
        this.productSearchHasExecuted = true
        this.loadProducts(true)
        return
      }
      this.loadProducts(true)
    },

    hasSelectedAvailableOption(options, selectedId) {
      if (!selectedId) return false
      return (options || []).some(option => option.value && String(option.value) === String(selectedId))
    },

    // ==========================================
    // Selección de cliente
    // ==========================================
    async selectCustomer(customer, zone = null) {
      this.crud.form.customers = customer
      this.crud.form.customers_id = customer.id
      this.crud.form.selected_zone = zone
      this.crud.form.erp_points_sales_id = 0
      this.crud.form.erp_points_sales = null
      this.crud.form.methods_payment_id = 0
      this.crud.form.methods_payment = null
      this.crud.form.price_list_id = 0
      this.crud.form.price_list = null

      // Asignar vendedor actual
      this.crud.form.sellers_id = this.getSeller.id

      // Usar datos ya cargados del cliente (tanto online como offline)
      // El endpoint de zonas ya trae data_fiscal y price_list.list
      const df = customer.data_fiscal
      if (df) {
        const psId = df.points_sales_id || df.erp_points_sales_id || (df.points_sales ? df.points_sales.id : 0)
        if (this.hasSelectedAvailableOption(this.pointsSalesOptions, psId)) {
          this.crud.form.erp_points_sales_id = psId
        }
        const mpId = df.methods_payment_id || (df.methods_payment ? df.methods_payment.id : 0)
        if (this.hasSelectedAvailableOption(this.methodsPaymentOptions, mpId)) {
          this.crud.form.methods_payment_id = mpId
          this.crud.form.methods_payment = df.methods_payment || null
        }
      }

      if (customer.price_list && customer.price_list.list && this.hasSelectedAvailableOption(this.priceListOptions, customer.price_list.list.id)) {
        this.crud.form.price_list = customer.price_list.list
        this.crud.form.price_list_id = customer.price_list.list.id
      }

      this.syncPriceListFromId()
      this.syncMethodsPaymentFromId()
      this.loadProducts()
      this.customerChange.active = false
      await this.saveDraftNow('customer-selected')
    },

    startCustomerChange() {
      this.customerChange.active = true
      this.search.customer = ''
      window.scrollTo(0, 0)
    },

    cancelCustomerChange() {
      this.customerChange.active = false
      this.search.customer = ''
    },

    onDuplicarPedido() {
      if (this.isEditingOrder) return
      this.modal.duplicateOrder = true
    },

    loadDuplicateOrders() {
      if (!this.crud.form.customers_id || !this.getSeller) return
      this.duplicateOrder.loading = true
      this.duplicateOrder.items = []
      this.duplicateOrder.loadedDetails = {}
      const filters = {
        customers: [{ code: this.crud.form.customers_id, label: this.crud.form.customers ? this.crud.form.customers.name : '' }],
        status: { code: 'closed', label: 'Cerrado' },
        date_start: moment().subtract(10, 'years').format('YYYY-MM-DD'),
        date_end: moment().format('YYYY-MM-DD'),
        source: 'product',
        typeOperations: 'wholesaler',
      }
      serviceAPI.filtrarPedido(filters, 1)
        .then((response) => {
          const paginate = response && response.data ? response.data : {}
          const items = (Array.isArray(paginate.data) ? paginate.data : [])
            .filter(item => item && item.status === 'closed')
            .slice(0, 5)
          this.duplicateOrder.items = items
          // Cargar detalle completo de cada pedido en paralelo (para mostrar productos en el modal)
          items.forEach(order => {
            this.$set(this.duplicateOrder.loadedDetails, order.id, undefined) // undefined = cargando
            serviceAPI.mostrarPedido(order.id)
              .then(resp => {
                this.$set(this.duplicateOrder.loadedDetails, order.id, resp && resp.data ? resp.data : null)
              })
              .catch(() => {
                this.$set(this.duplicateOrder.loadedDetails, order.id, null)
              })
          })
        })
        .catch(() => {
          this.$bvToast.toast('Error al cargar pedidos del cliente', { title: 'Error', variant: 'danger', solid: true })
        })
        .finally(() => {
          this.duplicateOrder.loading = false
        })
    },

    async selectOrderForDuplicate(order) {
      if (!order || !order.id || this.duplicateOrder.selecting) return
      this.duplicateOrder.selecting = true
      this.duplicateOrder.selectedId = order.id
      try {
        // Usar caché si ya está cargado, sino fetch
        const cached = this.duplicateOrder.loadedDetails[order.id]
        const orderData = (cached && cached.id) ? cached : (await serviceAPI.mostrarPedido(order.id)).data
        if (!orderData) throw new window.Error('No se pudo obtener el pedido')

        const detailRows = Array.isArray(order.details) ? order.details : []
        const detailFormatted = Array.isArray(orderData.detail)
          ? orderData.detail.map((detailItem, index) => this.mapOrderDetailForEdit(detailItem, detailRows[index]))
          : []

        // Detectar extras: mismo producto repetido → el de id más alto es el extra
        if (detailRows.length > 0) {
          const productIdMap = {}
          detailRows.forEach((row, index) => {
            const pid = row.products_id || row.products_colors_id || row.products_waist_id || row.products_color_waist_id
            if (!pid) return
            if (!productIdMap[pid]) productIdMap[pid] = []
            productIdMap[pid].push({ index, id: row.id })
          })
          Object.values(productIdMap).forEach(entries => {
            if (entries.length > 1) {
              const maxEntry = entries.reduce((max, e) => e.id > max.id ? e : max)
              if (detailFormatted[maxEntry.index]) detailFormatted[maxEntry.index].is_extra = true
            }
          })
        }

        // Tomar POS/pago/lista del pedido original (el cliente ya está seleccionado)
        this.crud.form.price_list_id = parseInt(orderData.price_list_id || (orderData.price_list ? orderData.price_list.id : 0), 10) || this.crud.form.price_list_id
        this.crud.form.erp_points_sales_id = parseInt(orderData.erp_points_sales_id || (orderData.points_sales ? orderData.points_sales.id : 0), 10) || this.crud.form.erp_points_sales_id
        this.crud.form.methods_payment_id = parseInt(orderData.methods_payment_id || (orderData.method_payment ? orderData.method_payment.id : 0), 10) || this.crud.form.methods_payment_id
        this.crud.form.detail = detailFormatted
        this.crud.form.observations = this.stripAutoDiscountObservations(orderData.observation || '')
        this.crud.form.reason_id = parseInt(orderData.reason_id || (orderData.reason ? orderData.reason.id : 0), 10) || 0

        this.syncPriceListFromId()
        this.syncMethodsPaymentFromId()
        this.search.product = ''
        this.arr.products = []

        this.modal.duplicateOrder = false
        this.currentStep = 3
        window.scrollTo(0, 0)

        // Refrescar precios en segundo plano (actualiza amount de cada ítem al precio vigente)
        this.$nextTick(() => this.refreshDuplicateItemPrices())

        this.$bvToast.toast('Pedido #' + order.id + ' cargado como base. Actualizando precios...', {
          title: 'Pedido duplicado',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        })
      } catch (error) {
        this.$bvToast.toast('Error al cargar el pedido', { title: 'Error', variant: 'danger', solid: true })
      } finally {
        this.duplicateOrder.selecting = false
        this.duplicateOrder.selectedId = 0
      }
    },

    async refreshDuplicateItemPrices() {
      const detail = this.crud.form.detail
      if (!detail || !detail.length) return
      const refreshedPrices = {}
      await Promise.all(detail.map(async (item) => {
        if (!item || !item.product || item.is_extra) return
        try {
          this.$set(item, 'priceLoading', true)
          const variant = { ...item.product, source: item.product.source || 'product' }
          const priceData = await this.fetchProductPrice(variant)
          if (priceData && !priceData.undefined) {
            const basePrice = parseFloat(priceData.price_simple || 0)
            // El renglón duplicado conserva su descuento: se aplica sobre el
            // precio vigente, igual que en los extras.
            const discountRate = (parseFloat(item.discount) || 0) / 100
            item.amount = this.roundItemAmount(basePrice * (1 - discountRate))
            this.$set(item, 'amount_original', basePrice)
            this.$set(item, 'price_initial', parseFloat(priceData.price_initial || 0))
            item.unit_measurement = priceData.unit_measurement_sale || item.unit_measurement
            const key = item.product.id + '_' + (item.product.source || 'product')
            refreshedPrices[key] = { amount: basePrice, price_initial: parseFloat(priceData.price_initial || 0) }
          }
        } catch (e) {
          // conservar precio viejo si falla
        } finally {
          this.$set(item, 'priceLoading', false)
        }
      }))
      // Sincronizar extras: aplicar su descuento sobre el precio base vigente del mismo producto
      detail.forEach(item => {
        if (!item || !item.product || !item.is_extra) return
        const key = item.product.id + '_' + (item.product.source || 'product')
        if (refreshedPrices[key]) {
          const newBasePrice = refreshedPrices[key].amount
          const discountRate = (parseFloat(item.discount) || 0) / 100
          item.amount = this.roundItemAmount(newBasePrice * (1 - discountRate))
          this.$set(item, 'amount_original', newBasePrice)
          this.$set(item, 'price_initial', refreshedPrices[key].price_initial)
        }
      })
    },

    onCobranza() {
      if (this.isEditingOrder) return
      this.cobranza.form.items = []
      this.cobranza.form.newItem = { type: 'Efectivo', amount: '', check: { type: 'de_terceros', bank: '', number: '', cuit: '', date_movement: '', observations: '' } }
      this.cobranza.form.observations = ''
      this.modal.cobranza = true
    },

    cobranzaAddItem() {
      const amount = parseFloat(this.cobranza.form.newItem.amount)
      if (!amount || amount <= 0) return
      const item = { type: this.cobranza.form.newItem.type, amount }
      if (item.type === 'Cheque') {
        item.check = { ...this.cobranza.form.newItem.check }
      }
      this.cobranza.form.items.push(item)
      this.cobranza.form.newItem.amount = ''
      if (this.cobranza.form.newItem.type === 'Cheque') {
        this.cobranza.form.newItem.check = { type: 'de_terceros', bank: '', number: '', cuit: '', date_movement: '', observations: '' }
      }
    },

    async saveCobranza() {
      if (!this.cobranza.form.items.length) return
      this.cobranza.loading = true
      try {
        const seller = Helper.getSeller()
        const totalAmount = this.cobranzaTotalValue
        const emptyCheck = { type: '', bank: '', number: '', observations: '', cuit: '', date_movement: '' }
        const payload = {
          date: moment().format('YYYY-MM-DD'),
          customers_id: this.crud.form.customers_id,
          sellers_id: seller ? seller.id : 0,
          staff_id: 0,
          observations: this.cobranza.form.observations,
          vouchers: [],
          amount_items: totalAmount,
          amount_vouchers: 0,
          detail: this.cobranza.form.items.map(item => ({
            type: item.type,
            amount: item.amount,
            check: item.check || emptyCheck,
          })),
        }
        const response = await serviceAPI.agregarCobranza(payload)
        this.modal.cobranza = false
        this.modal.cobranzaSuccess = true
        this.loadStatsHoy()
        if (response && response.data && response.data.id) {
          this.print.id = response.data.id
          this.print.key = Math.random()
        }
      } catch (error) {
        this.$bvToast.toast(Error.showError(error) || 'Error al guardar', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.cobranza.loading = false
      }
    },

    onObservaciones() {
      if (this.isEditingOrder) return
      this.observaciones.form.date = moment().format('YYYY-MM-DD')
      this.observaciones.form.observations = ''
      this.modal.observations = true
    },

    async loadStatsHoy() {
      const seller = Helper.getSeller()
      if (!seller) return
      const today = moment().format('YYYY-MM-DD')
      const filters = {
        date_start: today,
        date_end: today,
        sellers: [{ code: seller.id, label: seller.name || '' }],
      }
      try {
        const [resCobranzas, resObs] = await Promise.all([
          this.haveReportsPayment ? serviceAPI.filtrarCobranzasHoy(filters) : Promise.resolve(null),
          this.haveReportsNews    ? serviceAPI.filtrarObservacionesHoy(filters) : Promise.resolve(null),
        ])
        if (resCobranzas && resCobranzas.data && resCobranzas.data.data) {
          var items = resCobranzas.data.data
          this.statsHoy.cobranzas.count = items.length
          this.statsHoy.cobranzas.total = items.reduce(function(sum, r) { return sum + parseFloat(r.amounts_items || 0) }, 0)
        }
        if (resObs && resObs.data && resObs.data.data) {
          this.statsHoy.observaciones.count = resObs.data.data.length
        }
      } catch (e) {}
    },

    async saveObservaciones() {
      if (!this.observaciones.form.observations.trim()) return
      this.observaciones.loading = true
      try {
        const seller = Helper.getSeller()
        const payload = {
          date: this.observaciones.form.date,
          observations: this.observaciones.form.observations,
          customers_id: this.crud.form.customers_id,
          sellers_id: seller ? seller.id : 0,
          staff_id: 0,
          read: false,
        }
        await serviceAPI.agregarObservacion(payload)
        this.modal.observations = false
        this.modal.observacionesSuccess = true
        this.loadStatsHoy()
      } catch (error) {
        this.$bvToast.toast(Error.showError(error) || 'Error al guardar', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.observaciones.loading = false
      }
    },

    nextStepFromCustomer() {
      if (!this.crud.form.customers_id) {
        this.$bvToast.toast('Debe seleccionar un cliente', {
          title: 'Validación',
          variant: 'warning',
          solid: true
        })
        return
      }

      if (!this.hasSelectedAvailableOption(this.pointsSalesOptions, this.crud.form.erp_points_sales_id)) {
        this.$bvToast.toast('Debe seleccionar un punto de venta', {
          title: 'Validación',
          variant: 'warning',
          solid: true
        })
        return
      }

      if (!this.hasSelectedAvailableOption(this.methodsPaymentOptions, this.crud.form.methods_payment_id)) {
        this.$bvToast.toast('Debe seleccionar un medio de pago', {
          title: 'Validación',
          variant: 'warning',
          solid: true
        })
        return
      }

      if (!this.hasSelectedAvailableOption(this.priceListOptions, this.crud.form.price_list_id)) {
        this.$bvToast.toast('Debe seleccionar una lista de precios', {
          title: 'Validación',
          variant: 'warning',
          solid: true
        })
        return
      }

      this.nextStep()
    },

    // ==========================================
    // Gestión del carrito
    // ==========================================
    incrementQty(product) {
      if (!product.quantity) product.quantity = 1
      product.quantity++
      this.markActiveInputChanged()
      this.applyStockLimitToProduct(product, product.quantity)
    },

    decrementQty(product) {
      if (!product.quantity || product.quantity <= 1) {
        product.quantity = 1
      } else {
        product.quantity--
      }
      this.markActiveInputChanged()
    },

    async addToCart(product) {
      if (this.loading.addingProductId) {
        return
      }

      const quantity = product.quantity || 1
      const productDiscount = this.normalizeDiscountValue(product.discount)

      this.loading.addingProductId = product.id

      try {
        const productDetail = await this.fetchProductDetail(product.id)
        if (!productDetail) {
          this.$bvToast.toast('No se pudo obtener el detalle del producto', {
            title: 'Error',
            variant: 'danger',
            solid: true
          })
          return
        }

        const variant = this.selectProductVariant(productDetail)
        if (!variant) {
          this.$bvToast.toast('El producto tiene variantes. Usar la vista completa para seleccionarlas.', {
            title: 'Producto con variantes',
            variant: 'warning',
            solid: true
          })
          return
        }

        const priceData = await this.fetchProductPrice(variant)
        if (!priceData || priceData.undefined) {
          this.$bvToast.toast('No se pudo obtener el precio del producto', {
            title: 'Precio no disponible',
            variant: 'warning',
            solid: true
          })
          return
        }

        if (product && product.unit_measurement_purchase_id) {
          priceData.unit_measurement_purchase_id = product.unit_measurement_purchase_id
        }
        if (product && product.unit_measurement_sale_id && !priceData.unit_measurement_sale_id) {
          priceData.unit_measurement_sale_id = product.unit_measurement_sale_id
        }
        if (product && product.stock !== undefined) {
          priceData.stock = product.stock
        }

        const detailItem = this.buildDetailItem(priceData, quantity, productDiscount, this.getProductMaxDiscount(product))

        const existingIndex = this.crud.form.detail.findIndex(
          item => item.product && item.product.id === detailItem.product.id && item.product.source === detailItem.product.source
        )

        if (existingIndex >= 0) {
          const existingItem = this.crud.form.detail[existingIndex]
          const previousQuantity = parseFloat(existingItem.quantity || 0)
          const combinedItem = {
            ...existingItem,
            product: existingItem.product || detailItem.product,
            unit_measurement: existingItem.unit_measurement || detailItem.unit_measurement,
            quantity: previousQuantity + parseFloat(detailItem.quantity || 0)
          }

          const stockOk = this.applyStockLimitToItem(combinedItem)
          if (!stockOk) {
            product.quantity = combinedItem.quantity
            this.$forceUpdate()
            return
          }

          existingItem.quantity = combinedItem.quantity
          if (productDiscount > 0) {
            existingItem.discount = productDiscount
            this.applyItemDiscount(existingItem)
          }
          this.syncProductQuantityByCartItem(existingItem)
        } else {
          const stockOk = this.applyStockLimitToItem(detailItem)
          if (!stockOk) {
            product.quantity = detailItem.quantity
            this.$forceUpdate()
            return
          }
          if (detailItem.quantity <= 0) {
            product.quantity = detailItem.quantity
            this.$forceUpdate()
            return
          }
          this.crud.form.detail.push(detailItem)
          this.syncProductQuantityByCartItem(detailItem)
        }

        this.updateGeneralDiscount()
        product.quantity = existingIndex >= 0 ? this.crud.form.detail[existingIndex].quantity : detailItem.quantity

        if (product.extraEnabled && this.allowExtraDiscount) {
          const extraQty = product.extraQuantity || 1
          const extraItem = this.buildDetailItem(priceData, extraQty, this.extraDiscountPercentage, null)
          extraItem.is_extra = true
          this.crud.form.detail.push(extraItem)
        }

        this.saveDraftNow('cart-add')
      } catch (error) {
        console.error('Error agregando producto:', error)
        this.$bvToast.toast('Error al agregar producto', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.loading.addingProductId = null
      }
    },

    cartRemovalKey(item, index) {
      const productId = item && item.product ? item.product.id : 'unknown'
      return `${productId}:${index}`
    },

    isCartRemovalPending(item, index) {
      return this.pendingCartRemovalKey === this.cartRemovalKey(item, index)
    },

    requestCartRemoval(index) {
      const item = this.crud.form.detail[index]
      if (!item) return

      const key = this.cartRemovalKey(item, index)
      if (this.pendingCartRemovalKey === key) {
        if (this._cartRemovalTimer) {
          clearTimeout(this._cartRemovalTimer)
          this._cartRemovalTimer = null
        }
        this.pendingCartRemovalKey = null
        this.removeFromCart(index)
        return
      }

      this.pendingCartRemovalKey = key
      if (this._cartRemovalTimer) clearTimeout(this._cartRemovalTimer)
      this._cartRemovalTimer = setTimeout(() => {
        this.pendingCartRemovalKey = null
        this._cartRemovalTimer = null
      }, 3500)
    },

    removeFromCart(index) {
      const removed = this.crud.form.detail[index]
      this.crud.form.detail.splice(index, 1)
      if (removed && removed.product && removed.product.id) {
        this.resetProductQuantity(removed.product.id)
      }
      this.updateGeneralDiscount()
      this.saveDraftNow('cart-remove')
    },

    // Con P75 el neto conserva los seis decimales que guarda el pedido, para
    // que al facturarlo se reproduzca el precio final unitario mostrado.
    roundItemAmount(value) {
      return parseFloat((parseFloat(value) || 0).toFixed(this.parameters.salePriceCalc ? 6 : 2))
    },

    applyItemDiscount(item) {
      const base = parseFloat(item.amount_original || item.amount || 0)
      const discount = this.normalizeDiscountValue(item.discount)
      item.discount = discount
      item.amount = this.roundItemAmount(base - (base * discount / 100))
    },

    updateItemDiscount(item, event, commit = false) {
      const rawValue = event && event.target ? event.target.value : item.discount
      const discount = this.normalizeDiscountValue(rawValue)
      if (commit || !event) {
        item.discount = discount
      }
      const base = parseFloat(item.amount_original || item.amount || 0)
      item.amount = this.roundItemAmount(base - (base * discount / 100))
      this.updateGeneralDiscount()
    },

    updateProductDiscount(product, event, commit = false) {
      if (!commit && event) {
        return
      }
      const rawValue = event && event.target ? event.target.value : product.discount
      product.discount = this.normalizeDiscountValue(rawValue)
    },

    incrementCartItem(item) {
      if (!item.quantity) item.quantity = 1
      item.quantity++
      this.markActiveInputChanged()
      this.applyStockLimitToItem(item)
      this.syncProductQuantityByCartItem(item)
      this.updateGeneralDiscount()
      this.saveDraftNow('cart-quantity')
    },

    decrementCartItem(item) {
      if (!item.quantity || item.quantity <= 1) {
        item.quantity = 1
      } else {
        item.quantity--
      }
      this.markActiveInputChanged()
      this.applyStockLimitToItem(item)
      this.syncProductQuantityByCartItem(item)
      this.updateGeneralDiscount()
      this.saveDraftNow('cart-quantity')
    },

    normalizeCartItemQuantity(item) {
      if (!item.quantity || item.quantity < 1) {
        item.quantity = 1
      }
      this.applyStockLimitToItem(item)
      this.syncProductQuantityByCartItem(item)
      this.updateGeneralDiscount()
    },

    updateGeneralDiscount(event, commit = false) {
      const rawValue = event && event.target ? event.target.value : this.crud.form.percentage_discount
      const percentage = this.normalizeDiscountValue(rawValue)
      if (commit || !event) {
        this.crud.form.percentage_discount = percentage
      }
      this.crud.form.discounts_price = parseFloat(((this.getSubTotalOrder * percentage) / 100).toFixed(2))
    },

    // ==========================================
    // Guardar pedido
    // ==========================================
    save() {
      if (this.loading.saving) {
        return
      }

      if (!this.validateOrder()) {
        return
      }

      this.updateGeneralDiscount()
      const isEditing = this.isEditingOrder

      if (this.allowSaveWithoutConfirmation) {
        this.executeSave()
        return
      }

      this.confirmDialog(
        isEditing
          ? '¿Desea confirmar la actualización del pedido?'
          : '¿Desea confirmar la generación del pedido?',
        {
          title: isEditing ? 'Actualizar Pedido' : 'Confirmar Pedido',
          icon: 'check2-circle',
          okTitle: isEditing ? 'Actualizar' : 'Confirmar',
          footerClass: 'confirm-order-modal-footer'
        })
        .then(value => {
          if (value) {
            this.executeSave()
          }
        })
        .catch(() => {})
    },

    async executeSave() {
      if (this.loading.saving) {
        return
      }

      const activeEditOrderId = this.getActiveEditOrderId
      // Si se está reguardando un pedido de la cola, el tipo real (alta o
      // edición) es el del item encolado: una alta pendiente reguardada sigue
      // siendo un alta (id 0, con client_request_id), no una edición.
      const queueLocalId = this.editOrder.editingQueueLocalId
      const queueItem = queueLocalId
        ? OfflineManager.getQueue().find(i => i.localId === queueLocalId)
        : null
      const isEdit = queueItem ? !!queueItem.isEdit : this.isEditingOrder

      if (isEdit && !this.isOnline) {
        this.$bvToast.toast('Para guardar cambios en un pedido enviado necesitás conexión. Volvé a cargarlo cuando se recupere la red.', {
          title: 'Edición sin conexión',
          variant: 'warning',
          solid: true,
          autoHideDelay: 5000,
        })
        return
      }

      if (isEdit && !queueLocalId) {
        if (!activeEditOrderId || activeEditOrderId !== this.normalizeOrderId(this.crud.form.id)) {
          this.$bvToast.toast('No se pudo validar el pedido a editar. Volvé a cargarlo desde la lista antes de guardar.', {
            title: 'Edición inválida',
            variant: 'danger',
            solid: true
          })
          return
        }
      }

      this.loading.saving = true
      this.lastSavedWasEdit = isEdit
      // Un alta re-encolada conserva su client_request_id original: si algún
      // intento anterior llegó al servidor sin que se viera la respuesta, la
      // idempotencia evita duplicarlo.
      const clientRequestId = isEdit
        ? null
        : (queueItem
            ? ((queueItem.data && queueItem.data.client_request_id) || generateClientRequestId())
            : this.ensureClientRequestId())

      const normalizedPercentage = this.normalizeDiscountValue(this.crud.form.percentage_discount)
      const detailWithDiscount = this.crud.form.detail.map((item) => ({
        ...item,
        discount: this.normalizeDiscountValue(item.discount),
      }))
      const manualObservations = this.stripAutoDiscountObservations(this.crud.form.observations)
      this.crud.form.observations = manualObservations
      const maxDiscountNotes = this.buildMaxDiscountObservations()
      const mergedObservations = this.mergeObservations(manualObservations, maxDiscountNotes)

      const data = {
        id: queueItem ? this.normalizeOrderId(queueItem.data && queueItem.data.id) : (activeEditOrderId || 0),
        date: this.crud.form.date || moment().format('YYYY-MM-DD'),
        customers_id: this.crud.form.customers_id,
        sellers_id: this.crud.form.sellers_id,
        observations: mergedObservations,
        observations_internal: '',
        erp_points_sales_id: this.crud.form.erp_points_sales_id,
        methods_payment_id: this.crud.form.methods_payment_id,
        price_list_id: this.crud.form.price_list_id,
        detail: detailWithDiscount,
        is_retail: false,
        reason_id: this.crud.form.reason_id || null,
        discounts_price: this.crud.form.discounts_price || 0,
        percentage_discount: normalizedPercentage || 0,
        client_request_id: clientRequestId,
        // Se crea una sola vez al confirmar y la cola la conserva intacta
        // hasta que el pedido llegue al servidor.
        mobile_context: !isEdit
          ? ((queueItem && queueItem.data && queueItem.data.mobile_context) || this.buildMobileOrderContext())
          : null,
        // Marca el request como flujo mobile para el backend. En altas ya
        // alcanza con client_request_id, pero las ediciones no lo llevan y el
        // backend necesita distinguirlas para la notificación de confirmación.
        mobile_origin: true,
      }

      // === ALTAS: se encolan siempre (con o sin conexión) ===
      // Así el pedido queda a salvo en el momento exacto del guardado, sin
      // depender de que el POST llegue a destino con la conexión del vendedor.
      // Si hay conexión, se dispara la sincronización de inmediato para que
      // en el caso normal (buena señal) el envío sea prácticamente instantáneo.
      if (!isEdit) {
        if (queueLocalId) {
          OfflineManager.removeFromQueue(queueLocalId)
          this.editOrder.editingQueueLocalId = null
        }
        const queued = await this.enqueueOrderAndShowSuccess(data, { resaved: !!queueItem, silentFail: this.isOnline })
        if (queued) {
          if (this.isOnline) {
            this.processSyncQueue()
          }
          return
        }
        if (!this.isOnline) return
        // Fallback: el almacenamiento local no está disponible (IndexedDB
        // rota, sin espacio) pero hay conexión. El pedido va directo al
        // servidor como en la versión anterior: nunca debe quedar un
        // dispositivo online sin poder vender por un problema de storage.
        if (data.mobile_context) data.mobile_context.save_channel = 'direct_fallback'
        serviceAPI.agregarPedido(data)
          .then((response) => {
            this.loading.saving = false
            this.orderCreatedId = (response && response.data && response.data.id) || 0
            this.clearDraft()
            this.resetForm({ preserveSuccessState: true })
            this.currentStep = 1
            this.loadEditOrders(1, true)
            this.modal.success = true
            if (response && response.data && response.data.id) {
              LocationTracker.send({ order_id: response.data.id, source: 'order' })
            }
          })
          .catch((error) => {
            this.loading.saving = false
            const message = Error.showError(error) || 'Error al crear el pedido'
            this.$bvToast.toast(message, {
              title: 'Error',
              variant: 'danger',
              solid: true
            })
          })
        return
      }

      // === EDICIONES ===
      // Toda edición requiere conexión y se envía directamente al servidor.
      // Nunca se difiere para evitar aplicar cambios sobre un pedido que pudo
      // haber cambiado de estado remotamente.
      const request = serviceAPI.editarPedido(data)

      request
        .then((response) => {
          this.loading.saving = false
          // Si esta edición venía de la cola, el item viejo ya se aplicó: se
          // elimina para que el barrido no re-aplique la versión anterior.
          if (queueLocalId) {
            OfflineManager.removeFromQueue(queueLocalId)
            this.editOrder.editingQueueLocalId = null
            this.offline.pendingCount = OfflineManager.pendingCount()
          }
          this.orderCreatedId = (response && response.data && response.data.id)
            ? response.data.id
            : activeEditOrderId
          this.clearDraft()
          this.resetForm({ preserveSuccessState: true })
          this.currentStep = 1
          this.loadEditOrders(1, true)
          this.modal.success = true
          if (response && response.data && response.data.id) {
            LocationTracker.send({ order_id: response.data.id, source: 'order' })
          }
        })
        .catch((error) => {
          this.loading.saving = false
          console.error('Error actualizando pedido:', error)

          const message = Error.showError(error) || 'Error al actualizar el pedido'

          this.$bvToast.toast(message, {
            title: 'Error',
            variant: 'danger',
            solid: true
          })
        })
    },

    async enqueueOrderAndShowSuccess(data, extras = {}) {
      const queuedLocalId = await OfflineManager.addToQueue(data, false, extras)
      if (!queuedLocalId) {
        // silentFail: el caller tiene un plan B (envío directo online), no
        // mostrar el error de storage ni cortar el estado de guardado.
        if (extras.silentFail) return false
        this.loading.saving = false
        this.$bvToast.toast('No se pudo guardar el pedido offline. Liberá espacio del navegador o conectate a internet e intentá nuevamente.', {
          title: 'Pedido no guardado',
          variant: 'danger',
          solid: true
        })
        return false
      }
      this.offline.pendingCount = OfflineManager.pendingCount()
      this.offline.queued = true
      this.loading.saving = false
      this.clearDraft()
      this.resetForm({ preserveSuccessState: true })
      this.currentStep = 1
      this.modal.success = true
      return true
    },

    normalizeOrderId(value) {
      const normalized = parseInt(value, 10)
      return Number.isInteger(normalized) && normalized > 0 ? normalized : 0
    },

    validateOrder() {
      if (!this.crud.form.customers_id) {
        this.$bvToast.toast('Debe seleccionar un cliente', {
          title: 'Validación',
          variant: 'warning',
          solid: true
        })
        return false
      }

      if (!this.crud.form.detail.length) {
        this.$bvToast.toast('Debe agregar al menos un producto', {
          title: 'Validación',
          variant: 'warning',
          solid: true
        })
        return false
      }

      if (!this.hasSelectedAvailableOption(this.pointsSalesOptions, this.crud.form.erp_points_sales_id)) {
        this.$bvToast.toast('Falta cargar el punto de venta', {
          title: 'Validación',
          variant: 'warning',
          solid: true
        })
        return false
      }

      if (!this.hasSelectedAvailableOption(this.methodsPaymentOptions, this.crud.form.methods_payment_id)) {
        this.$bvToast.toast('Falta cargar el medio de pago', {
          title: 'Validación',
          variant: 'warning',
          solid: true
        })
        return false
      }

      if (!this.hasSelectedAvailableOption(this.priceListOptions, this.crud.form.price_list_id)) {
        this.$bvToast.toast('Falta cargar la lista de precios', {
          title: 'Validación',
          variant: 'warning',
          solid: true
        })
        return false
      }

      if (this.parameters.haveReason && !this.crud.form.reason_id) {
        this.$bvToast.toast('Debe seleccionar un motivo', {
          title: 'Validación',
          variant: 'warning',
          solid: true
        })
        return false
      }

      return true
    },

    resetForm(options = {}) {
      const { preserveSuccessState = false } = options

      this.crud.form = createEmptyOrderForm()
      this.orderContext.draftRecovered = false
      if (!preserveSuccessState) {
        this.lastSavedWasEdit = false
      }
      this.search.customer = ''
      this.search.product = ''
      this.arr.products = []
      this.productsRequestId = 0
      if (!preserveSuccessState) {
        this.orderCreatedId = 0
      }
      this.editOrder.activeOrderId = 0
      this.editOrder.selectedId = 0
      this.editOrder.editingQueueLocalId = null
      this.editOrder.search = ''
      this.loading.products = false
      this.loading.addingProductId = null
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
        this.searchTimer = null
      }
      this.cancelDraftSaveTimer()
      if (this.priceObserver) {
        this.priceObserver.disconnect()
        this.priceObserver = null
      }
    },

    async fetchProductDetail(productId) {
      if (this.useLocalData) {
        // eslint-disable-next-line eqeqeq
        const visible = this.arr.products.find(p => p.id == productId)
        if (visible) return visible
        return OfflineManager.getProduct(this.getSeller.id, productId)
      }
      const data = {
        where: [
          { field: 'm_products.active', condition: true }
        ]
      }
      const response = await serviceAPI.mostrarProductoControl(productId, data)
      return response.data
    },

    selectProductVariant(productDetail) {
      if ((productDetail.colors_waist && productDetail.colors_waist.length) ||
          (productDetail.colors && productDetail.colors.length) ||
          (productDetail.waist && productDetail.waist.length)) {
        return null
      }

      const variant = { ...productDetail }
      variant.source = 'product'
      return variant
    },

    async fetchProductPrice(productVariant) {
      const customerId = this.crud.form.customers_id || null
      const priceListId = this.crud.form.price_list_id || null

      // Modo local: usar exclusivamente el precio base de la lista
      // sincronizada. Los acuerdos particulares requieren backend activo.
      if (this.useLocalData) {
        if (priceListId && productVariant._offlinePrices) {
          const embedded = productVariant._offlinePrices[priceListId]
          if (embedded) {
            const priceList = (this.arr.priceLists || []).find(pl => pl.id === priceListId)
            return {
              ...productVariant,
              source: 'product',
              price_initial: parseFloat(embedded.amount_initial || 0),
              price_simple: parseFloat(embedded.amount_total || 0),
              list: priceList || { id: priceListId },
              undefined: false,
            }
          }
        }
        return null
      }

      // Modo online: obtener el precio vigente del backend.
      const data = {
        product: productVariant,
        customer_id: customerId,
        price_list_id: priceListId,
        reason_id: this.crud.form.reason_id || null
      }

      const response = await serviceAPI.obtenerPrecioProducto(data)
      let result
      if (Array.isArray(response.data)) {
        const firstValid = response.data.find(item => item && !item.undefined && item.amount)
        result = firstValid || response.data[0] || null
      } else {
        result = response.data
      }

      return result
    },

    buildDetailItem(priceData, quantity, discount = 0, maxDiscount = null) {
      const amountOriginal = parseFloat(priceData.price_simple || 0)
      if (!priceData.source) priceData.source = 'product'
      const detailItem = {
        product: priceData,
        quantity: quantity,
        quantityOld: quantity,
        currency: priceData.list ? priceData.list.currency : null,
        amount_original: amountOriginal,
        amount: amountOriginal,
        discount: discount,
        unit_measurement: priceData.unit_measurement_sale || null,
        price_list: priceData.list || null,
        max_discount: maxDiscount,
        is_extra: false,
      }
      if (detailItem.product && maxDiscount !== null && maxDiscount !== undefined) {
        detailItem.product.max_discount = maxDiscount
      }
      this.applyItemDiscount(detailItem)
      return detailItem
    },

    // ==========================================
    // Utilidades
    // ==========================================
    normalizeDiscountValue(value) {
      if (value === null || value === undefined || value === '') {
        return 0
      }
      const normalized = String(value).replace(',', '.')
      let parsed = parseFloat(normalized)
      if (isNaN(parsed) || parsed < 0) parsed = 0
      if (parsed > 100) parsed = 100
      return parsed
    },

    normalizeNumber(value, fallback = 0) {
      if (value === null || value === undefined || value === '') {
        return fallback
      }
      const normalized = String(value).replace(',', '.')
      const parsed = parseFloat(normalized)
      if (isNaN(parsed)) {
        return fallback
      }
      return parsed
    },

    formatPercent(value) {
      if (value === null || value === undefined || value === '') return '0%'
      const parsed = parseFloat(value)
      if (isNaN(parsed)) return '0%'
      return Intl.NumberFormat('es-AR', { style: 'percent', maximumFractionDigits: 2 }).format(parsed / 100)
    },

    formatStock(value) {
      if (value === null || value === undefined || value === '') return ''
      const parsed = parseFloat(value)
      if (isNaN(parsed)) return ''
      if (Number.isInteger(parsed)) return String(parsed)
      return parsed.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    syncInputValue(event, value) {
      const target = event && event.target
      if (!target) return
      const formatted = value === null || value === undefined ? '' : String(value)
      if (String(target.value) === formatted) return
      this.$nextTick(() => {
        if (target) target.value = formatted
      })
    },

    getProductMaxDiscount(product) {
      if (!product) return null
      const raw = product.max_discount
      if (raw === null || raw === undefined || raw === '') return null
      const parsed = parseFloat(raw)
      if (isNaN(parsed) || parsed < 0) return null
      return parsed
    },

    getItemMaxDiscount(item) {
      if (!item) return null
      if (item.max_discount !== null && item.max_discount !== undefined && item.max_discount !== '') {
        const parsed = parseFloat(item.max_discount)
        if (!isNaN(parsed) && parsed >= 0) return parsed
      }
      if (item.product && item.product.max_discount !== undefined && item.product.max_discount !== null && item.product.max_discount !== '') {
        const parsed = parseFloat(item.product.max_discount)
        if (!isNaN(parsed) && parsed >= 0) return parsed
      }
      return null
    },

    isDiscountExceeded(maxDiscount, discount) {
      if (maxDiscount === null || maxDiscount === undefined) return false
      const max = parseFloat(maxDiscount)
      if (isNaN(max) || max < 0) return false
      const current = this.normalizeDiscountValue(discount)
      return current > max
    },

    isProductDiscountExceeded(product) {
      if (!product) return false
      const max = this.getProductMaxDiscount(product)
      return this.isDiscountExceeded(max, product.discount)
    },

    isCartItemDiscountExceeded(item) {
      const max = this.getItemMaxDiscount(item)
      return this.isDiscountExceeded(max, item.discount)
    },

    notifyDiscountIfExceeded(product) {
      if (!this.isProductDiscountExceeded(product)) return
      const max = this.getProductMaxDiscount(product)
      const current = this.normalizeDiscountValue(product.discount)
      this.$bvToast.toast(`El descuento asignado supera el máximo (${current}% > ${max}%)`, {
        title: 'Descuento máximo',
        variant: 'danger',
        solid: true,
        autoHideDelay: 3000
      })
    },

    notifyCartItemDiscountIfExceeded(item) {
      if (!this.isCartItemDiscountExceeded(item)) return
      const max = this.getItemMaxDiscount(item)
      const current = this.normalizeDiscountValue(item.discount)
      this.$bvToast.toast(`El descuento asignado supera el máximo (${current}% > ${max}%)`, {
        title: 'Descuento máximo',
        variant: 'danger',
        solid: true,
        autoHideDelay: 3000
      })
    },

    buildMaxDiscountObservations() {
      if (!this.crud.form.detail || !this.crud.form.detail.length) {
        return ''
      }
      const lines = []
      this.crud.form.detail.forEach(item => {
        const max = this.getItemMaxDiscount(item)
        if (!max) return
        const current = this.normalizeDiscountValue(item.discount)
        if (current > max) {
          const name = item.product && item.product.name ? item.product.name : 'Producto'
          lines.push(`Producto ${name}: descuento máximo ${max}% - descuento asignado ${current}%`)
        }
      })
      if (!lines.length) return ''
      return `ALERTA DESCUENTO MAXIMO:\n${lines.join('\n')}`
    },

    stripAutoDiscountObservations(value) {
      const text = value ? String(value) : ''
      if (!text) return ''
      const lines = text.split(/\r?\n/)
      const markerIndex = lines.findIndex(line => String(line).trim() === 'ALERTA DESCUENTO MAXIMO:')
      if (markerIndex === -1) {
        return text.trim()
      }
      return lines.slice(0, markerIndex).join('\n').trim()
    },

    mergeObservations(base, extra) {
      const baseText = base ? String(base).trim() : ''
      const extraText = extra ? String(extra).trim() : ''
      if (!extraText) return baseText
      if (!baseText) return extraText
      return `${baseText}\n${extraText}`
    },

    isCoarsePointer() {
      if (typeof window === 'undefined') return false
      if (typeof window.matchMedia === 'function') {
        return window.matchMedia('(pointer: coarse)').matches
      }
      if (typeof navigator !== 'undefined' && navigator.maxTouchPoints) {
        return navigator.maxTouchPoints > 0
      }
      return 'ontouchstart' in window
    },

    handleMobileInputFocus(event, model, field) {
      if (!this.isCoarsePointer()) {
        this.selectAllInput(event)
        return
      }
      const target = event && event.target
      if (!target) return
      target.__mobilePrevValue = model ? model[field] : ''
      target.__mobileChanged = false
      target.__mobileCleared = true
      target.__mobileRestored = false
      this.mobileClearedInput = target
      if (model) {
        model[field] = ''
      }
      this.$nextTick(() => {
        if (target) target.value = ''
      })
    },

    handleMobileInputChange(event) {
      const target = event && event.target
      if (target && target.__mobileCleared) {
        target.__mobileChanged = true
        target.__mobileRestored = false
      }
    },

    handleMobileInputCommit(event) {
      const target = event && event.target
      if (target && target.__mobileCleared && target.value !== '') {
        target.__mobileChanged = true
        target.__mobileRestored = false
      }
    },

    handleMobileInputBlur(event, model, field) {
      if (!this.isCoarsePointer()) return
      const target = event && event.target
      if (!target || !target.__mobileCleared) return
      const rawValue = target.value
      if (rawValue !== undefined && rawValue !== null && String(rawValue) !== '') {
        target.__mobileChanged = true
        if (model && (model[field] === '' || model[field] === null || model[field] === undefined)) {
          model[field] = rawValue
        }
      }
      if (!target.__mobileChanged) {
        const previousValue = target.__mobilePrevValue
        if (previousValue !== undefined && model) {
          model[field] = previousValue
        }
        target.__mobileRestored = true
      } else {
        target.__mobileRestored = false
      }
      target.__mobileCleared = false
      if (this.mobileClearedInput === target) {
        this.mobileClearedInput = null
      }
    },

    shouldSkipMobileCommit(event) {
      if (!this.isCoarsePointer()) return false
      const target = event && event.target
      if (target && target.__mobileRestored) {
        target.__mobileRestored = false
        return true
      }
      return false
    },

    markActiveInputChanged() {
      if (!this.isCoarsePointer()) return
      const target = this.mobileClearedInput
      if (target && target.__mobileCleared) {
        target.__mobileChanged = true
      }
    },

    onProductDiscountInput(product, event) {
      this.handleMobileInputChange(event)
      this.updateProductDiscount(product, event)
    },

    onProductDiscountChange(product, event) {
      if (this.shouldSkipMobileCommit(event)) return
      this.handleMobileInputCommit(event)
      this.updateProductDiscount(product, event, true)
      this.notifyDiscountIfExceeded(product)
      this.saveDraftNow('product-discount')
    },

    onCartQuantityInput(item, event) {
      this.handleMobileInputChange(event)
      this.normalizeCartItemQuantity(item)
      this.syncInputValue(event, item.quantity)
      this.saveDraftNow('cart-quantity')
    },

    onCartQuantityChange(item, event) {
      if (this.shouldSkipMobileCommit(event)) return
      this.handleMobileInputCommit(event)
      this.normalizeCartItemQuantity(item)
      this.syncInputValue(event, item.quantity)
    },

    onProductQuantityInput(product, event) {
      this.handleMobileInputChange(event)
      this.applyStockLimitToProduct(product, parseFloat(product.quantity || 0))
      this.syncInputValue(event, product.quantity)
    },

    onProductQuantityChange(product, event) {
      if (this.shouldSkipMobileCommit(event)) return
      this.handleMobileInputCommit(event)
      this.applyStockLimitToProduct(product, parseFloat(product.quantity || 0))
      this.syncInputValue(event, product.quantity)
    },

    onCartDiscountInput(item, event) {
      this.handleMobileInputChange(event)
      this.updateItemDiscount(item, event)
    },

    onCartDiscountChange(item, event) {
      if (this.shouldSkipMobileCommit(event)) return
      this.handleMobileInputCommit(event)
      this.updateItemDiscount(item, event, true)
      this.notifyCartItemDiscountIfExceeded(item)
      this.saveDraftNow('cart-discount')
    },

    getOrdersConfigBoolean(id, defaultValue = false) {
      if (!this.arr || !Array.isArray(this.arr.configurations)) {
        return defaultValue
      }
      const config = this.arr.configurations.find(item => parseInt(item.id, 10) === parseInt(id, 10))
      if (!config || config.value_boolean === undefined || config.value_boolean === null) {
        return defaultValue
      }
      return this.normalizeStoredBoolean(config.value_boolean)
    },

    getOrdersConfigNumber(id, defaultValue = 0) {
      if (!this.arr || !Array.isArray(this.arr.configurations)) {
        return defaultValue
      }
      const config = this.arr.configurations.find(item => parseInt(item.id, 10) === parseInt(id, 10))
      if (!config || config.value_number === undefined || config.value_number === null) {
        return defaultValue
      }
      return parseFloat(config.value_number)
    },

    isStockValidationEnabled() {
      // Config ID 3: Permite agregar items sin stock
      const allowWithoutStock = this.getOrdersConfigBoolean(3, false)
      return !allowWithoutStock
    },

    getUnitMeasurementIdFromProduct(product) {
      if (!product) return null
      if (product.unit_measurement && product.unit_measurement.id) {
        return product.unit_measurement.id
      }
      if (product.unit_measurement_sale_id) {
        return product.unit_measurement_sale_id
      }
      if (product.unit_measurement_sale && product.unit_measurement_sale.id) {
        return product.unit_measurement_sale.id
      }
      return null
    },

    getUnitMeasurementIdFromItem(item) {
      if (!item) return null
      if (item.unit_measurement && item.unit_measurement.id) {
        return item.unit_measurement.id
      }
      if (item.product && item.product.unit_measurement_sale_id) {
        return item.product.unit_measurement_sale_id
      }
      if (item.product && item.product.unit_measurement_sale && item.product.unit_measurement_sale.id) {
        return item.product.unit_measurement_sale.id
      }
      return null
    },

    shouldValidateStock(purchaseUnitId, unitMeasurementId) {
      if (!this.isStockValidationEnabled()) {
        return false
      }
      if (!purchaseUnitId || !unitMeasurementId) {
        return false
      }
      return parseInt(purchaseUnitId, 10) === parseInt(unitMeasurementId, 10)
    },

    getProductStock(product) {
      if (!product || product.stock === undefined || product.stock === null || product.stock === '') {
        return null
      }
      const parsed = parseFloat(product.stock)
      return isNaN(parsed) ? null : parsed
    },

    getItemStock(item) {
      if (!item) return null
      if (item.product && item.product.stock !== undefined && item.product.stock !== null && item.product.stock !== '') {
        const parsed = parseFloat(item.product.stock)
        return isNaN(parsed) ? null : parsed
      }
      if (item.stock !== undefined && item.stock !== null && item.stock !== '') {
        const parsed = parseFloat(item.stock)
        return isNaN(parsed) ? null : parsed
      }
      return null
    },

    notifyStockInsufficient(stock) {
      const formattedStock = this.formatStock(stock)
      const message = formattedStock ? `Stock insuficiente. Stock máximo: ${formattedStock}` : 'Stock insuficiente'
      this.$bvToast.toast(message, {
        title: 'Stock',
        variant: 'warning',
        solid: true,
        autoHideDelay: 3000
      })
    },

    applyStockLimitToProduct(product, quantity) {
      const purchaseUnitId = product ? product.unit_measurement_purchase_id : null
      const unitMeasurementId = this.getUnitMeasurementIdFromProduct(product)
      if (!this.shouldValidateStock(purchaseUnitId, unitMeasurementId)) {
        return true
      }
      const stock = this.getProductStock(product)
      if (stock === null) {
        return true
      }
      if (quantity > stock) {
        product.quantity = stock
        this.notifyStockInsufficient(stock)
        return false
      }
      return true
    },

    applyStockLimitToItem(item) {
      const purchaseUnitId = item && item.product ? item.product.unit_measurement_purchase_id : null
      const unitMeasurementId = this.getUnitMeasurementIdFromItem(item)
      if (!this.shouldValidateStock(purchaseUnitId, unitMeasurementId)) {
        return true
      }
      const stock = this.getItemStock(item)
      if (stock === null) {
        return true
      }
      if (item.quantity > stock) {
        item.quantity = stock
        this.notifyStockInsufficient(stock)
        return false
      }
      return true
    },

    syncProductQuantityByCartItem(item) {
      if (!item || !item.product || !item.product.id) return
      const product = this.arr.products.find(p => p.id === item.product.id)
      if (product) {
        product.quantity = item.quantity
      }
    },

    resetProductQuantity(productId) {
      if (!productId) return
      const product = this.arr.products.find(p => p.id === productId)
      if (product) {
        product.quantity = 1
      }
    },

    onGeneralDiscountInput(event) {
      this.handleMobileInputChange(event)
      this.updateGeneralDiscount(event)
    },

    onGeneralDiscountChange(event) {
      if (this.shouldSkipMobileCommit(event)) return
      this.handleMobileInputCommit(event)
      this.updateGeneralDiscount(event, true)
      this.saveDraftNow('general-discount')
    },

    selectAllInput(event) {
      const target = event && event.target
      if (target && typeof target.select === 'function') {
        this.$nextTick(() => {
          target.select()
        })
      }
    },

    formatPrice(value) {
      if (!value) return '0.00'
      return parseFloat(value).toLocaleString('es-AR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    // Imágenes de producto removidas de este pedido móvil (con y sin
    // conexión): sin límite de resultados de búsqueda ni lazy loading, eran
    // una causa real de quedarse sin memoria (pantalla en blanco) en
    // celulares gama media/baja al scrollear listas largas.

    getProductCategoryName(product) {
      if (product && product.subcategory && product.subcategory.category && product.subcategory.category.name) {
        return product.subcategory.category.name
      }
      return 'Sin categoría'
    },

    getProductSubcategoryName(product) {
      if (product && product.subcategory && product.subcategory.name) {
        return product.subcategory.name
      }
      return 'Sin subcategoría'
    },

    isProductInCart(product) {
      if (!product || !product.id) return false
      return this.addedProductIdSet.has(product.id)
    },

    getCategoryAddedCount(category) {
      if (!category || !Array.isArray(category.subcategories)) {
        return 0
      }
      let total = 0
      category.subcategories.forEach(subcategory => {
        if (!subcategory || !Array.isArray(subcategory.products)) return
        subcategory.products.forEach(product => {
          if (product && this.addedProductIdSet.has(product.id)) {
            total += 1
          }
        })
      })
      return total
    },

    getSubcategoryAddedCount(subcategory) {
      if (!subcategory || !Array.isArray(subcategory.products)) {
        return 0
      }
      let total = 0
      subcategory.products.forEach(product => {
        if (product && this.addedProductIdSet.has(product.id)) {
          total += 1
        }
      })
      return total
    },

    openConversionModal(product) {
      const normalized = (product.price === undefined && product.price_simple !== undefined)
        ? { ...product, price: parseFloat(product.price_simple || 0) }
        : product
      const saleUnit = this.getProductUnitMeasurement(normalized)
      const purchaseId = normalized.unit_measurement_purchase_id
      const purchaseUnit = (purchaseId && this.arr.unitMeasurementsMap[purchaseId])
        ? this.arr.unitMeasurementsMap[purchaseId].reference
        : (normalized.unit_measurement_purchase && normalized.unit_measurement_purchase.reference) || ''
      this.conversionModal.product = normalized
      this.conversionModal.saleUnit = saleUnit
      this.conversionModal.purchaseUnit = purchaseUnit
      this.conversionModal.show = true
    },

    getProductUnitMeasurement(product) {
      if (!product) return ''
      if (product.unit_measurement && product.unit_measurement.reference) {
        return product.unit_measurement.reference
      }
      if (product.unit_measurement_sale && product.unit_measurement_sale.reference) {
        return product.unit_measurement_sale.reference
      }
      const id = product.unit_measurement_sale_id
      if (id && this.arr.unitMeasurementsMap[id]) {
        return this.arr.unitMeasurementsMap[id].reference
      }
      return ''
    },

    getProductPurchaseUnitLabel(product) {
      if (!product) return ''
      const purchaseId = product.unit_measurement_purchase_id
      if (purchaseId && this.arr.unitMeasurementsMap[purchaseId]) {
        return this.arr.unitMeasurementsMap[purchaseId].reference
      }
      if (product.unit_measurement_purchase && product.unit_measurement_purchase.reference) {
        return product.unit_measurement_purchase.reference
      }
      return this.getProductUnitMeasurement(product)
    },

    // ==========================================
    // Sistema de borrador/respaldo
    // ==========================================
    cancelDraftSaveTimer() {
      if (this.draftSaveTimer) {
        clearTimeout(this.draftSaveTimer)
        this.draftSaveTimer = null
      }
    },

    ensureClientRequestId() {
      if (this.isEditingOrder) {
        return null
      }
      if (!this.crud.form.client_request_id) {
        this.crud.form.client_request_id = generateClientRequestId()
      }
      return this.crud.form.client_request_id
    },

    buildMobileOrderContext() {
      const seller = this.getSeller
      const lastSync = seller && seller.id ? OfflineManager.getLastSync(seller.id) : null
      const syncTimestamp = lastSync ? new Date(lastSync) : null
      let appVersion = null
      try {
        // La versión se guarda con el wrapper Storage (prefijada con el
        // origin en main.js), no como clave literal en localStorage.
        appVersion = Storage.getValue('AppBuildVersion') || null
      } catch (error) {
        // El navegador puede bloquear localStorage; el resto del contexto
        // sigue siendo válido para diagnosticar el pedido.
      }

      return {
        app_version: appVersion,
        created_at_device: new Date().toISOString(),
        data_source: this.useLocalData ? 'downloaded' : 'server',
        backend_available: !!this.isOnline,
        last_data_sync_at: syncTimestamp && !isNaN(syncTimestamp.getTime()) ? syncTimestamp.toISOString() : null,
        data_age_minutes: syncTimestamp && !isNaN(syncTimestamp.getTime())
          ? Math.max(0, Math.floor((Date.now() - syncTimestamp.getTime()) / 60000))
          : null,
        draft_recovered: !!this.orderContext.draftRecovered,
        save_channel: 'queue',
      }
    },

    buildDraftData(reason = 'auto') {
      // Una edición nunca se respalda para recuperar más tarde: al reabrirla
      // debe leerse otra vez desde el servidor y validar su estado vigente.
      if (this.isEditingOrder) return null
      if (!this.crud.form.customers_id && !this.crud.form.detail.length) return null

      return {
        timestamp: Date.now(),
        reason,
        currentStep: this.currentStep,
        draft_recovered: !!this.orderContext.draftRecovered,
        form: {
          id: this.crud.form.id || 0,
          client_request_id: this.ensureClientRequestId(),
          edit_order_id: this.editOrder.activeOrderId || 0,
          date: this.crud.form.date,
          customers_id: this.crud.form.customers_id,
          customers: this.crud.form.customers,
          selected_zone: this.crud.form.selected_zone,
          erp_points_sales_id: this.crud.form.erp_points_sales_id,
          erp_points_sales: this.selectedPointSale,
          methods_payment_id: this.crud.form.methods_payment_id,
          methods_payment: this.crud.form.methods_payment,
          price_list_id: this.crud.form.price_list_id,
          sellers_id: this.crud.form.sellers_id,
          detail: this.crud.form.detail,
          observations: this.crud.form.observations,
          reason_id: this.crud.form.reason_id,
          discounts_price: this.crud.form.discounts_price,
          percentage_discount: this.crud.form.percentage_discount,
          price_list: this.crud.form.price_list,
        }
      }
    },

    getDraftFallbackStorageKey() {
      const seller = this.getSeller
      return seller && seller.id ? `${DRAFT_FALLBACK_STORAGE_PREFIX}_${seller.id}` : null
    },

    removeDraftFallback() {
      const storageKey = this.getDraftFallbackStorageKey()
      if (!storageKey) return
      try {
        localStorage.removeItem(storageKey)
      } catch (error) {
        // El fallback ya no está disponible o el navegador bloqueó localStorage.
      }
    },

    saveDraftFallback(draftData) {
      const storageKey = this.getDraftFallbackStorageKey()
      if (!storageKey) return false
      try {
        localStorage.setItem(storageKey, JSON.stringify(draftData))
        return true
      } catch (error) {
        return false
      }
    },

    async saveDraftNow(reason = 'auto') {
      const seller = this.getSeller
      const draftData = this.buildDraftData(reason)
      if (!seller || !seller.id || !draftData) return null

      try {
        const saved = await DraftStorage.save(seller.id, draftData)
        // IndexedDB es el único guardado normal. Si antes hubo un fallback,
        // se elimina recién después de confirmar la escritura principal.
        this.removeDraftFallback()
        this.draft.snapshots = saved ? [saved] : []
        this.draft.timestamp = saved ? saved.savedAt : null
        return saved
      } catch (error) {
        console.error('Error guardando borrador en IndexedDB:', error)
        // Fallback excepcional para navegadores donde IndexedDB no funciona.
        const fallbackSaved = this.saveDraftFallback(draftData)
        if (fallbackSaved) {
          const fallbackRecord = { id: 'local-fallback', savedAt: Date.now(), data: draftData }
          this.draft.snapshots = [fallbackRecord]
          this.draft.timestamp = fallbackRecord.savedAt
        }
        this.$bvToast.toast(fallbackSaved
          ? 'El pedido quedó respaldado de forma limitada. Verificá el almacenamiento del dispositivo.'
          : 'No se pudo respaldar el pedido en el dispositivo. Liberá espacio e intentá nuevamente.', {
          title: 'Respaldo pendiente', variant: 'warning', solid: true, autoHideDelay: 5000
        })
        return fallbackSaved ? { id: 'local-fallback', savedAt: Date.now() } : null
      }
    },

    saveDraftDebounced() {
      this.cancelDraftSaveTimer()
      this.draftSaveTimer = setTimeout(() => {
        this.draftSaveTimer = null
        this.saveDraftNow('auto')
      }, DRAFT_SAVE_DELAY)
    },

    loadDraftFallback() {
      const storageKey = this.getDraftFallbackStorageKey()
      if (!storageKey) return null
      try {
        const saved = localStorage.getItem(storageKey)
        if (!saved) return null
        return JSON.parse(saved)
      } catch (e) {
        console.error('Error cargando borrador:', e)
        return null
      }
    },

    async loadDraftSnapshots() {
      const seller = this.getSeller
      if (!seller || !seller.id) return
      this.cancelDraftSaveTimer()
      try {
        let snapshots = await DraftStorage.list(seller.id)
        const fallback = this.loadDraftFallback()
        const fallbackSellerId = fallback && fallback.form ? parseInt(fallback.form.sellers_id, 10) : 0
        const fallbackTimestamp = fallback ? parseInt(fallback.timestamp, 10) || 0 : 0
        const indexedTimestamp = snapshots.length ? parseInt(snapshots[0].savedAt, 10) || 0 : 0
        const hasCurrentFallback = fallback && fallback.form &&
          (!fallbackSellerId || fallbackSellerId === parseInt(seller.id, 10))

        if (hasCurrentFallback && (!snapshots.length || fallbackTimestamp > indexedTimestamp)) {
          try {
            const recovered = await DraftStorage.save(seller.id, fallback)
            if (recovered) {
              this.removeDraftFallback()
              snapshots = await DraftStorage.list(seller.id)
            }
          } catch (error) {
            // IndexedDB no disponible: el backup local sigue siendo recuperable.
            snapshots = [{ id: 'local-fallback', savedAt: fallbackTimestamp || Date.now(), data: fallback }]
          }
        } else if (hasCurrentFallback && snapshots.length) {
          this.removeDraftFallback()
        }

        // Las versiones anteriores podían persistir una edición del servidor.
        // Como ese estado puede quedar obsoleto, se descarta silenciosamente
        // antes de ofrecer cualquier recuperación.
        if (snapshots.some(snapshot => this.isServerEditDraft(snapshot))) {
          this.removeDraftFallback()
          await DraftStorage.clearSeller(seller.id)
          snapshots = []
        }

        this.draft.snapshots = snapshots
        this.draft.timestamp = snapshots.length ? snapshots[0].savedAt : null
        if (snapshots.length) this.draft.showRecoveryModal = true
      } catch (error) {
        console.error('Error cargando borradores:', error)
      }
    },

    async clearDraft() {
      this.cancelDraftSaveTimer()
      const seller = this.getSeller
      this.removeDraftFallback()
      if (seller && seller.id) {
        await DraftStorage.clearSeller(seller.id)
      }
      this.draft.timestamp = null
      this.draft.snapshots = []
    },

    isServerEditDraft(snapshot) {
      const draftData = snapshot && snapshot.data ? snapshot.data : snapshot
      const form = draftData && draftData.form ? draftData.form : null
      if (!form) return false
      return this.normalizeOrderId(form.edit_order_id || form.id) > 0
    },

    getPendingDraftData() {
      const selected = this.draft.snapshots[0]
      return selected && selected.data ? selected.data : selected
    },

    restoreDraft(draftData) {
      if (!draftData || !draftData.form) return false

      // Los borradores de ediciones antiguas no son seguros: el pedido pudo
      // cambiar de estado o ser modificado en el servidor desde su guardado.
      if (this.isServerEditDraft(draftData)) return false

      // Restaurar el formulario. A esta altura solo puede ser un alta nueva:
      // cualquier borrador con id de pedido fue descartado arriba.
      this.editOrder.activeOrderId = 0
      this.editOrder.editingQueueLocalId = null
      this.crud.form.id = 0
      this.crud.form.client_request_id = draftData.form.client_request_id || generateClientRequestId()
      this.crud.form.date = draftData.form.date || ''
      this.crud.form.customers_id = draftData.form.customers_id || 0
      this.crud.form.customers = draftData.form.customers || null
      this.crud.form.selected_zone = draftData.form.selected_zone || null
      this.crud.form.erp_points_sales_id = draftData.form.erp_points_sales_id || 0
      this.crud.form.methods_payment_id = draftData.form.methods_payment_id || 0
      this.crud.form.methods_payment = draftData.form.methods_payment || null
      this.crud.form.price_list_id = draftData.form.price_list_id || 0
      this.crud.form.price_list = draftData.form.price_list || null
      this.crud.form.sellers_id = draftData.form.sellers_id || this.getSeller.id
      this.crud.form.detail = draftData.form.detail || []
      this.crud.form.observations = this.stripAutoDiscountObservations(draftData.form.observations || '')
      this.crud.form.reason_id = draftData.form.reason_id || 0
      this.crud.form.discounts_price = draftData.form.discounts_price || 0
      this.crud.form.percentage_discount = draftData.form.percentage_discount || 0
      this.orderContext.draftRecovered = !!draftData.draft_recovered

      // Al recuperar, mostrar siempre el pedido completo para que el usuario
      // revise cliente, productos y totales antes de continuar o confirmarlo.
      this.currentStep = 3
      this.syncPriceListFromId()
      this.syncMethodsPaymentFromId()
      return true
    },

    recoverDraft() {
      const draftData = this.getPendingDraftData()
      if (!this.restoreDraft(draftData)) {
        this.draft.showRecoveryModal = false
        return
      }

      this.draft.showRecoveryModal = false
      this.draft.discardConfirming = false
      this.customerChange.active = false
      this.orderContext.draftRecovered = true

      this.$bvToast.toast('Se recuperó el pedido guardado', {
        title: 'Borrador recuperado',
        variant: 'success',
        solid: true,
        autoHideDelay: 3000
      })
    },

    requestDraftDiscard() {
      this.draft.discardConfirming = true
    },

    async discardDraftAndStartNew() {
      if (this.draft.discarding) return

      this.draft.discarding = true
      this.cancelDraftSaveTimer()
      try {
        // No se reinicia la pantalla hasta confirmar que IndexedDB eliminó el
        // borrador. Así nunca se le informa al vendedor que fue descartado si
        // el respaldo quedó persistido por un error de almacenamiento.
        const seller = this.getSeller
        const cleared = !seller || !seller.id || await DraftStorage.clearSeller(seller.id)
        if (!cleared) {
          this.draft.discardConfirming = false
          this.$bvToast.toast('No se pudo descartar el trabajo guardado. Intentá nuevamente.', {
            title: 'Borrador conservado',
            variant: 'danger',
            solid: true,
          })
          return
        }

        this.removeDraftFallback()
        this.draft.timestamp = null
        this.draft.snapshots = []
        this.draft.discardConfirming = false
        this.draft.showRecoveryModal = false
        this.customerChange.active = false
        this.resetForm()
        this.currentStep = 1

        this.$bvToast.toast('El trabajo pendiente fue descartado. Ya podés crear un pedido nuevo.', {
          title: 'Pedido nuevo',
          variant: 'info',
          solid: true,
          autoHideDelay: 3000,
        })
      } finally {
        this.draft.discarding = false
      }
    },

    formatDraftTimestamp(timestamp) {
      if (!timestamp) return ''
      return moment(timestamp).format('DD/MM/YYYY HH:mm')
    },

    getDraftCustomerName(snapshot) {
      const form = this.getDraftForm(snapshot)
      return (form && form.customers && form.customers.name) || 'Cliente sin identificar'
    },

    getDraftItemCount(snapshot) {
      const form = this.getDraftForm(snapshot)
      return form && Array.isArray(form.detail) ? form.detail.length : 0
    },

    getDraftForm(snapshot) {
      const data = snapshot && snapshot.data ? snapshot.data : snapshot
      return data && data.form ? data.form : null
    },

    getDraftPointSaleLabel(snapshot) {
      const form = this.getDraftForm(snapshot)
      if (!form || !form.erp_points_sales_id) return ''
      const dataFiscal = form.customers && form.customers.data_fiscal
      const point = form.erp_points_sales || (this.arr.pointsSales || []).find(item => String(item.id) === String(form.erp_points_sales_id)) ||
        (dataFiscal && dataFiscal.points_sales && String(dataFiscal.points_sales.id) === String(form.erp_points_sales_id) ? dataFiscal.points_sales : null)
      if (!point) return ''
      if (point.full_identification) return point.full_identification
      const code = point.point_sale ? String(point.point_sale).padStart(4, '0') : ''
      return `${code} - ${point.name || ''}`.trim()
    },

    getDraftMethodsPaymentLabel(snapshot) {
      const form = this.getDraftForm(snapshot)
      if (!form || !form.methods_payment_id) return ''
      const method = form.methods_payment || (this.arr.methodsPayments || []).find(item => String(item.id) === String(form.methods_payment_id))
      if (!method) return ''
      return `${method.reference ? method.reference + ' - ' : ''}${method.name || ''}`.trim()
    },

    getDraftPriceListLabel(snapshot) {
      const form = this.getDraftForm(snapshot)
      if (!form || !form.price_list_id) return ''
      const list = form.price_list || (this.arr.priceLists || []).find(item => String(item.id) === String(form.price_list_id))
      if (!list) return ''
      return `${list.reference ? list.reference + ' - ' : ''}${list.name || ''}`.trim()
    },

    handleDraftVisibilityChange() {
      if (document.visibilityState === 'hidden') this.saveDraftNow('background')
    },

    flushDraftOnExit() {
      this.saveDraftNow('pagehide')
    },

    async requestPersistentDraftStorage() {
      if (!navigator.storage || !navigator.storage.persist) return
      try {
        await navigator.storage.persist()
      } catch (error) {
        // Algunos navegadores no permiten persistencia explícita.
      }
    },
  }
}
</script>

<style scoped>
.mobile-order-create {
  min-height: 100vh;
  background-color: var(--app-color-canvas);
  padding-bottom: 100px;
}

.action-square-btn {
  border-radius: 8px;
  padding: 8px 16px;
  border: 1px solid var(--app-color-border) !important;
  background: var(--app-color-surface) !important;
  color: var(--app-color-text) !important;
  font-size: 0.9rem;
  font-weight: var(--app-font-semibold);
}
.action-duplicate-btn {
  background-color: var(--app-color-primary) !important;
  border: 1px solid var(--app-color-primary) !important;
  color: var(--app-color-on-primary) !important;
  border-radius: 8px;
  font-size: 0.9rem;
  padding: 8px 16px;
}
.action-square-btn--cobranza {
  border-color: var(--app-color-success-hover) !important;
  background: var(--app-color-success-soft) !important;
}
.action-square-btn--observaciones {
  border-color: var(--app-color-warning-hover) !important;
  background: var(--app-color-warning-soft) !important;
}
.action-square-btn__icon--cobranza { color: var(--app-color-success-hover); }
.action-square-btn__icon--observaciones { color: var(--app-color-warning-hover); }
.stats-hoy-card {
  background: var(--app-color-surface-subtle);
  border: 1px solid var(--app-color-border);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.88rem;
}
.stats-hoy-card--cobranza {
  background: var(--app-color-success-soft);
  border-color: var(--app-color-success-border);
}
.stats-hoy-card--observaciones {
  background: var(--app-color-warning-soft);
  border-color: var(--app-color-warning-border);
}
.stats-hoy-card--cobranza .stats-hoy-card__icon { color: var(--app-color-success-hover); }
.stats-hoy-card--observaciones .stats-hoy-card__icon { color: var(--app-color-warning-hover); }
.action-square-label {
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  white-space: nowrap;
}

.mobile-header {
  z-index: 1000;
  background: var(--app-color-primary-900);
}

.mobile-header-icon-btn {
  width: 32px;
  height: 32px;
  padding: 0 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
}

.offline-mode-help-btn {
  width: 30px;
  min-width: 30px;
  height: 30px;
  margin: -0.35rem 0.1rem -0.35rem 0.2rem;
  padding: 0 !important;
  border-radius: 50%;
  color: var(--app-color-primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
}

.offline-mode-help-btn:hover,
.offline-mode-help-btn:focus {
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary-active);
}

.offline-mode-indicator {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.55rem;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.25;
  vertical-align: middle;
}

.offline-mode-indicator--connected {
  color: var(--app-color-success-hover);
  background: var(--app-color-success-soft);
  border-color: var(--app-color-success-border);
}

.offline-mode-indicator--local {
  color: var(--app-color-primary-active);
  background: var(--app-color-primary-soft);
  border-color: var(--app-color-primary-200);
}

/* Acciones que comparten renglón: el botón que parte el texto en dos líneas
   define la altura del grupo para que ninguno quede visualmente más chico. */
.mobile-equal-height-actions {
  display: flex;
  align-items: stretch;
}

.mobile-equal-height-actions > .btn {
  align-self: stretch;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.mobile-order-create .btn-group {
  align-items: stretch;
}

.mobile-order-create .btn-group .btn {
  align-self: stretch;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.product-scroll-sentinel {
  min-height: 32px;
}

.product-results-counter {
  font-size: 0.78rem;
  opacity: 0.85;
}

.mobile-header-title {
  font-size: 1rem;
  line-height: 1.2;
}

.mobile-header small {
  font-size: 0.75rem;
  line-height: 1.2;
}

.offline-status-bar {
  gap: 0.35rem;
  font-size: 0.78rem;
}
.offline-status-bar--offline {
  background-color: var(--app-color-danger-soft);
  border-top: 1px solid var(--app-color-danger-border);
  border-bottom: 1px solid var(--app-color-danger-border);
  color: var(--app-color-danger-hover);
}
.offline-status-bar--online {
  background: var(--app-color-success-soft);
  border-top: 1px solid var(--app-color-success-border);
  border-bottom: 1px solid var(--app-color-success-border);
  color: var(--app-color-success-hover);
}
.offline-status-bar__mode {
  display: inline-flex;
  align-items: center;
  font-weight: 700;
  line-height: 1.25;
}
.offline-status-bar__syncing {
  display: inline-flex;
  align-items: center;
  font-weight: 700;
  line-height: 1.25;
}
.offline-status-bar--online .offline-status-bar__syncing {
  color: var(--app-color-primary-active);
}
.offline-status-bar--offline .offline-status-bar__syncing {
  color: var(--app-color-danger-hover);
}
.offline-status-bar__mode--connected {
  color: var(--app-color-success-hover);
}
.offline-status-bar__mode--local {
  color: var(--app-color-primary-active);
}
.offline-status-bar--offline .offline-status-bar__mode {
  color: var(--app-color-danger-hover);
}

.offline-mode-selector ::v-deep .offline-mode-selector-toggle {
  min-height: 32px;
  padding: 0.25rem 0.6rem !important;
  border: 1px solid transparent !important;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.25;
  text-decoration: none;
}
.offline-mode-selector--connected ::v-deep .offline-mode-selector-toggle {
  background: var(--app-color-success) !important;
  border-color: var(--app-color-success) !important;
  color: var(--app-color-on-primary) !important;
}
.offline-mode-selector--local ::v-deep .offline-mode-selector-toggle {
  background: var(--app-color-primary) !important;
  border-color: var(--app-color-primary) !important;
  color: var(--app-color-on-primary) !important;
}

.offline-mode-selector ::v-deep .offline-mode-selector-toggle:hover,
.offline-mode-selector ::v-deep .offline-mode-selector-toggle:focus {
  text-decoration: none;
}
.offline-mode-selector--connected ::v-deep .offline-mode-selector-toggle:hover,
.offline-mode-selector--connected ::v-deep .offline-mode-selector-toggle:focus {
  color: var(--app-color-on-primary) !important;
}
.offline-mode-selector--local ::v-deep .offline-mode-selector-toggle:hover,
.offline-mode-selector--local ::v-deep .offline-mode-selector-toggle:focus {
  color: var(--app-color-on-primary) !important;
}

.offline-mode-selector ::v-deep .offline-mode-selector-menu {
  min-width: 235px;
  border-color: var(--app-color-primary-200);
  box-shadow: var(--app-shadow-sm);
}

.offline-mode-selector ::v-deep .offline-mode-selector-menu .dropdown-item {
  padding: 0.55rem 0.75rem;
  white-space: normal;
}

.offline-mode-selector ::v-deep .offline-mode-selector-menu .dropdown-item.active,
.offline-mode-selector ::v-deep .offline-mode-selector-menu .dropdown-item:active {
  background: var(--app-color-primary-soft);
  color: var(--app-color-text);
}

.local-data-freshness-alert {
  display: flex;
  align-items: flex-start;
  padding: 0.5rem 0.75rem;
  font-size: 0.84rem;
  line-height: 1.35;
}

.local-data-freshness-alert .app-icon {
  flex-shrink: 0;
  margin-top: 0.08rem;
}

.offline-sync-link {
  color: var(--app-color-primary);
  cursor: pointer;
}
.offline-sync-link:hover {
  text-decoration: underline;
}

.step1-edit-order-card {
  border-left: 4px solid var(--app-color-primary);
}

.edit-order-indicator {
  font-size: 0.9rem;
  border-left: 0;
  border-right: 0;
}

.edit-order-cancel-btn {
  white-space: nowrap;
}

@media (max-width: 576px) {
  .edit-order-cancel-btn {
    margin-top: 0.5rem;
    width: 100%;
  }
}

.customer-avatar-wrap {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
}
.customer-avatar {
  flex-shrink: 0;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.customer-summary__avatar {
  margin-right: 0.65rem;
}

.customer-summary__heading {
  margin-bottom: 0.55rem;
}

.customer-summary__name {
  min-width: 0;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.32;
}

.customer-summary__toggle {
  width: 36px;
  min-width: 36px !important;
  min-height: 36px !important;
  margin-left: 0.5rem;
  padding: 0 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.customer-summary__details {
  display: grid;
  gap: 0.32rem;
}

.customer-summary__detail {
  display: flex;
  align-items: flex-start;
  gap: 0.48rem;
  color: var(--app-color-text-muted);
  font-size: 0.9rem;
  line-height: 1.35;
}

.customer-summary__detail .app-icon {
  width: 1.05rem;
  margin-top: 0.1rem;
  color: var(--app-color-primary);
  flex-shrink: 0;
}

.product-customer-context-card {
  border-color: var(--app-color-primary-200);
  border-left: 4px solid var(--app-color-primary);
  box-shadow: var(--app-shadow-sm);
}

.product-customer-context-wrap {
  padding-bottom: 0.25rem !important;
}

.product-search-wrap {
  padding-top: 0.25rem !important;
}

/* Cabeceras de contexto: conservan la estructura de card, pero adquieren una
   jerarquía ligera y consistente con el color principal de la aplicación. */
.customer-configuration-card,
.review-customer-card {
  border-color: var(--app-color-primary-200);
  box-shadow: var(--app-shadow-sm);
}

.review-customer-card ::v-deep .card-header,
.review-products-card ::v-deep .card-header {
  background: var(--app-color-primary-soft);
  border-bottom-color: var(--app-color-primary-200);
  color: var(--app-color-text);
}

.review-customer-card ::v-deep .card-header {
  border-left: 4px solid var(--app-color-primary);
}

.mobile-input {
  font-size: 16px;
  height: 48px;
}

.add-to-cart-btn {
  min-height: 44px;
  padding-top: 0.6rem;
  padding-bottom: 0.6rem;
  font-size: 0.95rem;
}

.extra-input-group .form-control {
  height: 44px;
  font-size: 16px;
}

.extra-check-addon {
  display: flex;
  align-items: center;
  min-width: 88px;
  background-color: var(--app-color-surface-subtle);
  font-weight: 600;
  font-size: 0.9rem;
}

.extra-check {
  margin-right: 0.5rem;
}
.extra-check .custom-control-label::before,
.extra-check .custom-control-label::after {
  transform: scale(1.3);
  transform-origin: left center;
}

.mobile-edit-orders-grid {
  max-height: 48vh;
  overflow-y: auto;
  border: 1px solid var(--app-color-border);
  border-radius: 0.35rem;
}

.mobile-edit-orders-help {
  border: 1px solid var(--app-color-primary-200);
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary-active);
  border-radius: 0.35rem;
  padding: 0.65rem 0.75rem;
  font-size: 0.85rem;
}

.mobile-edit-orders-grid ::v-deep table td,
.mobile-edit-orders-grid ::v-deep table th {
  font-size: 0.82rem;
  padding: 0.45rem;
  vertical-align: middle;
}

.mobile-edit-orders-grid ::v-deep table thead th {
  white-space: nowrap;
}

.mobile-edit-orders-grid ::v-deep table th:nth-child(1),
.mobile-edit-orders-grid ::v-deep table td:nth-child(1) {
  width: 56px;
}

.mobile-edit-orders-grid ::v-deep table th:nth-child(3),
.mobile-edit-orders-grid ::v-deep table td:nth-child(3) {
  width: 92px;
}

.mobile-edit-orders-grid ::v-deep table th:nth-child(4),
.mobile-edit-orders-grid ::v-deep table td:nth-child(4) {
  width: 46px;
}

.mobile-edit-orders-grid ::v-deep tr.mobile-edit-order-row,
.mobile-edit-orders-grid ::v-deep tr.mobile-edit-order-row-selected {
  cursor: pointer;
}

.mobile-edit-orders-grid ::v-deep tr.mobile-edit-order-row-selected td {
  background-color: var(--app-color-primary-soft);
}

.mobile-edit-order-customer {
  line-height: 1.2;
}

.mobile-edit-select-btn {
  min-width: 32px;
  padding: 0.2rem 0.35rem;
}

.product-controls-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.product-view-toggle .btn {
  min-width: 38px;
  padding: 0.3rem 0.45rem;
}

.product-view-toggle .btn-secondary {
  background-color: var(--app-color-primary) !important;
  border-color: var(--app-color-primary) !important;
  color: var(--app-color-on-primary) !important;
}

.product-view-toggle .btn-outline-secondary {
  background: var(--app-color-surface);
  border-color: var(--app-color-primary-200);
  color: var(--app-color-primary-active);
}

.products-grid .product-card {
  min-height: 100%;
}

.product-query-filters-inline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-left: auto;
}

.product-switch {
  font-size: 0.78rem;
  white-space: nowrap;
}

::v-deep .product-switch .custom-control-label {
  padding-top: 0.1rem;
}

/* Bootstrap posiciona el switch con un valor fijo desde arriba. Al aumentar
   el área táctil de la etiqueta, eso lo deja visualmente más alto que el
   texto; se centra respecto de la misma etiqueta. */
::v-deep .product-switch .custom-control-label::before {
  top: 50%;
  transform: translateY(-50%);
}

/* No usar transform en la perilla: Bootstrap lo necesita para desplazarla
   horizontalmente al activar el switch. */
::v-deep .product-switch .custom-control-label::after {
  top: calc(50% - 0.375rem);
}

@media (max-width: 360px) {
  .product-query-filters-inline {
    gap: 0.35rem;
  }

  .product-switch {
    font-size: 0.72rem;
  }
}

.mobile-list-item {
  min-height: 72px;
  cursor: pointer;
}

.mobile-list-item:active {
  background-color: var(--app-color-surface-subtle);
}

.mobile-inline-label {
  margin-right: 10px;
  display: inline-block;
}

.mobile-qty-input {
  max-width: 200px;
}

.mobile-discount-input {
  flex-wrap: nowrap;
}

::v-deep .mobile-qty-input input[type="number"] {
  -moz-appearance: textfield;
}

::v-deep .mobile-qty-input input[type="number"]::-webkit-outer-spin-button,
::v-deep .mobile-qty-input input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.product-card {
  background: var(--app-color-surface);
  box-shadow: var(--app-shadow-sm);
}

.product-stock-pill {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--app-color-success-hover);
  background-color: var(--app-color-success-soft);
  border: 1px solid var(--app-color-success-border);
  border-radius: 999px;
  padding: 0.28rem 0.75rem;
  line-height: 1.2;
}

.product-stock-pill-empty {
  color: var(--app-color-danger-hover);
  background-color: var(--app-color-danger-soft);
  border-color: var(--app-color-danger-border);
}

/* Variante compacta: comparte renglón con el código de producto en vez de
   ocupar una línea propia (ahorra espacio vertical en la lista). */
.product-stock-pill-inline {
  font-size: 0.8rem;
  padding: 0.15rem 0.55rem;
  flex-shrink: 0;
  margin-left: 0.5rem;
}
.product-stock-pill-inline .product-stock-value {
  font-size: 0.8rem;
  font-weight: 700;
}
.product-stock-pill-inline .app-icon {
  /* Tamaño fijo: no depender del font-size reducido del pill compacto,
     que dejaba el ícono demasiado chico para verse. */
  width: 0.85rem;
  height: 0.85rem;
  flex-shrink: 0;
}

/* Quitar un producto requiere una segunda pulsación breve. Así se evita una
   baja accidental sin detener la carga con un modal de confirmación. */
.cart-remove-btn {
  min-width: 30px;
  min-height: 30px;
  color: var(--app-color-danger) !important;
  border-radius: 6px;
}

.cart-remove-btn--confirm {
  min-width: 36px;
  padding: 0 0.35rem !important;
  background: var(--app-color-danger) !important;
  color: var(--app-color-on-primary) !important;
  box-shadow: 0 0 0 2px var(--app-color-danger-soft);
}

.product-stock-value {
  font-size: 1rem;
  font-weight: 800;
}

.review-item-header {
  gap: 0.5rem;
}

.review-item-main {
  min-width: 0;
  overflow-wrap: anywhere;
}

.review-item-price {
  flex: 0 0 auto;
  white-space: nowrap;
}

.review-item-total {
  white-space: nowrap;
  line-height: 1.1;
}

/* Excepción de paleta: violeta deliberado, sin token. En esta pantalla
   "primary" ya significa selección de filtro/zona y "success" ya significa
   stock disponible (product-stock-pill); reusar cualquiera de los dos acá
   generaría ambigüedad con esos estados. "Agregado al pedido" necesita un
   acento propio que no compita con ninguno de los dos. */
.product-card-added {
  border-color: #6f42c1 !important;
  border-width: 4px;
  background: linear-gradient(180deg, #f3ebff 0%, #ede0ff 100%);
  box-shadow:
    inset 0 0 0 3px rgba(111, 66, 193, 0.75),
    inset 0 -20px 30px rgba(111, 66, 193, 0.18),
    0 0 0 1px rgba(111, 66, 193, 0.45),
    0 10px 20px rgba(111, 66, 193, 0.30);
}

.discount-exceeded .input-group-text,
.discount-exceeded .form-control {
  border-color: var(--app-color-danger) !important;
}

.discount-exceeded .input-group-text {
  background-color: var(--app-color-danger-soft);
  color: var(--app-color-danger-hover);
}

.cart-footer {
  z-index: 1000;
}

/* Estilos del acordeón de zonas */
.zones-accordion .card {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--app-color-border);
}

.zones-accordion .zone-header {
  background: var(--app-color-primary-soft);
  border: none;
  border-left: 4px solid var(--app-color-primary);
  color: var(--app-color-text);
  border-radius: 0;
  font-size: 16px;
}

.zones-accordion .zone-header:hover,
.zones-accordion .zone-header:active {
  background: var(--app-color-primary-100);
  color: var(--app-color-primary-active);
}

.zones-accordion .zone-header .badge {
  background: var(--app-color-primary) !important;
  color: var(--app-color-on-primary);
}

.zones-accordion .zone-header:focus {
  box-shadow: none;
}

.zones-accordion .zone-chevron {
  transition: transform 0.2s ease;
}

.zones-accordion .collapsed .zone-chevron {
  transform: rotate(-90deg);
}

.zones-accordion .not-collapsed .zone-chevron {
  transform: rotate(0deg);
}

.zones-accordion .list-group-item {
  border-left: none;
  border-right: none;
}

.zones-accordion .list-group-item:first-child {
  border-top: 1px solid var(--app-color-border);
}

::v-deep .products-accordion-review .card-body {
  padding: 6px 8px;
}

/* Estilos del acordeón de productos */
.products-accordion .card {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--app-color-border);
}

.products-accordion .category-header,
.products-accordion .subcategory-header {
  border: none;
  border-radius: 0;
  font-size: 16px;
}

.products-accordion .category-header {
  background-color: var(--app-color-primary-900);
  color: var(--app-color-on-primary);
}

.products-accordion .category-header:hover,
.products-accordion .category-header:active {
  background-color: var(--app-color-primary-active);
}

.products-accordion .category-header .badge {
  background: rgba(255, 255, 255, 0.18) !important;
  color: var(--app-color-on-primary);
}

.products-accordion .subcategory-header {
  background-color: var(--app-color-primary-soft);
  color: var(--app-color-text);
  font-size: 15px;
  border-left: 4px solid var(--app-color-primary-200);
}

.products-accordion .subcategory-header:hover,
.products-accordion .subcategory-header:active {
  background-color: var(--app-color-primary-100);
}

.products-accordion .subcategory-header .badge {
  background: var(--app-color-primary) !important;
  color: var(--app-color-on-primary);
}

.products-accordion .subcategory-card {
  border: 1px solid var(--app-color-border);
}

.products-accordion .zone-chevron {
  transition: transform 0.2s ease;
}

.products-accordion .collapsed .zone-chevron {
  transform: rotate(-90deg);
}

.products-accordion .not-collapsed .zone-chevron {
  transform: rotate(0deg);
}

/* En teléfonos el catálogo se usa casi siempre en vertical. Se compactan
   solamente los espacios de contención: se preservan alturas de controles y
   botones para no reducir la superficie táctil. */
@media (max-width: 576px) {
  .mobile-order-create .mobile-step--review {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }

  /* Cliente y Productos comparten la misma cabecera de contexto. Se conserva
     el marco, pero con padding compacto para no perder alto útil en móvil. */
  .mobile-order-create ::v-deep .review-customer-card > .card-header,
  .mobile-order-create ::v-deep .review-products-card > .card-header {
    padding: 0.45rem 0.75rem;
    background: var(--app-color-primary-soft);
    border-bottom: 1px solid var(--app-color-primary-200);
    border-left: 4px solid var(--app-color-primary);
  }

  .mobile-order-create ::v-deep .review-products-card {
    border: 1px solid var(--app-color-primary-200);
    border-radius: 0.25rem;
    background: var(--app-color-surface);
    box-shadow: var(--app-shadow-sm);
  }

  .mobile-order-create ::v-deep .review-products-card > .card-body {
    padding: 0.35rem !important;
  }

  .mobile-order-create .product-list {
    padding: 0.5rem !important;
  }

  .mobile-order-create .products-accordion > .card {
    margin-bottom: 0.5rem !important;
  }

  .mobile-order-create .products-accordion .category-header {
    padding: 0.65rem 0.75rem !important;
  }

  .mobile-order-create .products-accordion .subcategory-header {
    padding: 0.5rem 0.65rem !important;
  }

  .mobile-order-create .product-category-body,
  .mobile-order-create .review-category-body {
    padding: 0.4rem !important;
  }

  .mobile-order-create .products-accordion .subcategory-card {
    margin-bottom: 0.4rem !important;
  }

  .mobile-order-create .product-subcategory-body,
  .mobile-order-create .review-subcategory-body {
    padding: 0.4rem !important;
  }

  .mobile-order-create .product-list .product-card,
  .mobile-order-create .review-products-list .product-card {
    margin-bottom: 0.6rem !important;
    padding: 0.65rem !important;
  }

  .mobile-order-create .products-accordion .product-card {
    margin-bottom: 0.45rem !important;
    padding: 0.65rem !important;
  }

  .mobile-order-create .products-accordion-review .review-accordion-item {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
  }
}

/* Badge de zona */
.zone-badge {
  font-size: 12px;
  padding: 6px 10px;
  white-space: nowrap;
}


.mobile-footer {
  left: 0;
  right: 0;
  background: var(--app-color-surface);
}

/* CTA final: mismo patrón borde a borde de la rendición mobile. El footer
   del carrito conserva su padding porque contiene resumen + botón. */
.mobile-footer--action {
  padding: 0 !important;
  background: transparent;
  border: 0 !important;
}

.mobile-footer--action ::v-deep .btn {
  width: 100%;
  min-height: 52px;
  margin: 0;
  border-radius: 0 !important;
  box-shadow: none;
}

/* El carrito comparte el pie fijo, pero tiene dos áreas funcionales. No se
   deja padding exterior: el resumen respira dentro de su mitad y el CTA
   ocupa la otra hasta el borde, sin franjas blancas intermedias. */
.mobile-footer--cart {
  padding: 0 !important;
  border-top: 1px solid var(--app-color-border) !important;
}

.mobile-cart-summary {
  min-height: 64px;
  padding: 0.55rem 1rem;
}

.mobile-cart-continue {
  height: 100%;
  min-height: 64px;
  margin: 0;
  border-radius: 0 !important;
  box-shadow: none;
}

@media (min-width: 992px) {
  .sidebar-lg-show .mobile-footer {
    left: 200px;
    width: calc(100% - 200px);
  }
  .sidebar-lg-show.sidebar-minimized .mobile-footer {
    left: 50px;
    width: calc(100% - 50px);
  }
}

.btn-lg {
  padding: 14px 24px;
  font-size: 18px;
}

/* Evitar zoom en iOS al hacer focus en inputs */
input[type="text"],
input[type="number"],
input[type="date"],
textarea,
select {
  font-size: 16px !important;
}

/* Mínimos de lectura y de área táctil para esta interfaz, tanto en teléfono
   como en escritorio. El flujo se utiliza habitualmente desde pantallas
   táctiles aunque el ancho sea de desktop. */
@media (min-width: 0) {
  .mobile-order-create {
    font-size: 1rem;
  }

  .mobile-header-icon-btn {
    width: 42px;
    height: 42px;
    min-width: 42px;
    font-size: 1.05rem;
    border-radius: 9px;
  }

  .mobile-header-title {
    font-size: 1.08rem;
  }

  .mobile-header small {
    font-size: 0.82rem;
  }

  .offline-status-bar {
    min-height: 36px;
    font-size: 0.875rem;
    line-height: 1.35;
    padding-top: 0.45rem !important;
    padding-bottom: 0.45rem !important;
  }

  .mobile-order-create small,
  .mobile-order-create .small {
    font-size: 0.875rem !important;
    line-height: 1.4;
  }

  .mobile-order-create .btn-sm {
    min-height: 42px;
    padding: 0.45rem 0.7rem;
    font-size: 0.95rem;
  }

  .sync-card-toggle-btn {
    width: 42px;
    min-width: 42px;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .mobile-text-action-btn {
    min-width: 64px;
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }

  .mobile-icon-action-btn {
    width: 44px;
    min-width: 44px;
    min-height: 44px;
    font-size: 1.15rem;
  }

  .action-square-btn,
  .action-duplicate-btn {
    min-height: 46px;
    padding: 0.65rem 0.85rem;
    font-size: 1rem;
  }

  .stats-hoy-card {
    padding: 0.75rem 0.85rem;
    font-size: 0.95rem;
    line-height: 1.4;
  }

  .mobile-list-item {
    min-height: 80px;
  }

  .customer-avatar-wrap {
    width: 52px;
    height: 52px;
  }

  .product-results-counter,
  .product-switch {
    font-size: 0.875rem;
  }

  ::v-deep .product-switch .custom-control-label {
    min-height: 34px;
    display: flex;
    align-items: center;
    padding-top: 0;
  }

  .product-view-toggle .btn {
    min-width: 44px;
    min-height: 42px;
    padding: 0.45rem 0.6rem;
    font-size: 1rem;
  }

  .product-scroll-sentinel {
    min-height: 44px;
    font-size: 0.875rem;
  }

  .mobile-order-create .input-group-sm .form-control,
  .mobile-order-create .input-group-sm .btn,
  .mobile-order-create .input-group-sm .input-group-text {
    min-height: 44px;
    font-size: 1rem;
  }

  .mobile-order-create .input-group-sm .btn {
    min-width: 42px;
    padding-left: 0.55rem;
    padding-right: 0.55rem;
  }

  .add-to-cart-btn {
    min-height: 48px;
    font-size: 1rem;
  }

  .extra-check-addon {
    min-height: 44px;
    font-size: 1rem;
  }

  .product-stock-pill-inline,
  .product-stock-pill-inline .product-stock-value {
    font-size: 0.875rem;
  }

  .product-stock-pill-inline {
    padding: 0.25rem 0.65rem;
  }

  .product-stock-pill-inline .app-icon {
    width: 1rem;
    height: 1rem;
  }

  .zones-accordion .zone-header,
  .products-accordion .category-header,
  .products-accordion .subcategory-header {
    min-height: 48px;
    font-size: 1rem;
  }

  .zone-badge,
  .mobile-order-create .badge {
    font-size: 0.8rem;
    padding: 0.35rem 0.55rem;
  }

  .mobile-edit-orders-help {
    font-size: 0.9rem;
    line-height: 1.4;
  }

  .mobile-edit-orders-grid ::v-deep table td,
  .mobile-edit-orders-grid ::v-deep table th {
    font-size: 0.875rem;
    padding: 0.55rem;
  }

  .mobile-edit-select-btn {
    width: 42px;
    min-width: 42px;
    min-height: 42px;
    padding: 0.4rem;
  }

  ::v-deep .col-form-label-sm {
    font-size: 0.95rem;
  }

  .review-item-total {
    line-height: 1.25;
  }
}

@media (max-width: 360px) {
  .product-switch {
    font-size: 0.82rem;
  }
}
</style>

<style>
/* El shell general reserva 15px a cada lado de container-fluid. Esta pantalla
   es mobile-first y ya tiene su propia separación interna, por lo que se
   recupera ese ancho solamente fuera del modo pantalla completa (donde el
   contenedor padre ya queda en cero). */
@media (max-width: 576px) {
  body:not(.mobile-order-focus-mode) .mobile-order-create {
    margin-left: -15px;
    margin-right: -15px;
  }
}

body.mobile-order-focus-mode .app-header,
body.mobile-order-focus-mode .sidebar,
body.mobile-order-focus-mode .aside-menu,
body.mobile-order-focus-mode .app-footer,
body.mobile-order-focus-mode .alert-clear-cache,
body.mobile-order-focus-mode .shortcuts-dropdown-full {
  display: none !important;
}

body.mobile-order-focus-mode .app-body {
  margin-top: 0 !important;
}

body.mobile-order-focus-mode .main {
  margin-left: 0 !important;
  width: 100% !important;
}

body.mobile-order-focus-mode .main .container-fluid {
  padding-left: 0 !important;
  padding-right: 0 !important;
  /* _platform-shell.scss: ".app-body .main > .container-fluid { padding-top: 6px }"
     — aire estándar bajo el navbar en cualquier pantalla normal. En pantalla
     completa no hay navbar, así que ese 6px queda como un margen suelto
     arriba del header sticky. */
  padding-top: 0 !important;
}

/* No hace falta ocultar .tabs-layout > .small-tabs ni resetear
   .tabs-layout__content/.tabs-layout__view acá: esta vista tiene
   mobile_first=true en su permiso (m_modules_permissions, ver getView() en
   el backend), que DefaultTabsLayout.vue lee en isNonTabRoute() — así que la
   barra de tabs ni se renderiza y la card de tab ya queda en cero (--bare)
   siempre, no solo en pantalla completa. */

body.mobile-order-focus-mode .mobile-footer {
  left: 0 !important;
  width: 100% !important;
}

/* msgBoxConfirm() de bootstrap-vue arma el modal por fuera de este
   componente (no hay markup propio ni <style scoped> que lo alcance), así
   que la única forma de tocar los botones es enganchando la clase del
   footerClass acá, sin scope. */
.confirm-order-modal-footer {
  gap: 10px;
}
.confirm-order-modal-footer .btn {
  min-height: 48px;
  font-size: 1.05rem;
}

@media (max-width: 576px) {
  .mobile-order-confirm-dialog {
    width: auto;
    max-width: calc(100% - 1rem);
    margin: 0.5rem;
  }
}

.mobile-order-confirm-header {
  min-height: 58px;
  padding: 0.65rem 0.85rem !important;
}

.mobile-order-confirm-header .app-confirm-header__title {
  font-size: 1.1rem;
  line-height: 1.3;
}

.mobile-order-confirm-header .close {
  width: 44px;
  min-width: 44px;
  height: 44px;
  padding: 0 !important;
  margin: -0.25rem -0.35rem -0.25rem auto !important;
  font-size: 1.5rem;
}

.mobile-order-confirm-body {
  padding: 1rem !important;
  font-size: 1rem;
  line-height: 1.5;
}

.mobile-order-confirm-footer {
  gap: 8px;
  align-items: stretch;
  flex-wrap: nowrap;
  padding: 0.5rem !important;
}

.mobile-order-confirm-footer .btn {
  flex: 1 1 0;
  align-self: stretch;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px !important;
  margin: 0 !important;
  padding: 0.7rem 0.6rem !important;
  font-size: 1rem !important;
  line-height: 1.25;
}

/* Acciones de los modales de cobranza y observaciones: mismo tamaño y
   jerarquía visual clara entre cancelar (secundaria) y guardar (principal). */
.mobile-report-modal-actions {
  gap: 8px;
  align-items: stretch;
}
.mobile-report-modal-action {
  flex: 1 1 0;
  align-self: stretch;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
}
.mobile-report-customer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid var(--app-color-border);
  border-radius: 8px;
  background: var(--app-color-surface-subtle);
}
.mobile-report-customer__avatar {
  flex-shrink: 0;
}

.cobranza-new-item-field {
  flex: 1 1 0;
  min-width: 0;
}

/* Los modales de Bootstrap se montan fuera del nodo del componente. La clase
   de diálogo permite aplicarles los mismos mínimos de lectura y tacto. */
@media (max-width: 576px) {
  .mobile-order-dialog {
    width: auto;
    max-width: calc(100% - 1rem);
    margin: 0.5rem;
  }
}

.mobile-order-dialog {
  font-size: 1rem;
}

.mobile-order-dialog .app-modal-header__title {
  font-size: 1.15rem;
  line-height: 1.3;
}

.mobile-order-dialog .mobile-modal-close-btn {
  width: 44px;
  min-width: 44px;
  height: 44px;
  margin-left: auto;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--app-color-text-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  flex-shrink: 0;
}

.mobile-order-dialog .mobile-modal-close-btn:hover,
.mobile-order-dialog .mobile-modal-close-btn:focus {
  color: var(--app-color-text);
  background: var(--app-color-surface-subtle);
  outline: none;
}

.mobile-order-dialog small,
.mobile-order-dialog .small {
  font-size: 0.875rem !important;
  line-height: 1.4;
}

.mobile-order-dialog .btn {
  min-height: 48px !important;
  padding: 0.7rem 1rem !important;
  font-size: 1rem;
  line-height: 1.25;
}

.mobile-order-dialog .btn-sm {
  min-height: 44px !important;
  padding: 0.55rem 0.75rem !important;
  font-size: 0.95rem;
}

.mobile-order-dialog .modal-footer {
  gap: 8px;
  align-items: stretch;
  flex-wrap: nowrap;
}

.mobile-order-dialog .modal-footer .btn {
  flex: 1 1 0;
  align-self: stretch;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 !important;
}

/* El recupero exige una decisión explícita: recuperar o descartar. */
.mobile-order-dialog .mobile-draft-recovery-footer {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: stretch;
  gap: 8px;
  flex-wrap: initial;
}

.mobile-order-dialog .mobile-draft-recovery-footer .btn {
  width: 100%;
  height: 100%;
  min-width: 0;
  flex: none;
  padding-left: 0.55rem !important;
  padding-right: 0.55rem !important;
}

.mobile-order-dialog .draft-recovery-summary {
  overflow: hidden;
  border: 1px solid var(--app-color-border);
  border-radius: 8px;
  background: var(--app-color-surface-subtle);
}

.mobile-order-dialog .draft-recovery-summary__row {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.45rem 0.65rem;
  color: var(--app-color-text-secondary);
  font-size: 0.92rem;
  line-height: 1.3;
}

.mobile-order-dialog .draft-recovery-summary__row + .draft-recovery-summary__row {
  border-top: 1px solid var(--app-color-border);
}

.mobile-order-dialog .draft-recovery-summary__row > .app-icon,
.mobile-order-dialog .draft-recovery-summary__row > svg {
  flex: 0 0 auto;
  margin-top: 0.1rem;
  color: var(--app-color-primary);
}

.mobile-order-dialog .draft-recovery-summary__row--customer {
  background: var(--app-color-primary-soft);
  color: var(--app-color-text);
  font-size: 0.98rem;
}

.mobile-order-dialog .draft-recovery-products {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-top: 0.75rem;
  padding: 0.65rem 0.75rem;
  border: 1px solid var(--app-color-primary-200);
  border-radius: 8px;
  background: var(--app-color-primary-soft);
  color: var(--app-color-text-secondary);
}

.mobile-order-dialog .draft-recovery-products__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: var(--app-color-surface);
  color: var(--app-color-primary);
}

.mobile-order-dialog .draft-recovery-products strong {
  margin-right: 0.2rem;
  color: var(--app-color-primary-active);
  font-size: 1.1rem;
}

.mobile-order-dialog .form-control,
.mobile-order-dialog .custom-select {
  min-height: 44px;
  font-size: 16px !important;
}

.mobile-modal-icon-action {
  width: 48px;
  min-width: 48px;
  min-height: 48px !important;
  padding: 0 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.mobile-report-modal-action {
  min-height: 50px !important;
  padding-top: 0.75rem !important;
  padding-bottom: 0.75rem !important;
}
</style>
